import { useState, useEffect, ReactNode } from "react";
import { ChevronDown, ChevronRight, Eye, EyeOff, CheckSquare, Square, BookOpen, Menu, X, ArrowUp, RotateCcw, Shuffle, Layers } from "lucide-react";

// ─── Type Definitions ────────────────────────────────────────────────────────

interface SectionData {
  id: string;
  title: string;
  num: number;
}

interface FlashcardData {
  term: string;
  definition: string;
}

interface FlashcardGroup {
  topic: string;
  color: keyof typeof colorMap;
  cards: FlashcardData[];
}

interface FlashcardWithTopic extends FlashcardData {
  topic: string;
  color: keyof typeof colorMap;
}

type ColorKey = "sky" | "violet" | "amber" | "rose" | "emerald";

// ─── Data ────────────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: "word-size", title: "1.1 Word Size", num: 1 },
  { id: "combinations", title: "1.2 Possible Combinations", num: 2 },
  { id: "binary-addition", title: "1.3 Binary Addition", num: 3 },
  { id: "fixed-bits", title: "1.4 Representing Integers", num: 4 },
  { id: "min-max", title: "1.4.3 Min/Max Formulas", num: 5 },
  { id: "overflow", title: "1.4.4 Overflow & Underflow", num: 6 },
  { id: "real-numbers", title: "1.5 Real Numbers", num: 7 },
  { id: "exercises", title: "LU1 Practice Exercises", num: 8 },
  { id: "cpu-design", title: "2.1 CPU Design", num: 9 },
  { id: "ram-memory", title: "2.2 RAM & Memory", num: 10 },
  { id: "motherboard", title: "2.3 Motherboard & Buses", num: 11 },
  { id: "secondary-storage", title: "2.4 Secondary Storage", num: 12 },
  { id: "caching", title: "2.5 Caching", num: 13 },
  { id: "exercises-lu2", title: "LU2 Practice Exercises", num: 14 },
  { id: "boot-process", title: "3.1 Boot Process", num: 15 },
  { id: "machine-cycle", title: "3.2 Machine Cycle", num: 16 },
  { id: "processing-techniques", title: "3.3 Processing Techniques", num: 17 },
  { id: "interrupts", title: "3.4 Interrupts", num: 18 },
  { id: "virtual-memory", title: "3.5 Virtual Memory", num: 19 },
  { id: "translators", title: "3.6 Translators", num: 20 },
  { id: "exercises-lu3", title: "LU3 Practice Exercises", num: 21 },
  { id: "lan-physical", title: "4.1 Physical Aspects of Network Media", num: 22 },
  { id: "bounded-media", title: "4.2 Bounded Media", num: 23 },
  { id: "unbounded-media", title: "4.3 Unbounded Media", num: 24 },
  { id: "broadcast-pp", title: "4.4 Broadcast vs Point-to-point", num: 25 },
  { id: "topology", title: "4.5 Topology", num: 26 },
  { id: "ethernet", title: "4.6 Ethernet", num: 27 },
  { id: "network-addressing", title: "4.7 Network Addressing", num: 28 },
  { id: "internet-protocols", title: "4.8 Internet Protocols", num: 29 },
  { id: "lan-devices", title: "4.9 LAN Devices", num: 30 },
  { id: "osi-model", title: "4.10 OSI Model", num: 31 },
  { id: "wireless-lan", title: "4.11 Wireless LAN", num: 32 },
  { id: "exercises-lu4", title: "LU4 Practice Exercises", num: 33 },
  { id: "wan-backbone", title: "5.1 Fibre Optic Backbone", num: 34 },
  { id: "wan-overview", title: "5.2 WAN", num: 35 },
  { id: "wan-gateways", title: "5.3 Devices & Gateways", num: 36 },
  { id: "wan-transmission", title: "5.4 Long-distance Transmission", num: 37 },
  { id: "wan-connection-tech", title: "5.5 Internet Connection Technology", num: 38 },
  { id: "app-protocols", title: "5.6 Application Protocols", num: 39 },
  { id: "exercises-lu5", title: "LU5 Practice Exercises", num: 40 },
  { id: "web-evolution", title: "6.1 Evolution of the Internet", num: 41 },
  { id: "web-applications", title: "6.2 Web-based Applications", num: 42 },
  { id: "mobile-websites", title: "6.3 Mobile Websites", num: 43 },
  { id: "mobile-applications", title: "6.4 Mobile Applications", num: 44 },
  { id: "mobile-design", title: "6.5 Mobile Design Factors", num: 45 },
  { id: "plugins-extensions", title: "6.6 Plug-ins & Extensions", num: 46 },
  { id: "search-engines", title: "6.7 Search Engines", num: 47 },
  { id: "multimedia-online", title: "6.8 Multimedia Online", num: 48 },
  { id: "compression", title: "6.9 Compression Technology", num: 49 },
  { id: "exercises-lu6", title: "LU6 Practice Exercises", num: 50 },
  { id: "error-sources", title: "7.1 Sources of Errors", num: 51 },
  { id: "error-solutions", title: "7.2 Verification & Validation", num: 52 },
  { id: "dbms-integrity", title: "7.3 DBMS Integrity", num: 53 },
  { id: "threats", title: "7.4 Threats", num: 54 },
  { id: "security-solutions", title: "7.5 Security Solutions", num: 55 },
  { id: "exercises-lu7", title: "LU7 Practice Exercises", num: 56 },
  { id: "workplace-employment", title: "8.1 Workplace & Employment", num: 57 },
  { id: "computer-crime", title: "8.2 Computer Crime", num: 58 },
  { id: "safeguards", title: "8.3 Safeguards", num: 59 },
  { id: "evaluating-information", title: "8.4 Evaluating Information", num: 60 },
  { id: "exercises-lu8", title: "LU8 Practice Exercises", num: 61 },
];

// ─── LU grouping (sidebar nav) ──────────────────────────────────────────────

function getLU(title: string): number | null {
  const m = title.match(/^(\d+)\./) || title.match(/^LU(\d+)/i);
  return m ? Number(m[1]) : null;
}

const LU_TITLES: Record<number, string> = {
  1: "Data Representation",
  2: "Hardware",
  3: "System Software",
  4: "Local Area Networks",
  5: "Wide Area Networks",
  6: "Internet",
  7: "Errors, Threats & Security",
  8: "Social Implications",
};

const SECTIONS_BY_LU: Record<number, SectionData[]> = SECTIONS.reduce((acc, s) => {
  const lu = getLU(s.title);
  if (lu == null) return acc;
  (acc[lu] ||= []).push(s);
  return acc;
}, {} as Record<number, SectionData[]>);

const LU_NUMBERS: number[] = Object.keys(SECTIONS_BY_LU).map(Number).sort((a, b) => a - b);

const FLASHCARDS = [
  { topic: "Fundamentals", color: "sky" as const, cards: [
    { term: "Word Size", definition: "The number of bits that a CPU register can hold and manipulate at any one time. It defines how much data can be processed per instruction cycle." },
    { term: "Word", definition: "A unit of data defining the number of bits that move around a computer from one component to another." },
    { term: "Binary Number System", definition: "A number system using base-2 that consists only of 0s and 1s. The foundation of all computer operations." },
    { term: "Bit", definition: "The smallest unit of data in computing \u2014 a single binary digit, either 0 or 1." },
    { term: "Nibble", definition: "A unit of data consisting of 4 bits, also called a half-byte. Can represent values 0\u201315." },
    { term: "Byte", definition: "A unit of data consisting of 8 bits. Can represent 2\u2078 = 256 different values." },
  ]},
  { topic: "Number Representation", color: "violet" as const, cards: [
    { term: "Unsigned Integer", definition: "A whole number that can only be positive (or zero). All bits used for magnitude. Range: 0 to 2\u207f \u2212 1." },
    { term: "Signed Integer", definition: "A whole number that can be positive or negative. Leftmost bit indicates sign. Range: \u22122\u207f\u207b\u00b9 to 2\u207f\u207b\u00b9 \u2212 1." },
    { term: "Fixed-Point Number", definition: "An integer \u2014 the decimal place is fixed at the end of the number." },
    { term: "Sign and Size", definition: "Left-most bit indicates sign (0=positive, 1=negative), remaining bits = magnitude. Cannot be used for arithmetic." },
    { term: "Two\u2019s Complement", definition: "Invert all bits of the positive number then add 1. Works correctly with arithmetic operations. The standard method." },
    { term: "One\u2019s Complement", definition: "Intermediate step: all bits inverted (0s become 1s, 1s become 0s). Not used alone for storage." },
    { term: "Padding", definition: "Adding leading zeros to fill the required number of bits. E.g. 1001\u2082 becomes 00001001\u2082 in 8 bits." },
  ]},
  { topic: "Real Numbers", color: "amber" as const, cards: [
    { term: "Floating-Point Number", definition: "A real number stored using a mantissa and an exponent." },
    { term: "Scientific Notation", definition: "Writing numbers as \u00b1n.nnn \u00d7 10\u00b1\u1d4f. Used in maths and science." },
    { term: "Normalised Form", definition: "Writing a real number as 0.nnn \u00d7 10\u207f for computer storage. All digits shifted right of the decimal point." },
    { term: "Mantissa", definition: "The digits after the decimal point in normalised form. Determines precision (significant digits)." },
    { term: "Exponent", definition: "The power to which the base is raised. Determines range (how large or small the number can be)." },
  ]},
  { topic: "Errors & Accuracy", color: "rose" as const, cards: [
    { term: "Overflow Error", definition: "Value too large for allocated bits. Extra bits lost, producing an incorrect wrapped-around result." },
    { term: "Underflow", definition: "Value too small (close to zero) to be represented. Gets rounded to zero." },
    { term: "Truncation", definition: "Removing extra digits from the end to fit storage. Simply cuts off excess \u2014 e.g. 56718 \u2192 567." },
    { term: "Rounding", definition: "Adjusting last stored digit based on next digit. E.g. 56758 \u2192 568 (round up, next digit \u2265 5)." },
    { term: "Truncation/Rounding Error", definition: "Loss of precision when digits are removed to fit storage. Unavoidable with limited storage." },
    { term: "Logical Error", definition: "Program compiles and runs but produces incorrect results. Overflow is logical because it\u2019s silent and hard to detect." },
  ]},
  { topic: "Key Formulas", color: "emerald" as const, cards: [
    { term: "Number of Combinations", definition: "2\u207f where n = number of bits. E.g. 8 bits \u2192 2\u2078 = 256." },
    { term: "Unsigned Range", definition: "Smallest: 0 | Largest: 2\u207f \u2212 1. E.g. 8 bits \u2192 0 to 255." },
    { term: "Signed Range", definition: "Smallest: \u22122\u207f\u207b\u00b9 | Largest: 2\u207f\u207b\u00b9 \u2212 1. E.g. 8 bits \u2192 \u2212128 to 127." },
    { term: "Binary Addition Rules", definition: "0+0=0 | 0+1=1 | 1+0=1 | 1+1=10 (write 0, carry 1) | 1+1+1=11 (write 1, carry 1)." },
    { term: "Two\u2019s Complement Algorithm", definition: "1) Convert to binary 2) Pad to required bits 3) Invert all bits 4) Add 1." },
    { term: "Two\u2019s Comp \u2192 Decimal", definition: "If MSB=1: 1) Invert all bits 2) Add 1 3) Convert to decimal 4) Add negative sign." },
  ]},
  { topic: "CPU & Processing", color: "sky" as const, cards: [
    { term: "Control Unit (CU)", definition: "Controls execution of each instruction. Sends read/write signals to memory and provides the address of data to be fetched or stored." },
    { term: "Arithmetic Logic Unit (ALU)", definition: "Performs calculations and logical comparisons. Results stored in the accumulator register." },
    { term: "Registers", definition: "Tiny, ultra-fast storage locations inside the CPU made from SRAM. Hold the current instruction and its data." },
    { term: "Accumulator", definition: "The register in the ALU that stores the result of each execution." },
    { term: "Serial Processing", definition: "Instructions executed one at a time, sequentially, on a single processor core." },
    { term: "Parallel Processing", definition: "Instructions broken into parts and run simultaneously on multiple processors/cores, reducing processing time." },
    { term: "Hyperthreading", definition: "Duplicates the registers on a single core, allowing fast context switching between two threads. OS sees two logical CPUs per core." },
    { term: "Multiprocessing", definition: "Multiple complete CPU cores on a single chip. Each core can genuinely execute instructions independently at the same time." },
  ]},
  { topic: "Memory & Cache", color: "violet" as const, cards: [
    { term: "RAM", definition: "Random Access Memory \u2014 main memory storing programs currently in use and their data. Made from DRAM." },
    { term: "DRAM", definition: "Dynamic RAM \u2014 uses capacitors that lose charge and must be refreshed many times per second." },
    { term: "SRAM", definition: "Static RAM \u2014 extremely fast, no refreshing needed. Used for registers and cache. Very expensive." },
    { term: "SDRAM", definition: "Synchronous DRAM \u2014 refreshing synchronised with CPU clock so CPU doesn\u2019t wait during refresh." },
    { term: "DDR", definition: "Double Data Rate \u2014 data transferred twice per clock pulse (on rise and fall). Versions: DDR, DDR2, DDR3, DDR4, DDR5." },
    { term: "Latency", definition: "Time taken for a component to respond. Fewer wasted clock ticks = faster performance. Cache has less latency than RAM." },
    { term: "L1 Cache", definition: "Smallest, fastest cache. Built into CPU core at CPU speed. Each core has its own L1." },
    { term: "L2 Cache", definition: "Medium-sized cache on the CPU chip. Runs at about half CPU speed. Slightly further from core." },
    { term: "L3 Cache", definition: "Largest, slowest cache. Shared between multiple cores. Located on CPU die or motherboard." },
    { term: "Cache Hit", definition: "Requested data is found in the cache \u2014 served quickly without accessing slower RAM." },
    { term: "Cache Miss", definition: "Requested data is NOT in cache \u2014 must be fetched from slower RAM, causing delay." },
  ]},
  { topic: "Buses & Motherboard", color: "amber" as const, cards: [
    { term: "Motherboard", definition: "Central PCB connecting all components. Hosts CPU, RAM. Uses buses (internal connections), slots (device cards) and ports (external devices)." },
    { term: "System Clock", definition: "Crystal microchip vibrating at a specific frequency. Determines the timing and speed of all operations. Measured in GHz." },
    { term: "Overclocking", definition: "Making components run faster than designed by manipulating clock frequencies. Can be per-component or whole-system." },
    { term: "Bandwidth", definition: "Theoretical/maximum speed of data transfer. The speed a bus is rated for." },
    { term: "Throughput", definition: "Actual speed of data transfer in practice. Usually lower than bandwidth due to latency and other factors." },
    { term: "Data Bus", definition: "Transfers actual instruction/data between CPU and RAM. Width determines throughput (like highway lanes)." },
    { term: "Address Bus", definition: "Transfers physical memory address between CPU and RAM. Width determines how much memory can be addressed (32-bit = 4GB, 64-bit = 2\u2076\u2074)." },
    { term: "Control Bus", definition: "Carries control commands between CPU and RAM \u2014 e.g. whether to read or write data." },
    { term: "FSB (Front Side Bus)", definition: "Internal bus connecting CPU and RAM. Consists of the data bus, address bus, and control bus." },
    { term: "PCI Express", definition: "High-speed expansion bus for graphics cards, SSDs, Wi-Fi cards. Generations 1\u20135, speed nearly doubles each generation." },
    { term: "SATA", definition: "Serial ATA \u2014 connects mass storage (HDDs, SSDs) to motherboard. Max 600MB/s (SATA III). Backwards compatible." },
    { term: "USB", definition: "Universal Serial Bus \u2014 standardises peripheral connections. Provides data transfer and electrical power. USB 3.0 = 5Gbps." },
    { term: "NVMe", definition: "Non-Volatile Memory Express \u2014 connects SSDs via PCI Express lanes directly, bypassing SATA controller. Much faster than SATA." },
    { term: "M.2 Format", definition: "Compact form factor for high-speed, low-power devices. Supports SATA 3.0, PCI Express 3.0, USB 3.0, and NVMe." },
  ]},
  { topic: "Storage & Caching", color: "rose" as const, cards: [
    { term: "HDD", definition: "Hard Disk Drive \u2014 uses spinning magnetic platters and a read/write head. Large capacity, relatively cheap, slower than SSD." },
    { term: "SSD", definition: "Solid State Drive \u2014 uses flash memory/electronic chips, no moving parts. Faster than HDD but more expensive per GB." },
    { term: "Cloud Storage", definition: "Remote storage accessed via internet. Speed depends on connection. Ongoing subscription cost. E.g. Google Drive, OneDrive." },
    { term: "Disk Cache", definition: "Reserved space in RAM used to pre-fetch data from slow hard disk. Subsequent reads come from faster RAM cache." },
    { term: "Browser Cache", definition: "Local copy of web pages stored on your hard disk. Speeds up repeat visits but may show outdated content." },
    { term: "Web/Proxy Cache", definition: "Shared cache on an organisation\u2019s server. First request goes to internet and is cached; subsequent users get the cached copy locally." },
  ]},
  { topic: "Boot & Machine Cycle", color: "sky" as const, cards: [
    { term: "BIOS", definition: "Basic Input/Output System \u2014 legacy firmware in motherboard ROM. First code to run at power-on; initialises hardware, runs POST, loads the bootloader." },
    { term: "UEFI", definition: "Unified Extensible Firmware Interface \u2014 modern replacement for BIOS. Faster boot, supports drives larger than 2 TB, supports Secure Boot, richer setup interface." },
    { term: "CMOS", definition: "Battery-backed RAM on the motherboard storing BIOS/UEFI settings (boot order, time, fan profiles). Survives power-off via coin-cell battery." },
    { term: "POST", definition: "Power-On Self-Test \u2014 the diagnostic check the firmware runs before booting: RAM, CPU, drives. Failures signalled by beeps or on-screen messages." },
    { term: "Bootloader", definition: "Small program loaded by firmware after POST. Locates the OS kernel on disk, loads it into RAM, and starts it. E.g. GRUB (Linux), Windows Boot Manager." },
    { term: "Kernel", definition: "The core of the OS. Loaded by the bootloader; manages drivers, scheduling, and memory; stays resident the whole time the computer is on." },
    { term: "Machine Cycle", definition: "The four-step rhythm the CPU follows for every instruction: Fetch \u2192 Decode \u2192 Execute \u2192 Store. Repeats billions of times per second." },
    { term: "Fetch", definition: "Stage 1. The Control Unit copies the next instruction from RAM into a register inside the CPU." },
    { term: "Decode", definition: "Stage 2. The CU works out what the instruction means: which operation, which registers, where the operands are." },
    { term: "Execute", definition: "Stage 3. The relevant unit performs the work \u2014 arithmetic and logic in the ALU, memory access via the bus, jumps update the program counter." },
    { term: "Store", definition: "Stage 4. The result is written to a register, RAM, or output device. Then the CPU loops back to Fetch with the next instruction." },
  ]},
  { topic: "Processing & Interrupts", color: "violet" as const, cards: [
    { term: "Process", definition: "One running program. Has its own memory space, files, and at least one thread of execution. Word and Chrome are processes." },
    { term: "Thread", definition: "A single sequence of instructions within a process. A process can have many threads sharing its memory but executing different code paths." },
    { term: "Multitasking", definition: "OS gives the illusion of running many processes at once by quickly switching the CPU between them. Software technique." },
    { term: "Multithreading", definition: "OS or program runs multiple threads inside ONE process at the same time. Useful when one part is waiting (e.g. for the network) while another can keep working." },
    { term: "Time Slice", definition: "Small block of CPU time (a few milliseconds) the OS gives a process before pausing it and letting another run. Switching is too fast for humans to see." },
    { term: "Context Switch", definition: "The OS\u2019s work between time slices: save the paused process\u2019s registers and program counter, load the next process\u2019s, then resume." },
    { term: "Pre-emptive Multitasking", definition: "OS can forcefully pause a running process when its time slice ends. Opposite is cooperative \u2014 a buggy program could lock up the system. All modern OSes are pre-emptive." },
    { term: "Hyper-threading", definition: "Hardware trick: one physical core presents two logical cores so the OS can schedule two threads on the same core. Makes multitasking work better, doesn\u2019t add real cores." },
    { term: "Multi-processing", definition: "Hardware reality: multiple physical cores. The OS schedules different processes (or threads) on different cores \u2014 genuine parallel execution." },
    { term: "Interrupt", definition: "A signal that pauses the CPU\u2019s current task and asks it to handle something more urgent. The CPU saves state, runs the handler, then resumes." },
    { term: "IRQ", definition: "Interrupt Request \u2014 the numbered line a device uses to request CPU attention. IRQ 1 traditionally = keyboard, IRQ 12 = PS/2 mouse." },
    { term: "Interrupt Handler", definition: "Small piece of code (in the OS or driver) that runs in response to a particular interrupt. Reads the keystroke / acknowledges the disk / updates the clock, then returns." },
    { term: "IO Range", definition: "Range of memory addresses (or port numbers) the OS reserves for talking to a particular device. Combined with its IRQ, it\u2019s how the OS knows which device sent which interrupt." },
  ]},
  { topic: "Virtual Memory", color: "amber" as const, cards: [
    { term: "Virtual Memory", definition: "OS pretends each program has more RAM than physically exists by using part of the disk as overflow. A capacity feature, not a speed feature." },
    { term: "Page", definition: "Fixed-size chunk of memory (typically 4 KB) the OS treats as the unit for moving things between RAM and disk." },
    { term: "Paging", definition: "Moving a page out of RAM to disk to make room for another page that\u2019s needed right now." },
    { term: "Swap File / Page File", definition: "Area of disk reserved for paged-out memory. On Windows: pagefile.sys. On Linux: a swap partition or /swapfile." },
    { term: "Swapping", definition: "The act of moving pages between RAM and the swap file. Strictly: paging moves individual pages, swapping originally moved entire processes." },
    { term: "Thrashing", definition: "The state where the system spends more time moving pages between RAM and disk than running actual code. Disk light stays on; the computer crawls. Cure: close programs or add RAM." },
  ]},
  { topic: "Translators", color: "rose" as const, cards: [
    { term: "High-level Language", definition: "Close to human language; abstracts away CPU details. Easier to read, write, maintain. Examples: Java, Python, Delphi, C#, JavaScript." },
    { term: "Low-level Language", definition: "Close to the CPU; programmer manipulates memory addresses and instructions directly. Hard to read; very efficient. Examples: assembly language, machine code." },
    { term: "Assembly Language", definition: "Human-readable representation of machine code using short mnemonics (MOV, ADD, JMP) that map almost one-to-one onto CPU instructions." },
    { term: "Machine Code", definition: "The binary instructions the CPU actually executes. The end-product of every translator chain." },
    { term: "Source Code", definition: "The original code a programmer writes in a high-level language (e.g. .java, .py, .pas)." },
    { term: "Object Code", definition: "Intermediate machine-code output, one file per source file, before the linker combines them. Not directly runnable." },
    { term: "Executable Code", definition: "The final, runnable form (.exe on Windows, ELF on Linux). Produced by linking object code with libraries." },
    { term: "Bytecode", definition: "Intermediate, platform-independent form (e.g. Java .class files, C# IL). Not native machine code; needs a runtime to execute." },
    { term: "Translator", definition: "Umbrella term for any program that converts source code from one language into another (usually lower-level). Compilers, interpreters and assemblers are all translators." },
    { term: "Compiler", definition: "Translates an entire source program ahead of time into machine code (or bytecode), saved as a separate file. Errors reported all at once at compile time." },
    { term: "Interpreter", definition: "Translates and runs source code one statement at a time, while the program is running. No separate executable. Errors only surface when the bad line is reached." },
    { term: "Assembler", definition: "Translates assembly language into machine code. A specialised compiler for low-level code." },
    { term: "JIT Compiler", definition: "Just-In-Time \u2014 hybrid: starts running like an interpreter but compiles hot (frequently used) sections to native machine code on the fly. Used by JVM and modern JavaScript engines." },
    { term: "One-stage Compiler", definition: "Translates source code directly to native machine code for a specific CPU+OS in one step. Output runs natively, no runtime needed. Examples: C, C++, Delphi/Pascal." },
    { term: "Two-stage Compiler", definition: "Stage 1: source \u2192 bytecode (ahead-of-time). Stage 2: a runtime interprets/JIT-compiles the bytecode on the user\u2019s machine. The same bytecode runs anywhere with the runtime. Examples: Java, C#." },
    { term: "JVM", definition: "Java Virtual Machine \u2014 the runtime that executes Java bytecode. A different JVM is built for each OS; the same .class file runs on all of them." },
    { term: "JRE", definition: "Java Runtime Environment \u2014 the JVM plus the standard Java class library. End-users install the JRE so they can run Java programs." },
    { term: "Portability", definition: "The ability to run the same compiled program on different OSes/hardware without re-compiling. The key advantage of two-stage compiled languages." },
    { term: "Syntax Error", definition: "A violation of the language\u2019s grammar (missing semicolon, misspelt keyword). A compiler refuses to produce an executable; an interpreter halts at that line." },
    { term: "Runtime Error", definition: "An error that only happens while the program is running (divide-by-zero, null reference, file-not-found). The translator can\u2019t catch these." },
  ]},
  { topic: "Physical Networking", color: "emerald" as const, cards: [
    { term: "Bounded media", definition: "Cabled transmission media \u2014 the signal is confined inside a physical conductor. UTP, STP, fibre, coax. Predictable speed and security; runs are limited in length." },
    { term: "Unbounded media", definition: "Wireless transmission media \u2014 the signal travels through air or space. Wi-Fi, Bluetooth, microwave, radio. Convenient and mobile; more exposed to interference and eavesdropping." },
    { term: "UTP", definition: "Unshielded Twisted Pair \u2014 the standard LAN cable. Four pairs of copper wires twisted to cancel self-interference. Cheap; \u2264 100 m per run; common speeds 1\u201310 Gbps." },
    { term: "STP", definition: "Shielded Twisted Pair \u2014 UTP with a foil or braid shield around the pairs. Resists external EMI better than UTP. More expensive; used near electrical machinery, fluorescent lights, lift motors." },
    { term: "Fibre-optic cable", definition: "Glass or plastic core that carries data as pulses of light. Very high bandwidth (10 Gbps \u2013 400 Gbps+), long reach (kilometres), immune to EMI and lightning. Expensive to install; used for backbone runs." },
    { term: "Coaxial cable", definition: "Copper core surrounded by an insulator and metallic shield. Used historically for early LANs; still common for cable-TV / cable-internet last-mile delivery. Less common in modern LANs." },
    { term: "Wi-Fi", definition: "The dominant unbounded LAN technology. IEEE 802.11 standards (a/b/g/n/ac/ax/be); range tens of metres indoors; speeds 100 Mbps \u2013 9 Gbps depending on standard, channel, and antenna count." },
    { term: "Bluetooth", definition: "Short-range (~10 m) wireless for PAN-scale links \u2014 earbuds, mice, file transfer between phones. Lower power and lower data rate than Wi-Fi; designed to pair two devices, not a whole LAN." },
    { term: "Microwave", definition: "High-frequency radio used in narrow point-to-point beams between fixed antennas (ISP tower to ISP tower). Long reach; requires line-of-sight; weather-affected." },
    { term: "Radio waves", definition: "Broad term for omnidirectional wireless transmission \u2014 Wi-Fi, cellular, Bluetooth all use radio waves at different frequencies. Convenient and flexible; lower bandwidth and more interference than cabled media." },
    { term: "Attenuation", definition: "The gradual loss of signal strength as it travels along a medium. Doubling the cable length more than doubles the loss. Why UTP has a 100 m limit; why long runs need fibre or a repeater." },
    { term: "Cross-talk", definition: "Signal 'leaking' from one wire to a neighbouring wire in the same cable bundle. Why UTP wires are twisted in pairs (twisting cancels most of it) and why STP adds a shield for noisy environments." },
    { term: "EMI", definition: "Electromagnetic Interference \u2014 corruption of a signal by external electromagnetic fields (fluorescent lights, motors, lightning, radio sources). Mitigated by shielding (STP), routing away from sources, or using fibre." },
    { term: "Eavesdropping", definition: "An attacker passively listening to traffic on a channel. Wireless is most exposed; copper cables can be tapped with physical access; fibre is hardest to tap. The real defence is encryption (WPA2/WPA3, HTTPS)." },
  ]},
  { topic: "Topology & Devices", color: "sky" as const, cards: [
    { term: "Topology", definition: "The geometric arrangement of nodes and links in a network. Determines reliability, cost, and how badly one broken cable hurts the whole LAN." },
    { term: "Star topology", definition: "Every node connects to a central device (typically a switch). One cable break affects only that node. The dominant LAN topology today. Failure of the central switch takes down the whole LAN." },
    { term: "Bus topology", definition: "Every node connects to a single shared backbone cable. Cheap; no central device. A break anywhere on the bus disables the whole network. Largely obsolete in modern LANs." },
    { term: "Ring topology", definition: "Every node connects to two neighbours forming a closed loop; data circulates around the ring. Used in older Token Ring LANs and some metro fibre networks. A break disables the ring unless dual-ring is used." },
    { term: "Mesh topology", definition: "Every (or many) node connects directly to every (or many) other node \u2014 multiple redundant paths between any two. Expensive in cabling; common in wireless mesh systems and the Internet backbone." },
    { term: "Hybrid topology", definition: "Combines two or more of the above. The typical real-world LAN is a 'star of stars' \u2014 a central core switch with classroom switches each forming their own star. A hybrid." },
    { term: "NIC", definition: "Network Interface Card \u2014 the hardware that lets a device connect to the network. Has a unique factory-burned MAC address. Built into laptops/desktops/servers; sometimes USB or PCIe." },
    { term: "Hub (legacy)", definition: "Repeats every signal it receives to every port. No intelligence; cannot tell which port a packet should go to. Replaced by switches in the 2000s." },
    { term: "Switch", definition: "Connects multiple devices in a LAN. Learns which MAC address is on which port and forwards each frame ONLY to the destination port. Operates at the data-link layer (OSI Layer 2)." },
    { term: "Bridge", definition: "Connects two LAN segments together so they behave as one. Conceptually a two-port switch. Mostly absorbed into modern multi-port switches; still used to describe joining two media types." },
    { term: "Router", definition: "Connects two or more DIFFERENT networks (e.g. LAN \u2194 Internet) and decides where to forward each packet based on its IP address. Operates at the network layer (OSI Layer 3)." },
    { term: "Repeater", definition: "Receives a weakening signal and re-transmits it at full strength. Extends a cable or wireless link beyond its native reach. A Wi-Fi extender is a repeater for wireless." },
    { term: "Backbone", definition: "The high-bandwidth, long-distance core of a network. Inside a campus this is fibre between buildings; on the Internet it is fibre between cities and continents." },
  ]},
  { topic: "Network Addressing", color: "violet" as const, cards: [
    { term: "MAC address", definition: "48-bit hardware address burned into every NIC at the factory. Unique per network card worldwide. Written as six hex pairs (00:1A:2B:3C:4D:5E). Used by switches to deliver a frame inside a LAN." },
    { term: "IPv4", definition: "32-bit network-layer address, written as four decimal octets (192.168.1.5). Total \u2248 4.3 billion addresses, exhausted at the global level. Still the most common LAN addressing." },
    { term: "IPv6", definition: "128-bit replacement for IPv4, written as eight 16-bit hex groups. Effectively unlimited address space (\u2248 3.4 \u00d7 10\u00b3\u2078). Adoption is partial but growing." },
    { term: "DNS", definition: "Domain Name System \u2014 the global service that translates human-friendly names (www.helpmekaar.co.za) into IP addresses. Without DNS you would memorise IP addresses." },
    { term: "DHCP", definition: "Dynamic Host Configuration Protocol \u2014 the LAN service that hands out IP addresses to nodes when they join the network, on a LEASE. Why your laptop just 'works' on Wi-Fi without you typing in an IP." },
    { term: "ARP", definition: "Address Resolution Protocol \u2014 the LAN-side mechanism that maps an IP address back to a MAC address. Before sending a frame to 192.168.1.5, your computer broadcasts 'who has 192.168.1.5?'; the owner replies." },
    { term: "MAC vs IP", definition: "MAC = hardware address used INSIDE a LAN by switches. IP = logical address used BETWEEN networks by routers. ARP is what bridges the two \u2014 it asks the LAN 'who has this IP?' and gets a MAC back." },
    { term: "DHCP lease", definition: "DHCP gives an IP for a fixed time (e.g. 24 hours), not permanently. The IP can change when the lease expires or when the device reconnects. For servers, use a static (manual) IP instead." },
    { term: "Reverse DNS", definition: "DNS lookup in the opposite direction \u2014 IP \u2192 name. Used much less than forward DNS, mostly for log readability and for some email-server checks." },
  ]},
  { topic: "Protocols", color: "amber" as const, cards: [
    { term: "Frame", definition: "The unit of data on a single LAN hop. An Ethernet frame contains: destination MAC, source MAC, optional VLAN tag, EtherType/length, payload, and frame check sequence (FCS). Discarded once forwarded across a router." },
    { term: "Packet", definition: "The unit of data at the network (IP) layer \u2014 one level above the frame. Contains source IP, destination IP, other headers, and a payload. A packet is CARRIED INSIDE successive frames as it crosses the WAN." },
    { term: "Header", definition: "The bookkeeping fields at the front of a frame or packet: who it is to, who it is from, what's inside, how long it is. Stripped at each layer as the data is processed." },
    { term: "Payload", definition: "The actual data being carried (e.g. part of a web request); the cargo, as opposed to the headers (the labelling)." },
    { term: "Frame Check Sequence (FCS)", definition: "A checksum at the end of an Ethernet frame. The receiver recomputes it; if it doesn't match, the frame is discarded as corrupted (likely from EMI or attenuation)." },
    { term: "VLAN", definition: "Virtual LAN \u2014 a logical division of one physical LAN into multiple isolated networks. The VLAN tag added to a frame tells switches which VLAN it belongs to. Used to separate, e.g., staff and student traffic." },
    { term: "Broadcast", definition: "A transmission addressed to EVERY node on the LAN. ARP uses broadcast; DHCP discovery uses broadcast; switches by default flood a broadcast frame out every port." },
    { term: "Point-to-point", definition: "A transmission addressed to ONE specific recipient. Most useful traffic \u2014 file transfer, web requests \u2014 is point-to-point. The switch forwards it only to the destination port." },
    { term: "OSI Model", definition: "Seven-layer reference model that organises networking concepts: Physical (1) \u00b7 Data link (2) \u00b7 Network (3) \u00b7 Transport (4) \u00b7 Session (5) \u00b7 Presentation (6) \u00b7 Application (7). Each layer uses the one below it." },
    { term: "OSI Layer 1 \u2014 Physical", definition: "The actual cables, radio waves, voltages, and pulses of light. UTP, fibre, Wi-Fi radios all operate here. Responsible for getting raw bits across the medium." },
    { term: "OSI Layer 2 \u2014 Data link", definition: "Frames, MAC addresses, switches, FCS error detection. Gets bits reliably across one hop on a LAN." },
    { term: "OSI Layer 3 \u2014 Network", definition: "Packets, IP addresses, routers, ARP. Gets packets across multiple LANs to a destination, possibly on the other side of the world." },
    { term: "OSI Layer 4 \u2014 Transport", definition: "TCP and UDP. Reliability, ordering, multiplexing by port number. Decides which application gets which data on the destination machine." },
    { term: "WAP / Access Point", definition: "Wireless access point \u2014 a device that lets Wi-Fi clients join a wired LAN. Plugs into the LAN by cable; broadcasts a Wi-Fi network. Usually built into the home Wi-Fi router; separate in larger sites." },
    { term: "Wireless bridge", definition: "Links two separate wired LANs over a wireless link (e.g. between two buildings without a fibre run between them). The wireless hop is invisible to the rest of the network." },
    { term: "Wireless router", definition: "Consumer-grade box that combines four roles: router (LAN \u2194 Internet), switch (a few wired ports), wireless access point (Wi-Fi), and firewall. The 'Wi-Fi router' you have at home." },
    { term: "SSID", definition: "Service Set Identifier \u2014 the human-readable name of a Wi-Fi network (e.g. 'Helpmekaar-Staff'). What you pick when joining Wi-Fi." },
    { term: "Hotspot", definition: "A temporary or public Wi-Fi access point \u2014 airport, coffee shop, your phone tethering its mobile data to a laptop. Same Wi-Fi technology, different operational context (often shared and unencrypted)." },
  ]},
  { topic: "WAN Foundations", color: "rose" as const, cards: [
    { term: "WAN", definition: "Wide Area Network \u2014 a network that spans multiple physical sites, often in different cities or countries, joined by long-distance links (fibre, cellular, satellite, microwave). Defined by GEOGRAPHY AND SCALE, not by being public." },
    { term: "Internet", definition: "The largest WAN \u2014 a global network of networks connected via standard protocols (mainly TCP/IP). Owned by no single party; comprises ISPs, content providers, and end users." },
    { term: "Private WAN", definition: "A WAN that is NOT public \u2014 private fibre or VPN links connecting a single organisation's branches across cities. Same scale as the Internet but with one owner." },
    { term: "ISP", definition: "Internet Service Provider \u2014 the company that sells you Internet access (Vodacom, MTN, Telkom, RAIN, Afrihost, Frogfoot). The ISP terminates your last-mile connection and forwards traffic to and from the rest of the Internet." },
    { term: "Backbone", definition: "The high-bandwidth core of a WAN \u2014 fibre between cities inside a country, undersea fibre between continents. Moves enormous traffic volumes; speed bottlenecks live mostly in the last mile, not here." },
    { term: "Last mile", definition: "The final physical link from the ISP's network to the customer \u2014 fibre to the home, copper for ADSL, cellular for mobile, coax for cable. Typically the slowest part of the path." },
    { term: "Gateway", definition: "The device that connects one network (your LAN) to another (the WAN / Internet). Usually a ROUTER in a school or office; usually a combo 'ADSL router' or 'fibre router' at home." },
    { term: "Combo home router", definition: "The single box at home that combines four roles: router (LAN \u2194 Internet), modem (ADSL/fibre signal conversion), wireless access point (Wi-Fi), and firewall. Sold as 'fibre router' or 'Wi-Fi router'." },
    { term: "Latency (WAN)", definition: "Round-trip delay across the WAN. Matters for interactive use \u2014 gaming, video calls, web browsing. Fibre and 5G \u2248 10\u201330 ms; geostationary satellite \u2248 600 ms." },
    { term: "Bandwidth (WAN)", definition: "Maximum data rate of the WAN link. Determines how fast a download finishes or how many simultaneous streams the link can carry." },
  ]},
  { topic: "Connection Tech", color: "emerald" as const, cards: [
    { term: "ADSL", definition: "Asymmetric Digital Subscriber Line \u2014 Internet over the copper telephone line. ASYMMETRIC \u2014 download faster than upload (typical 10 Mbps down / 1 Mbps up). Cheap and widely deployed; being phased out as fibre rolls out." },
    { term: "VDSL", definition: "Very-high-bitrate DSL \u2014 faster successor to ADSL on copper; up to ~100 Mbps over short distances." },
    { term: "Fibre to the Home (FTTH)", definition: "Fibre-optic cable run all the way to the customer's premises. SYMMETRIC, very high bandwidth (100 Mbps to 1 Gbps), low latency, immune to electrical interference. Best home option where available." },
    { term: "Cable Internet", definition: "Internet over the coax cable used by cable TV. Common in some countries; less so in South Africa." },
    { term: "3G", definition: "Third-generation cellular \u2014 bandwidth ~few Mbps; obsolete for most uses, being shut down by SA carriers." },
    { term: "4G / LTE", definition: "Fourth-generation cellular \u2014 typical 10\u2013200 Mbps depending on signal and tower load. Ubiquitous SA mobile data; backbone of WhatsApp, mobile banking, ride-hailing." },
    { term: "5G", definition: "Fifth-generation cellular \u2014 100 Mbps to 1+ Gbps in good signal; latency \u2248 10 ms. Rolling out in SA cities; competes with home fibre via fixed-wireless." },
    { term: "Satellite (Geostationary)", definition: "Internet via satellites at 36 000 km altitude. Bandwidth 5\u201350 Mbps; LATENCY ~600 MS round-trip from the distance alone \u2014 feels sluggish for interactive use. Useful where nothing else reaches." },
    { term: "Satellite (LEO / Starlink)", definition: "Low-earth-orbit satellites at ~550 km. Bandwidth 50\u2013250 Mbps; latency ~30\u201350 ms. Performance approaches fibre; reaches rural areas with no fibre or cellular." },
    { term: "Microwave Link", definition: "Long-range point-to-point wireless between fixed antennas. Used by ISPs to connect a tower to backhaul fibre several kilometres away. Affected by weather; requires line-of-sight." },
    { term: "Symmetric vs Asymmetric", definition: "SYMMETRIC = upload speed equals download speed (fibre, fixed-wireless 5G). ASYMMETRIC = download faster than upload (ADSL, most cellular). Symmetric matters for video calls, server hosting, large file uploads." },
  ]},
  { topic: "Application Protocols", color: "sky" as const, cards: [
    { term: "Protocol", definition: "An agreed set of rules for how two machines exchange data: the FORMAT of messages, the ORDER of exchange, what to do on errors. Both ends must speak the same protocol or the conversation breaks." },
    { term: "Protocol stack", definition: "Protocols arranged in layers, each layer using the one below. Internet stack: Link (Ethernet/Wi-Fi) \u2192 Network (IP) \u2192 Transport (TCP/UDP) \u2192 Application (HTTP, SMTP, etc.)." },
    { term: "TCP", definition: "Transmission Control Protocol \u2014 reliable, in-order, connection-oriented transport. Splits data into numbered segments, retransmits losses, throttles when congested. Used by web, email, file transfer. (Recap from LU4 \u00a74.8.)" },
    { term: "UDP", definition: "User Datagram Protocol \u2014 fast, connectionless, no retransmission. Used where latency beats completeness \u2014 voice calls, video streaming, DNS queries, online games. (Recap from LU4 \u00a74.8.)" },
    { term: "Port number", definition: "16-bit number identifying a SERVICE on a host. HTTP = 80, HTTPS = 443, SMTP = 25/587, IMAP = 143, POP3 = 110, FTP = 21, SSH = 22, DNS = 53. Lets one IP host many services at once." },
    { term: "HTTP", definition: "Hypertext Transfer Protocol \u2014 what browsers and web servers speak. Plain text on the wire \u2014 no encryption. Port 80." },
    { term: "HTTPS", definition: "HTTP wrapped in SSL/TLS encryption. Same protocol, but the conversation on the wire is encrypted gibberish to anyone listening. Port 443. The padlock in the browser address bar." },
    { term: "SSL / TLS", definition: "The encryption layer used by HTTPS (and many other protocols). SSL was the original; TLS is the modern successor. The terms are used interchangeably in everyday speech but TLS is what's actually deployed." },
    { term: "SMTP", definition: "Simple Mail Transfer Protocol \u2014 for SENDING email. Used by your mail client (Outlook, Gmail app) to push outgoing mail to your provider's outgoing mail server. Port 25 / 587." },
    { term: "POP3", definition: "Post Office Protocol v3 \u2014 for RECEIVING email. Downloads messages to the client and (traditionally) DELETES them from the server. One device gets the mail; older single-device pattern. Port 110." },
    { term: "IMAP", definition: "Internet Message Access Protocol \u2014 for RECEIVING email. Keeps messages ON THE SERVER; clients view them in place. Multiple devices stay synchronised. The dominant modern protocol. Port 143." },
    { term: "POP3 vs IMAP", definition: "POP3 = download + delete (one device wins). IMAP = leave on server (all devices in sync). Choose IMAP if you read mail on phone + laptop + webmail and want consistent state across all three." },
    { term: "FTP", definition: "File Transfer Protocol \u2014 uploads/downloads files between client and server. Plain-text credentials. INSECURE on the public Internet; still used inside controlled networks. Port 21." },
    { term: "SFTP", definition: "SSH File Transfer Protocol \u2014 secure FTP-like transfer running over SSH. Encrypted; the recommended modern replacement for FTP. Port 22 (SSH)." },
    { term: "WebDAV", definition: "HTTP-based protocol that lets remote folders be MOUNTED as if they were local drives. Used by some collaboration tools and content systems. Runs over HTTP/HTTPS." },
    { term: "Connection vs Connectionless", definition: "TCP is CONNECTION-ORIENTED \u2014 establishes a session before data flows; reliable. UDP is CONNECTIONLESS \u2014 fires datagrams independently; fast but no guarantees. Most application protocols (HTTP, SMTP, IMAP, FTP) run on TCP; DNS, VOIP, gaming run on UDP." },
  ]},
  { topic: "Web Evolution", color: "violet" as const, cards: [
    { term: "Static site", definition: "Every visitor sees the SAME content, generated by hand-written HTML files. Server delivers a fixed file. Fast, cheap, easy to host; cannot personalise per user. Most of Web 1.0 was static." },
    { term: "Dynamic site", definition: "Content generated PER REQUEST by a server-side program reading from a database, or by client-side scripts updating the page after it loads. Almost every modern site (Gmail, Facebook, online banking) is dynamic." },
    { term: "Web 1.0", definition: "Roughly 1991\u20132004. The READ-ONLY Web. Mostly static HTML pages with text, images, hyperlinks. Browsing meant reading; user contributions rare. Examples: early Yahoo!, early news sites." },
    { term: "Web 2.0", definition: "Roughly 2004\u2013present. The READ-WRITE Web. Users CREATE content \u2014 blogs, wikis (Wikipedia), social networks, video uploads (YouTube), shared documents (Google Docs). Driven by JavaScript-heavy client-side apps." },
    { term: "Web 3.0", definition: "Emerging. The SEMANTIC / INTELLIGENT Web. Sites publish data computers can interpret, enabling smarter search, personal assistants, AI agents. NOTE: in tech-marketing 'Web3' sometimes means blockchain \u2014 different idea, same name. The IEB SAG sense is the semantic Web." },
    { term: "Web 4.0", definition: "Speculative / early. The AMBIENT / AI-BLENDED Web. Always-present, AI-mediated, woven into IoT devices, wearables, physical spaces. Less a standard than a direction; expected to clarify over the next decade." },
    { term: "Scripting language", definition: "A programming language used to add dynamic behaviour to a web page or web app. Lightweight, interpreted, designed for fast iteration." },
    { term: "Client-side scripting", definition: "Code that runs IN THE USER'S BROWSER, after the page is downloaded. Typical jobs: validate a form, animate, fetch new data without reloading. JavaScript is overwhelmingly the language used." },
    { term: "Server-side scripting", definition: "Code that runs ON THE WEB SERVER, before the response is sent. Typical jobs: read a database, build a personalised page, process payment. Languages: PHP, Python, Node.js, Java, C#." },
    { term: "Cookie", definition: "A small piece of data the server asks the browser to store. The browser sends it back with every subsequent request to the same site. Used for login, preferences, shopping carts." },
    { term: "Session cookie", definition: "Temporary; deleted when the browser closes. Used for short-lived state (current login session, in-progress shopping cart)." },
    { term: "Persistent cookie", definition: "Has a future expiry date; survives browser restarts. Used for 'remember me' logins and saved preferences." },
    { term: "Third-party cookie", definition: "Set by a domain OTHER THAN the one in the address bar (typically an ad network). Allows tracking a user across many sites. Increasingly blocked by default in modern browsers (privacy push)." },
  ]},
  { topic: "Mobile & Apps", color: "amber" as const, cards: [
    { term: "Web-based application", definition: "Software that runs INSIDE a web browser; no install, no platform-specific build. Loaded from a URL, kept up-to-date by the server. Examples: Gmail, Google Docs, Office 365, Caliper itself." },
    { term: "SaaS", definition: "Software as a Service \u2014 the business model under which most web apps are delivered: pay (or subscribe) for ongoing access to a hosted service rather than for a one-off install." },
    { term: "Single-page application (SPA)", definition: "A web app that loads ONE HTML page initially, then updates parts of the page in place using JavaScript and small data fetches. Feels closer to a desktop app than a multi-page site. Used by Gmail, Trello, much of Caliper." },
    { term: "Native mobile application", definition: "Built for ONE mobile OS using its native toolkit. Android: Java/Kotlin in Android Studio. iOS: Swift in Xcode. Distributed through the platform's app store. Highest performance and deepest device-feature access; requires a SEPARATE codebase per OS." },
    { term: "Mobile web application", definition: "A website that's MOBILE-OPTIMISED \u2014 responsive layout, large tap targets. Runs in the phone's browser. No install. Limited access to device features. CHEAPEST to build (one codebase serves desktop and mobile)." },
    { term: "Hybrid application", definition: "A WEB APPLICATION WRAPPED IN A NATIVE SHELL. Built once with HTML/CSS/JavaScript, packaged into a native container (Cordova, Ionic, React Native). Distributed via app store. Compromise: less performant than native, more capable than mobile web; one codebase across platforms." },
    { term: "App store", definition: "The platform-managed catalogue from which users install native and hybrid apps. Apple App Store (iOS), Google Play Store (Android). Vetting, discovery, payments, and updates flow through the store." },
    { term: "Screen size", definition: "Mobile design factor: phones have far less display area than desktops. Layouts must collapse to single columns, prioritise the most important content, use larger type and tap targets." },
    { term: "Processing demands", definition: "Mobile design factor: mobile CPUs are less powerful than desktop CPUs and share resources with calls/messages. Heavy in-browser computation drains battery and slows the device." },
    { term: "Storage demands", definition: "Mobile design factor: phones often have less storage than laptops; local caches must be modest. App size matters \u2014 a 200 MB app installs slowly and may not fit on entry-level phones." },
    { term: "Bandwidth requirements", definition: "Mobile design factor: mobile users frequently have METERED data plans (charged per GB) and intermittent connections. A page downloading 5 MB of images per visit is fine on Wi-Fi and painful on a 1 GB monthly plan." },
    { term: "Plug-in", definition: "Separately-installed module that ADDED support for a new content type or runtime to the browser. Historically vital \u2014 Adobe Flash, Java applets, Silverlight. ALMOST ENTIRELY RETIRED \u2014 modern browsers handle audio/video/animation natively via HTML5." },
    { term: "Browser extension", definition: "Small program installed INTO the browser that modifies its UI or behaviour. Examples: ad blockers (uBlock Origin), password managers (1Password, Bitwarden), grammar checkers (Grammarly), translation tools. Currently the dominant form of browser add-on." },
    { term: "Plug-in vs Extension", definition: "PLUG-IN added new content types (Flash, Java applets) \u2014 needed separate install \u2014 mostly extinct. EXTENSION modifies the browser itself (ad blockers, password managers) \u2014 installed via extension store \u2014 everywhere. Different role, different layer." },
  ]},
  { topic: "Searching", color: "emerald" as const, cards: [
    { term: "Search engine", definition: "A service (Google, Bing, DuckDuckGo) that crawls, indexes, and ranks pages on the Web. Turns 'I want to know X' into 'these 10 pages most likely have X'." },
    { term: "Modality (search)", definition: "The kind of input the search engine accepts: text typed by the user, audio recorded by a microphone, an image uploaded or pasted, a screenshot. Modern engines accept several." },
    { term: "Sound search", definition: "Identifying a song or audio clip from a short recording. The user holds the phone up to a speaker; the app records 5\u201315 s; within seconds it returns the title and artist. Shazam is the canonical example." },
    { term: "Audio fingerprinting", definition: "The technique behind sound search. The recording is converted into a spectrogram; distinctive peaks are extracted as a compact FINGERPRINT robust to background noise. The fingerprint is matched against a pre-built database of millions of song fingerprints." },
    { term: "Image search", definition: "Find IMAGES BY A TEXT DESCRIPTION (e.g. 'blue Vespa scooter 1960s'). Most search engines have an 'Images' tab." },
    { term: "Reverse image search", definition: "Find PAGES BY A GIVEN IMAGE \u2014 visually similar images and pages containing this image. Tools: Google Reverse Image Search, Bing Visual Search, TinEye. Useful for tracing a photo's source, spotting fakes, finding higher-resolution copies." },
    { term: "EXIF metadata", definition: "Extra information embedded INSIDE an image file by the camera or phone \u2014 date, GPS coordinates, camera settings. Reverse image search engines may surface it; downloads from social media often strip it." },
    { term: "site: operator", definition: "Restricts results to one domain. Example: 'site:sars.gov.za tax brackets' returns only SARS pages mentioning 'tax brackets'. Indispensable when one source is more trusted than the wider Web." },
    { term: "filetype: operator", definition: "Restricts results to one file extension. Example: 'filetype:pdf \"Information Technology SAG\"' returns PDFs containing that phrase. Common indexed types: pdf, doc, docx, ppt, pptx, xls, xlsx." },
    { term: "intitle: operator", definition: "Restricts results to pages whose TITLE contains the word. Example: 'intitle:syllabus information technology' finds pages with 'syllabus' specifically in the title \u2014 far more focused than searching the whole body." },
    { term: "Phrase search", definition: "Words inside double quotes must appear in that EXACT ORDER. Example: '\"third normal form\"' finds the exact phrase, not pages with the words scattered around." },
    { term: "Exclusion (-)", definition: "Excludes pages containing the term. Example: 'python -snake' returns programming-Python pages, not biology-snake pages." },
    { term: "Wildcard (*)", definition: "Placeholder for any word. Example: '\"how to * a school timetable\"' matches 'how to design / build / plan a school timetable'. Less common; supported by Google." },
    { term: "Crawl / Index / Rank", definition: "How a search engine works. CRAWL: an automated browser follows links and downloads pages. INDEX: a data structure mapping each word to the pages containing it (with positions, frequencies). RANK: an algorithm that orders matching results by relevance, authority, freshness." },
  ]},
  { topic: "Compression", color: "rose" as const, cards: [
    { term: "Multimedia", definition: "Content combining two or more of: text, images, audio, video, animation, interactive elements. YouTube, Zoom, podcasts \u2014 all multimedia." },
    { term: "Download", definition: "The file is FULLY TRANSFERRED to the user's device before (or independently of) use. Once downloaded: plays offline, replayable, shareable. Single bandwidth burst; doesn't require a steady stream." },
    { term: "Streaming", definition: "The file is delivered CONTINUOUSLY in real time, played as it arrives. Only a small buffer is held locally; the bulk is never stored on the device. Requires SUSTAINED bandwidth for the duration of playback." },
    { term: "Buffering", definition: "Holding a few seconds of the upcoming stream in memory before playing it, so brief network drops don't interrupt playback. The 'Buffering...' message means the buffer ran empty \u2014 the network couldn't keep up with the playback rate." },
    { term: "Jitter", definition: "Variation in the time between successive packets arriving. A constant 50 ms latency is fine; latency swinging between 30 ms and 200 ms produces audible glitches in voice/video calls." },
    { term: "Adaptive bitrate streaming", definition: "Modern streaming (HLS, MPEG-DASH) automatically picks a quality level matched to the user's measured bandwidth, dropping resolution if the connection slows. Why YouTube and Netflix degrade gracefully instead of stopping." },
    { term: "Video on-demand (VOD)", definition: "Pre-recorded video the user starts and stops at will. Netflix, Showmax, YouTube. Almost always streamed (lossy-compressed, adaptive bitrate)." },
    { term: "Live streaming", definition: "Video or audio broadcast as it is being captured \u2014 live sports, news, gaming streams. Adds the constraint of LOW LATENCY \u2014 the broadcaster wants viewers to see events within a few seconds, not minutes." },
    { term: "VOIP", definition: "Voice over IP \u2014 real-time voice (and often video) calls over the Internet. WhatsApp calls, Zoom audio, Microsoft Teams. Requires LOW LATENCY and LOW JITTER \u2014 humans notice delays above ~150 ms." },
    { term: "Podcast / Vodcast", definition: "Episodic AUDIO (podcast) or VIDEO (vodcast) programmes published as a feed. Subscribers download new episodes (or stream them) using a podcast app. Inherently asynchronous \u2014 listened to whenever convenient, often offline." },
    { term: "Compression", definition: "Encoding data so it occupies less space. Trade-offs: smaller file = less bandwidth/storage; processing required at both ends; sometimes quality lost." },
    { term: "Codec", definition: "Coder\u2013decoder. Software (or hardware) that performs the compression and decompression for a particular format. MP3 has an MP3 codec; H.264 has an H.264 codec. Both ends of a stream must support the same codec." },
    { term: "Lossy compression", definition: "Discards data the human eye or ear is least likely to notice, in order to shrink the file dramatically. The original CANNOT be reconstructed exactly. Examples: MP3, AAC for audio; JPEG for images; MP4/H.264/H.265/AV1 for video." },
    { term: "Lossless compression", definition: "Every bit of the original survives; the original is reconstructed EXACTLY on decompression. The file is smaller but not as small as a lossy version. Examples: FLAC for audio; PNG for images; ZIP/RAR/7z for general files." },
    { term: "Bitrate", definition: "The data rate of an encoded media stream, in kbps or Mbps. Higher bitrate = better quality, larger file, more bandwidth needed. A 128 kbps MP3 is 'good enough' for casual listening; 320 kbps is near-CD quality." },
    { term: "Sample rate", definition: "For audio, how many measurements of the sound wave are taken per second (44.1 kHz on a CD, 48 kHz for video audio). Higher = more accurate sound; doesn't necessarily mean larger file (compression dominates)." },
    { term: "Psychoacoustic model", definition: "The basis of LOSSY AUDIO compression. The encoder identifies sounds the human ear can't perceive (frequencies above hearing range, sounds masked by louder simultaneous sounds) and discards them. Why a 320 kbps MP3 sounds nearly identical to the uncompressed CD source." },
    { term: "MP3 / AAC", definition: "Lossy audio codecs. MP3 \u2014 older but ubiquitous; AAC \u2014 newer, slightly better quality at the same bitrate. Both achieve ~10\u00d7 file shrinkage over uncompressed WAV." },
    { term: "FLAC", definition: "Lossless audio codec. ~50% file shrinkage over uncompressed WAV; perfectly reconstructs the original. Used for audiophile listening and archival." },
    { term: "JPEG / PNG", definition: "JPEG = lossy image compression; small files; great for photographs but degrades with repeated saves. PNG = lossless image compression; larger files; great for screenshots, line art, anything with sharp edges." },
    { term: "MP4 / H.264 / H.265", definition: "Lossy video container (MP4) and codecs (H.264, H.265, AV1). H.265 is ~50% more efficient than H.264 at the same quality \u2014 important for 4K streaming on consumer bandwidths." },
    { term: "ZIP / RAR / 7z", definition: "Lossless GENERAL-PURPOSE compression. ZIP is universally supported; 7z compresses better; RAR is widely used historically. All reconstruct the original exactly \u2014 essential for code, documents, archives." },
  ]},
  { topic: "Errors & Validation", color: "emerald" as const, cards: [
    { term: "GIGO", definition: "Garbage In, Garbage Out \u2014 the principle that incorrect input produces incorrect output. A computer cannot fix wrong data; if a salary is captured as R 350 000 instead of R 35 000, every report based on it is wrong." },
    { term: "Input error", definition: "Any error introduced when data is captured \u2014 typos, missing fields, transposed digits, wrong units, picking the wrong dropdown option." },
    { term: "Arithmetic error", definition: "A calculation produces a wrong result for technical reasons \u2014 overflow, underflow, rounding, truncation in fixed-bit storage. Not a wrong formula; a wrong representation." },
    { term: "Transmission error", definition: "Bits arrive at the receiver different from those that left the sender. Caused by EMI, attenuation, cross-talk on cable; by interference or weak signal on wireless." },
    { term: "Logical error", definition: "Program runs without crashing but produces a wrong result because the LOGIC is wrong \u2014 a < where a <= was needed, an off-by-one, the wrong variable used. Hardest to spot." },
    { term: "Verification", definition: "Confirming that captured data MATCHES ITS SOURCE \u2014 that it was entered correctly. Asks: 'did I capture what the user intended?' Techniques: double entry, visual check, read-back." },
    { term: "Validation", definition: "Confirming that captured data is SENSIBLE \u2014 within expected ranges, formats, types. Asks: 'does the captured value make sense?' Performed by code, ideally at the moment of input." },
    { term: "Double entry", definition: "A verification technique \u2014 the user types the value twice and the system compares the two entries. Catches random typos that aren't repeated identically." },
    { term: "Presence check", definition: "Validation rule \u2014 the field must not be empty. Catches blank required fields." },
    { term: "Range check", definition: "Validation rule \u2014 the value must lie between a minimum and maximum. Age 0\u2013120; exam mark 0\u2013100; temperature \u221250\u201360." },
    { term: "Length check", definition: "Validation rule \u2014 the input has the expected number of characters. SA ID = 13 digits; SA postcode = 4 digits." },
    { term: "Type check", definition: "Validation rule \u2014 the input is the expected data type. Numeric fields reject letters; date fields reject malformed dates." },
    { term: "Uniqueness check", definition: "Validation rule \u2014 the value is not already present in the database. Email at sign-up; student number in enrolment." },
    { term: "Logical check", definition: "Validation rule \u2014 the value is consistent with other fields. Date of joining must not be after date of leaving; total subjects = number of selected checkboxes." },
    { term: "Check digit", definition: "A digit appended to a code, calculated from the others by a known formula. The system recalculates and compares; mismatch = invalid code. Catches most single-digit typos and transpositions. Used in SA ID, ISBN, credit cards (Luhn)." },
    { term: "Checksum", definition: "Like a check digit but for whole files / blocks of data. A value computed from the entire content, sent alongside, recalculated by the receiver. Mismatch means the data was altered." },
    { term: "Parity bit", definition: "Extra bit appended to a byte so the total number of 1s is even (even parity) or odd (odd parity). Receiver counts; mismatch = at least one bit flipped. Catches single-bit errors only \u2014 two flips can cancel out." },
    { term: "Barcode / QR / RFID", definition: "Input techniques that REPLACE typing. Barcode = 1D, point-of-sale; QR = 2D, more data; RFID = radio chip, no line-of-sight needed. All near-zero error rate compared to manual typing." },
    { term: "OCR", definition: "Optical Character Recognition \u2014 software that converts an image of text into editable digital text. Eliminates re-typing pages; introduces small errors (O for 0, l for 1)." },
    { term: "Biometric input", definition: "Identifies a person by a physical or behavioural trait (fingerprint, iris, face, voice). Removes the 'did I type the right credentials' failure mode; introduces sensor / environmental failure modes." },
    { term: "GUI Input Controls", definition: "Dropdown for FIXED LIST; checkbox for INDEPENDENT MULTIPLE choices; radio button for MUTUALLY EXCLUSIVE choices; date picker for DATES. Pattern: don't ask the user to type what you can ask them to pick." },
  ]},
  { topic: "DBMS Integrity", color: "sky" as const, cards: [
    { term: "Data integrity", definition: "The property that data is ACCURATE, COMPLETE, and UNCHANGED since it was last legitimately updated. The umbrella property the five Cs measure." },
    { term: "Accuracy", definition: "The captured data correctly reflects reality \u2014 no typos, no transcription errors, no wrong units. Often confused with 'correctness'; accuracy is about the VALUE matching reality." },
    { term: "Correctness", definition: "The data is internally consistent and follows the system's rules \u2014 referential integrity, foreign-key constraints, no orphan records, no contradictions across tables." },
    { term: "Currency", definition: "The data is UP-TO-DATE \u2014 phone numbers, addresses, prices, stock counts reflect today's reality, not last year's. Stale data may still be 'accurate' for a past moment but useless now." },
    { term: "Completeness", definition: "All required fields are populated; no records are missing. A learner record without a date of birth is not complete; a sales report missing one day's transactions is not complete." },
    { term: "Relevance", definition: "The data captured is APPROPRIATE FOR THE PURPOSE. Storing every key a user has ever pressed is irrelevant for a payroll system; storing only what is relevant respects privacy and reduces clutter." },
    { term: "Five Cs (memory aid)", definition: "Accuracy \u00b7 Correctness \u00b7 Currency \u00b7 Completeness \u00b7 Relevance \u2014 the five properties the SAG uses to measure DBMS data integrity. (Some texts use 'Five Cs' meaning Currency / Correctness / Completeness / Consistency / Conformity \u2014 both flavours appear in IEB material.)" },
    { term: "Referential integrity", definition: "DBMS rule that every foreign key must point to an existing primary key \u2014 no orphan records. The DBMS rejects updates that would break this." },
    { term: "Audit trail (DB)", definition: "A log of every change to a database record \u2014 who updated, when, the before/after values. Lets the school trace bad data back to its source and roll back if needed." },
  ]},
  { topic: "Threats", color: "violet" as const, cards: [
    { term: "Hardware failure", definition: "Components fail eventually. HDDs wear mechanically; SSDs wear by write cycles; PSUs and motherboards fail electrically. A single failure can lose every byte on the affected device." },
    { term: "Power failure", definition: "Electricity supply is cut. Running computers shut down uncleanly; unsaved work is lost; databases mid-write may corrupt. SA load-shedding makes this an everyday risk." },
    { term: "Power surge", definition: "Sudden spike in voltage (lightning, grid switching). Can fry electronics in milliseconds \u2014 far more damaging than a clean cut." },
    { term: "Malware", definition: "Umbrella term for malicious software \u2014 virus, worm, ransomware, spyware, trojan, rootkit. Distinguished by what they DO and how they SPREAD." },
    { term: "Virus", definition: "Malicious code that ATTACHES ITSELF to a host program or file and runs when the host runs. Spreads when the infected file is shared." },
    { term: "Worm", definition: "Like a virus but SELF-PROPAGATING across networks; doesn't need a host file. Examples: Morris Worm, ILOVEYOU, WannaCry." },
    { term: "Ransomware", definition: "Malware that ENCRYPTS the user's files and demands payment (usually crypto) for the decryption key. Modern variants also exfiltrate data and threaten to publish it (double extortion)." },
    { term: "Spyware", definition: "Malware that silently OBSERVES the user \u2014 keystrokes, screens, web activity \u2014 and sends data to the attacker. Used to steal credentials, banking info, business secrets." },
    { term: "Phishing", definition: "Tricking a user into giving up credentials by IMPERSONATING a trusted party \u2014 bank, employer, school IT. Usually via email; SMS = smishing, voice = vishing." },
    { term: "Pharming", definition: "Quietly redirecting a user from a real site to a fake one WITHOUT changing the URL they typed \u2014 typically by poisoning DNS or hosts files. Worse than phishing because the user did nothing wrong." },
    { term: "Spoofing", definition: "Falsifying the source of a message \u2014 From: address, caller ID, source IP. The technical basis for most phishing and pharming attacks." },
    { term: "Botnet", definition: "A network of compromised computers ('zombies') controlled remotely by an attacker. Used for DDoS, spam, cryptocurrency mining, spreading malware. The owner usually doesn't know." },
    { term: "Zombie", definition: "A single computer in a botnet \u2014 usually a domestic or office PC infected with malware that takes commands from a central control server." },
    { term: "DoS", definition: "Denial of Service \u2014 flooding a server with so many requests it can't respond to legitimate users. The attacker doesn't gain access; they take AVAILABILITY away." },
    { term: "DDoS", definition: "Distributed DoS \u2014 DoS launched from a botnet (many sources at once). Much harder to block than a single attacking IP." },
    { term: "Open port", definition: "A network port with a service listening on it. Each open port is a potential entry point; reducing the open-port footprint shrinks the attack surface." },
    { term: "Wi-Fi vulnerabilities", definition: "Weak passphrases, outdated encryption (WEP, WPA), open networks, default router-admin passwords, evil-twin APs. Each of these turns a wireless network into an entry point." },
    { term: "SQL Injection", definition: "An attack that smuggles SQL commands into an input field, tricking the database into executing them \u2014 leaking, modifying, or deleting data. Defended by parameterised queries and input validation." },
  ]},
  { topic: "Security Solutions", color: "amber" as const, cards: [
    { term: "RAID", definition: "Redundant Array of Independent Disks \u2014 multiple drives behaving as one, with extra capacity used for redundancy or speed. RAID 1 = mirror; RAID 5 = striping with parity." },
    { term: "Mirroring (RAID 1)", definition: "Every block written to disk A is also written to disk B. Either disk can fail with no data loss. Cost: half the raw capacity. Pure redundancy." },
    { term: "Striping with parity (RAID 5)", definition: "Data striped across 3+ disks; one disk's worth of capacity is parity, spread across the array. Survives one disk failure; uses (n\u22121)/n of capacity. Slower writes; long rebuild time." },
    { term: "Backup", definition: "A separate copy of data, made regularly, kept somewhere different from the original. The single most important defence against ransomware, hardware failure, fire, theft." },
    { term: "Local vs Remote backup", definition: "LOCAL = same building (external HDD, NAS) \u2014 fast restore; vulnerable to fire/theft/ransomware on LAN. REMOTE = off-site or cloud \u2014 slow restore; survives building-level disasters." },
    { term: "3-2-1 backup rule", definition: "3 copies of important data, on 2 different media, with 1 copy off-site. The lowest-effort discipline that survives most realistic disasters." },
    { term: "UPS", definition: "Uninterruptible Power Supply \u2014 battery-backed power source that bridges short outages and gives time for clean shutdown during longer ones. Also CONDITIONS the supply, smoothing surges." },
    { term: "Redundant power supply", definition: "A server with two PSUs fed from independent power circuits. If one fails or its circuit drops, the other keeps the server running." },
    { term: "Strong password", definition: "Long (>12 chars), not based on guessable words, not reused across sites. Modern advice: passphrases over short complex passwords; password manager so each site has a unique random one." },
    { term: "Multi-factor authentication (MFA)", definition: "Login requires more than just a password \u2014 a code from an authenticator app, a hardware token, or a biometric. Even if the password is stolen, the attacker can't get in." },
    { term: "User rights / Permissions", definition: "What each user account is allowed to do \u2014 read which files, run which programs, install software. The OS enforces these." },
    { term: "Principle of least privilege", definition: "Every user (and program) gets ONLY the permissions they actually need. A learner account shouldn't install software; a teacher account shouldn't be a domain admin. Limits blast radius if compromised." },
    { term: "Encryption", definition: "Converting readable data ('plaintext') to unreadable form ('ciphertext') using a key. Only the right key can decrypt. AT REST = files on disk; IN TRANSIT = data on the wire (TLS, HTTPS, WPA3)." },
    { term: "AES", definition: "Advanced Encryption Standard \u2014 the dominant SYMMETRIC algorithm (same key encrypts and decrypts). Variants AES-128, AES-256. Used widely for at-rest and in-transit encryption." },
    { term: "Key length", definition: "Size of the encryption key in bits. Longer = exponentially harder to brute-force. AES-128 safe for most purposes; AES-256 recommended for sensitive data and long-term secrets." },
    { term: "Symmetric vs Asymmetric", definition: "SYMMETRIC = one key shared by sender and receiver; fast but the key must be exchanged securely first. ASYMMETRIC = public+private key pair; the public key encrypts, only the private key decrypts; solves the key-exchange problem but slower." },
    { term: "Digital signature", definition: "Uses public-key cryptography to prove a message was sent by the holder of a particular private key, and that the message wasn't altered. Basis for HTTPS server identity, signed updates, secure email." },
    { term: "WPA2 / WPA3", definition: "Wi-Fi encryption standards. WPA2 (2004) has been the workhorse; WPA3 (2018) is stronger handshake and individualised data encryption. New equipment should default to WPA3 with WPA2 fallback." },
    { term: "TKIP", definition: "Temporal Key Integrity Protocol \u2014 older Wi-Fi encryption used with original WPA; now deprecated. Replaced by AES-CCMP in WPA2+. Mentioned in the SAG; should not be used for new installs." },
    { term: "Firewall", definition: "Hardware or software that INSPECTS traffic entering and leaving a network and blocks anything violating the rules. Edge firewall sits between LAN and Internet; host firewalls run on each machine." },
    { term: "Port filtering", definition: "Firewall rule pattern of allowing or blocking traffic by destination PORT. Only ports for legitimate services are open; everything else closed by default. Reduces attack surface." },
    { term: "Audit trail", definition: "A log of who did what, when. Records logins, configuration changes, access to sensitive data. Needed for incident response, compliance, and deterrence." },
    { term: "Anti-malware", definition: "Software that detects and removes viruses, worms, ransomware, spyware. Combines SIGNATURE matching (known threats) with BEHAVIOURAL detection (rapid mass encryption = ransomware). Must be kept up-to-date." },
    { term: "Patch / Update", definition: "A software fix for a known bug or security vulnerability. MOST successful attacks exploit known vulnerabilities for which a patch already exists \u2014 the victim hadn't installed it. Patching is one of the highest-value security activities." },
  ]},
  { topic: "Workplace Impact", color: "emerald" as const, cards: [
    { term: "Decentralisation", definition: "The shift from work being concentrated in a single physical office to being distributed across locations \u2014 telecommuting, remote work, hybrid working, mobile offices, branch offices over WAN. Enabled by reliable Internet, cloud collaboration, and mobile devices." },
    { term: "Telecommuting", definition: "Working from home (or any non-office location) using employer connectivity. Now the dominant mode for many information-economy roles after the 2020 pandemic." },
    { term: "Mobile Office", definition: "A workplace pattern where staff are routinely productive away from a fixed desk \u2014 laptop, smartphone, mobile data, cloud storage \u2014 turning any location into a temporary office." },
    { term: "Acceptable User Policy (AUP)", definition: "A written policy defining what employees (or learners) may and may not do with employer-provided IT \u2014 devices, accounts, network, data. Signed as a condition of access; breach is grounds for disciplinary action." },
    { term: "Robotics", definition: "The replacement (or augmentation) of human physical labour by programmable machines \u2014 industrial robots in factories, warehouse robots in logistics, surgical robots in hospitals." },
    { term: "Artificial Intelligence (AI)", definition: "Software systems that perform tasks formerly requiring human intelligence \u2014 language understanding, image recognition, decision support, content generation. Modern AI (deep learning, large language models) is far more capable than 1990s\u20132010s 'AI'." },
    { term: "UAV (Drone)", definition: "Unmanned Aerial Vehicle \u2014 a flying robot, remotely piloted or autonomous. Used in agriculture, security, parcel delivery, film-making, surveying. Replaces or augments roles previously done by helicopters or ground couriers." },
    { term: "Job Displacement", definition: "The negative side of automation \u2014 workers replaced by robots, AI, or UAVs face unemployment; impact falls disproportionately on lower-skilled roles. New roles (AI supervision, prompt engineering) emerge but rarely absorb all the displaced." },
  ]},
  { topic: "Computer Crime", color: "sky" as const, cards: [
    { term: "Social Engineering", definition: "Manipulating PEOPLE (not computers) to obtain information, access, or actions that benefit an attacker. Bypasses technical defences by going through the user." },
    { term: "Shoulder Surfing", definition: "Observing someone enter sensitive information by looking over their shoulder \u2014 passwords typed in a coffee shop, PINs at an ATM, data on a colleague\u2019s screen. Mitigated by privacy filters and situational awareness." },
    { term: "Dumpster Diving", definition: "Searching through discarded paper or e-waste for sensitive information \u2014 printed account numbers, contact lists, hard drives that weren\u2019t properly erased. Mitigated by shredding and certified data-erasure." },
    { term: "Phishing", definition: "Tricking the user via email, SMS, or web message into giving up credentials or clicking a malicious link. A social-engineering tactic at heart, even though it\u2019s delivered electronically." },
    { term: "Trojan Horse (social)", definition: "Software disguised as legitimate (a 'free game', a 'useful utility') that the user installs willingly. The deception is social; the consequence is technical \u2014 hidden malware activates after install." },
    { term: "Reverse Social Engineering", definition: "The attacker creates an apparent problem or authority that the victim CONTACTS them about. Example: 'IT Help' cards left near printers; staff later phone the attacker for 'support' and willingly hand over passwords." },
    { term: "Social-media Engineering", definition: "Using publicly-visible social-media information (job title, employer, family details, vacation photos) to build a convincing pretext for a phishing attack or impersonation." },
    { term: "Hacker", definition: "Historically a neutral term for someone who explores computer systems creatively. Modern usage is loose \u2014 popular media uses 'hacker' to mean any computer-crime perpetrator." },
    { term: "White-hat Hacker", definition: "Security researcher who breaks into systems WITH PERMISSION to find vulnerabilities so they can be fixed. Penetration testers, bug-bounty hunters. Legal and beneficial." },
    { term: "Black-hat Hacker", definition: "Breaks into systems MALICIOUSLY AND ILLEGALLY for personal gain or harm. The popular-press meaning of 'hacker'." },
    { term: "Grey-hat Hacker", definition: "Operates between white and black \u2014 explores systems without permission but with no malicious intent, sometimes disclosing vulnerabilities publicly. Legally murky." },
    { term: "Cracker", definition: "An older term for a malicious hacker \u2014 distinguished from 'hacker' in earlier writing. Modern usage has faded as 'hacker' colloquially absorbed both meanings." },
    { term: "Virus / Malware Author", definition: "Someone who writes malicious software (viruses, ransomware, spyware). May or may not be the same person who deploys it; modern malware is often bought and sold between criminal groups." },
    { term: "Hardware Theft", definition: "Physical theft of devices \u2014 laptops, phones, servers. Often the gateway to information theft (a lost laptop exposes its data unless the disk is encrypted)." },
    { term: "Software Theft (Piracy)", definition: "Unauthorised copying or distribution of paid software \u2014 cracked Windows licences, pirated games, copying paid music or films." },
    { term: "Information Theft", definition: "Unauthorised access to and copying of confidential data \u2014 customer records, intellectual property, financial information. The most damaging category for businesses and governments." },
    { term: "Identity Theft", definition: "Using another person\u2019s identifying information (ID number, banking details, signatures) to impersonate them \u2014 fraudulent accounts, loans in their name, tax fraud." },
    { term: "Bandwidth Theft", definition: "Using someone else\u2019s Internet connection without permission \u2014 piggy-backing on unsecured Wi-Fi, hijacking server bandwidth for cryptocurrency mining." },
    { term: "Time Theft", definition: "Using employer-paid time for personal activity \u2014 extensive personal browsing, social media or gaming during work hours. Mitigated by an AUP and reasonable monitoring." },
    { term: "Service Theft", definition: "Using a paid service in violation of its terms \u2014 subscription-sharing, fraudulent free-trial cycling, unauthorised use of cloud-storage allocations." },
  ]},
  { topic: "Information Literacy", color: "violet" as const, cards: [
    { term: "Affiliation", definition: "WHO supports or hosts the source? Funding and ownership shape what gets emphasised \u2014 a hospital\u2019s health page, a .gov tax page, an industry-funded advocacy site each carry different weight." },
    { term: "Audience", definition: "WHO is the source written for? A primary-school explainer is not a research paper. Match the source\u2019s intended audience to your need." },
    { term: "Authority", definition: "WHO is the author and what are their credentials? Subject-matter expert with a track record? Anonymous? Journalist? AI-generated? Authority is about expertise AND accountability." },
    { term: "Content", definition: "Is the content well-organised, well-supported, free of broken links, and citing primary sources? Quality and completeness of the material itself." },
    { term: "Currency", definition: "Is the information UP-TO-DATE? For tax tables, syllabus details, and technology, out-of-date is wrong. For historical or theoretical material, age matters less." },
    { term: "Design", definition: "Is the page navigable, professionally presented, and reasonably fast? Bad design alone doesn\u2019t make a source untrustworthy, but a site that hasn\u2019t been updated in years often has equally stale content." },
    { term: "Objectivity", definition: "Does the source acknowledge other viewpoints, or advocate one position without acknowledgement? Bias is not the same as falsehood \u2014 knowing the bias lets you read the source critically." },
    { term: "Triangulation", definition: "The practice of cross-checking a fact across at least THREE independent sources before relying on it. Not in the SAG\u2019s seven-criterion list but the practical extension that holds them together." },
    { term: "Fake News", definition: "Fabricated or deliberately misleading content presented as journalism. Often optimised to spread on social media. The seven evaluation criteria are how a learner separates it from the real thing." },
    { term: "Misinformation", definition: "False information spread WITHOUT intent to deceive \u2014 someone passing on what they believed to be true. Distinct from disinformation (false AND deliberately spread)." },
    { term: "Disinformation", definition: "False information spread DELIBERATELY to deceive \u2014 propaganda, fraud, political manipulation. Stronger than misinformation; intent is what separates the two." },
  ]},
];

const ALL_CARDS = FLASHCARDS.flatMap(g => g.cards.map(c => ({ ...c, topic: g.topic, color: g.color })));

const colorMap = {
  sky: { border: "border-sky-200", accent: "bg-sky-500", text: "text-sky-700", badge: "bg-sky-100 text-sky-700" },
  violet: { border: "border-violet-200", accent: "bg-violet-500", text: "text-violet-700", badge: "bg-violet-100 text-violet-700" },
  amber: { border: "border-amber-200", accent: "bg-amber-500", text: "text-amber-700", badge: "bg-amber-100 text-amber-700" },
  rose: { border: "border-rose-200", accent: "bg-rose-500", text: "text-rose-700", badge: "bg-rose-100 text-rose-700" },
  emerald: { border: "border-emerald-200", accent: "bg-emerald-500", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-700" },
};

// ─── Reusable Components ─────────────────────────────────────────────────────

function Spoiler({ label = "Show Answer", children }: { label?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="my-3">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm font-semibold tracking-wide uppercase px-3 py-1.5 rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
      >
        {open ? <EyeOff size={14} /> : <Eye size={14} />}
        {open ? "Hide Answer" : label}
      </button>
      {open && (
        <div className="mt-2 p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm leading-relaxed font-mono whitespace-pre-wrap">
          {children}
        </div>
      )}
    </div>
  );
}

function Section({ id, title, completed, onToggle, defaultOpen = false, children }: {
  id: string;
  title: string;
  completed: boolean;
  onToggle: () => void;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section id={id} className="mb-6 scroll-mt-20">
      <div
        className="flex items-center gap-3 cursor-pointer group"
        onClick={() => setOpen(!open)}
      >
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          className="shrink-0 text-slate-400 hover:text-emerald-600 transition-colors"
          title="Mark as completed"
        >
          {completed ? <CheckSquare size={20} className="text-emerald-600" /> : <Square size={20} />}
        </button>
        <h2 className={`flex-1 text-lg md:text-xl font-bold tracking-tight ${completed ? "text-emerald-700" : "text-slate-800"}`}>
          {title}
        </h2>
        <span className="text-slate-400 group-hover:text-slate-600 transition-colors">
          {open ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </span>
      </div>
      <div className={`border-l-2 ml-2.5 pl-6 mt-2 ${open ? "border-amber-300" : "border-transparent"}`}>
        {open && <div className="pb-2 text-slate-700 text-sm md:text-base leading-relaxed">{children}</div>}
      </div>
    </section>
  );
}

function KeyTerm({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex gap-2 my-1.5">
      <span className="font-bold text-amber-700 shrink-0">{term}:</span>
      <span>{children}</span>
    </div>
  );
}

function InfoBox({ title, children, color = "blue" }: { title?: string; children: ReactNode; color?: "blue" | "green" | "orange" | "red" }) {
  const colors: Record<string, string> = {
    blue: "bg-sky-50 border-sky-300 text-sky-900",
    green: "bg-emerald-50 border-emerald-300 text-emerald-900",
    orange: "bg-orange-50 border-orange-300 text-orange-900",
    red: "bg-red-50 border-red-300 text-red-900",
  };
  return (
    <div className={`my-4 p-4 rounded-lg border ${colors[color]}`}>
      {title && <div className="font-bold mb-1">{title}</div>}
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto my-4">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="text-left px-3 py-2 bg-slate-100 border border-slate-200 font-semibold text-slate-700">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-slate-50"}>
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-2 border border-slate-200 font-mono">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Worked({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="my-4 p-4 rounded-lg bg-violet-50 border border-violet-200">
      <div className="font-bold text-violet-800 mb-2 text-sm uppercase tracking-wide">{title}</div>
      <div className="text-sm text-violet-900 leading-relaxed font-mono whitespace-pre-wrap">{children}</div>
    </div>
  );
}

function Flashcard({ term, definition, topic, color, flipped, onFlip }: {
  term: string;
  definition: string;
  topic: string;
  color: keyof typeof colorMap;
  flipped: boolean;
  onFlip: () => void;
}) {
  const c = colorMap[color as keyof typeof colorMap] || colorMap.sky;
  return (
    <div onClick={onFlip} className={`relative cursor-pointer rounded-xl border-2 ${c.border} overflow-hidden transition-all duration-200 hover:shadow-lg active:scale-[0.98] select-none`} style={{ minHeight: "180px" }}>
      <div className={`absolute top-0 left-0 right-0 h-1.5 ${c.accent}`} />
      <div className="p-5 flex flex-col h-full justify-between" style={{ minHeight: "180px" }}>
        <div>
          <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${c.badge} mb-3`}>{topic}</span>
          {!flipped ? (<div><p className="text-lg font-bold text-slate-800 leading-snug">{term}</p><p className="text-xs text-slate-400 mt-3 italic">Tap to reveal definition</p></div>)
          : (<div><p className={`text-xs font-semibold uppercase tracking-wider ${c.text} mb-1`}>{term}</p><p className="text-sm text-slate-700 leading-relaxed">{definition}</p></div>)}
        </div>
      </div>
    </div>
  );
}

function CheatSheet() {
  const [mode, setMode] = useState<"browse" | "quiz">("browse");
  const [filter, setFilter] = useState<string>("all");
  const [flippedCards, setFlippedCards] = useState<Record<string, boolean>>({});
  const [quizOrder, setQuizOrder] = useState<FlashcardWithTopic[]>([]);
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizFlipped, setQuizFlipped] = useState(false);
  const [knownCount, setKnownCount] = useState(0);
  const filteredCards = filter === "all" ? ALL_CARDS : ALL_CARDS.filter(c => c.topic === filter);

  const startQuiz = (shuffle = true) => {
    const cards = [...filteredCards];
    if (shuffle) { for (let i = cards.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [cards[i], cards[j]] = [cards[j], cards[i]]; } }
    setQuizOrder(cards); setQuizIndex(0); setQuizFlipped(false); setKnownCount(0); setMode("quiz");
  };
  const topics = ["all", ...FLASHCARDS.map(g => g.topic)];

  if (mode === "quiz") {
    if (quizOrder.length === 0) return null;
    const done = quizIndex >= quizOrder.length;
    const card = !done ? quizOrder[quizIndex] : null;
    return (
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setMode("browse")} className="text-sm text-slate-500 hover:text-slate-700">{"\u2190"} Back to browse</button>
          <span className="text-sm font-semibold text-slate-600">{done ? "Complete!" : `${quizIndex + 1} / ${quizOrder.length}`}</span>
        </div>
        <div className="w-full h-2 rounded-full bg-slate-200 mb-6 overflow-hidden"><div className="h-full rounded-full bg-emerald-500 transition-all duration-300" style={{ width: `${(quizIndex / quizOrder.length) * 100}%` }} /></div>
        {done ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">{"\uD83C\uDF89"}</div>
            <p className="text-xl font-bold text-slate-800 mb-2">All cards reviewed!</p>
            <p className="text-slate-600 mb-1">You marked <strong className="text-emerald-600">{knownCount}</strong> out of <strong>{quizOrder.length}</strong> as known.</p>
            <p className="text-sm text-slate-500 mb-6">{knownCount === quizOrder.length ? "Perfect!" : knownCount >= quizOrder.length * 0.7 ? "Great work! Review the ones you missed." : "Keep studying!"}</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button onClick={() => startQuiz(true)} className="px-4 py-2 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors flex items-center gap-2"><Shuffle size={16} /> Retry</button>
              <button onClick={() => setMode("browse")} className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 font-semibold hover:bg-slate-300 transition-colors">Browse</button>
            </div>
          </div>
        ) : (
          card && <div>
            <Flashcard term={card.term} definition={card.definition} topic={card.topic} color={card.color} flipped={quizFlipped} onFlip={() => setQuizFlipped(!quizFlipped)} />
            {quizFlipped && (
              <div className="flex gap-3 justify-center mt-5">
                <button onClick={() => { setQuizIndex(i => i + 1); setQuizFlipped(false); }} className="px-5 py-2.5 rounded-lg bg-rose-100 text-rose-700 font-semibold hover:bg-rose-200 transition-colors">Still learning</button>
                <button onClick={() => { setKnownCount(k => k + 1); setQuizIndex(i => i + 1); setQuizFlipped(false); }} className="px-5 py-2.5 rounded-lg bg-emerald-100 text-emerald-700 font-semibold hover:bg-emerald-200 transition-colors">{"\u2713"} I know this</button>
              </div>
            )}
            {!quizFlipped && <p className="text-center text-sm text-slate-400 mt-4 italic">Tap the card to reveal the definition</p>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap gap-1.5">
          {topics.map(t => <button key={t} onClick={() => { setFilter(t); setFlippedCards({}); }} className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${filter === t ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{t === "all" ? `All (${ALL_CARDS.length})` : t}</button>)}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setFlippedCards({})} className="text-xs px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 flex items-center gap-1 transition-colors"><RotateCcw size={12} /> Reset</button>
          <button onClick={() => startQuiz(true)} className="text-xs px-3 py-1.5 rounded-full bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-1.5 font-semibold transition-colors"><Shuffle size={12} /> Quiz me</button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">Showing {filteredCards.length} term{filteredCards.length !== 1 ? "s" : ""} {"\u00b7"} Tap a card to reveal its definition</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {filteredCards.map((card, idx) => { const gid = `${filter}-${idx}`; return <Flashcard key={gid} term={card.term} definition={card.definition} topic={card.topic} color={card.color} flipped={!!flippedCards[gid]} onFlip={() => setFlippedCards(p => ({ ...p, [gid]: !p[gid] }))} />; })}
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function StudyGuide() {
  const [completed, setCompleted] = useState<Record<string, boolean>>(() => {
    return {};
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const [activeTab, setActiveTab] = useState<"study" | "cheatsheet">("study");
  const [expandedLUs, setExpandedLUs] = useState<Record<number, boolean>>(() => ({ 1: true }));
  const toggleLU = (lu: number) => setExpandedLUs((p) => ({ ...p, [lu]: !p[lu] }));

  useEffect(() => {
    const handler = () => setShowTop(window.scrollY > 400);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const toggle = (id: string) => setCompleted((c) => ({ ...c, [id]: !c[id] }));
  const progress = SECTIONS.filter((s) => completed[s.id]).length;

  const scrollTo = (id: string) => {
    setActiveTab("study");
    setTimeout(() => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); }, 50);
    setMenuOpen(false);
  };

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #faf5ef 0%, #f0ebe3 50%, #e8e0d4 100%)", fontFamily: "'Georgia', 'Cambria', serif" }}>
      {/* ─── Top bar ─── */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-amber-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-slate-600">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <BookOpen size={22} className="text-amber-700 shrink-0 hidden md:block" />
          <div className="flex-1 min-w-0">
            <h1 className="text-base md:text-lg font-bold text-slate-800 truncate" style={{ fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" }}>
              Information Technology
            </h1>
            <p className="text-xs text-slate-500">Grade 11 · Interactive Study Guide</p>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <div className="w-24 h-2 rounded-full bg-slate-200 overflow-hidden">
              <div
                className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                style={{ width: `${(progress / SECTIONS.length) * 100}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-slate-500">{progress}/{SECTIONS.length}</span>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 flex gap-0">
          <button onClick={() => { setActiveTab("study"); setMenuOpen(false); }} className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === "study" ? "border-amber-600 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}><span className="flex items-center gap-1.5"><BookOpen size={14} /> Study Guide</span></button>
          <button onClick={() => { setActiveTab("cheatsheet"); setMenuOpen(false); }} className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === "cheatsheet" ? "border-amber-600 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}><span className="flex items-center gap-1.5"><Layers size={14} /> Cheat Sheet</span></button>
        </div>

        {/* ─── Mobile drawer nav (LU-grouped). Desktop uses the sidebar below. ─── */}
        {activeTab === "study" && (
        <nav className={`${menuOpen ? "block" : "hidden"} md:hidden border-t border-amber-100 bg-white/60 max-h-[70vh] overflow-y-auto`}>
          <div className="max-w-4xl mx-auto px-4 py-2 space-y-1">
            {LU_NUMBERS.map((lu) => {
              const sections = SECTIONS_BY_LU[lu] || [];
              const isExpanded = !!expandedLUs[lu];
              const luProgress = sections.filter((s) => completed[s.id]).length;
              return (
                <div key={lu}>
                  <button
                    onClick={() => toggleLU(lu)}
                    className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-semibold text-slate-700 hover:bg-amber-50"
                  >
                    {isExpanded ? <ChevronDown size={14} className="shrink-0" /> : <ChevronRight size={14} className="shrink-0" />}
                    <span className="flex-1 text-left">LU{lu} — {LU_TITLES[lu]}</span>
                    <span className={`text-[10px] tabular-nums ${luProgress === sections.length ? "text-emerald-600 font-bold" : "text-slate-400"}`}>{luProgress}/{sections.length}</span>
                  </button>
                  {isExpanded && (
                    <div className="ml-3 mt-0.5 border-l border-amber-100 flex flex-col">
                      {sections.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => scrollTo(s.id)}
                          className={`text-left text-xs pl-3 pr-2 py-1 transition-colors ${
                            completed[s.id]
                              ? "text-emerald-700 font-semibold"
                              : "text-slate-500 hover:text-amber-800"
                          }`}
                        >
                          {s.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
        )}
      </header>

      {/* ─── Content (with desktop sidebar at md+) ─── */}
      <div className="md:flex md:items-start md:max-w-7xl md:mx-auto md:px-4 md:gap-6">
      {activeTab === "study" && (
        <aside className="hidden md:block md:w-60 md:shrink-0 md:sticky md:top-28 md:self-start md:max-h-[calc(100vh-7rem)] md:overflow-y-auto md:py-8">
          <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3 px-2">Contents</h3>
          <nav className="space-y-0.5">
            {LU_NUMBERS.map((lu) => {
              const sections = SECTIONS_BY_LU[lu] || [];
              const isExpanded = !!expandedLUs[lu];
              const luProgress = sections.filter((s) => completed[s.id]).length;
              return (
                <div key={lu}>
                  <button
                    onClick={() => toggleLU(lu)}
                    className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-semibold text-slate-700 hover:bg-amber-50 transition-colors"
                  >
                    {isExpanded ? <ChevronDown size={14} className="shrink-0" /> : <ChevronRight size={14} className="shrink-0" />}
                    <span className="flex-1 text-left truncate">LU{lu} — {LU_TITLES[lu]}</span>
                    <span className={`text-[10px] tabular-nums ${luProgress === sections.length && sections.length > 0 ? "text-emerald-600 font-bold" : "text-slate-400"}`}>{luProgress}/{sections.length}</span>
                  </button>
                  {isExpanded && (
                    <div className="ml-3 mt-0.5 border-l border-amber-100 flex flex-col">
                      {sections.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => scrollTo(s.id)}
                          className={`text-left text-xs pl-3 pr-2 py-1 transition-colors ${
                            completed[s.id]
                              ? "text-emerald-700 font-semibold"
                              : "text-slate-500 hover:text-amber-800"
                          }`}
                        >
                          {s.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>
      )}
      <main className="max-w-4xl mx-auto px-4 py-8 md:flex-1 md:max-w-none md:mx-0 md:px-0">
        {activeTab === "cheatsheet" && (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-800 mb-1" style={{ fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" }}>Term Flashcards</h2>
              <p className="text-sm text-slate-600">{ALL_CARDS.length} terms across {FLASHCARDS.length} topics. Tap a card to reveal its definition, or hit <strong>Quiz me</strong> to test yourself.</p>
            </div>
            <CheatSheet />
          </div>
        )}

        {activeTab === "study" && (<div>

        {/* ══════════════ LEARNING UNIT 1 ══════════════ */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-amber-800 text-center" style={{ fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" }}>Learning Unit 1 — Data Representation</h2>
          <p className="text-center text-sm text-slate-500 mt-1">Word Size, Binary Numbers & Numeric Representation</p>
        </div>

        {/* ══════════════ 1.1 WORD SIZE ══════════════ */}
        <Section id="word-size" title="1.1 Word Size" completed={completed["word-size"]} onToggle={() => toggle("word-size")} defaultOpen={true}>
          <p>The number of bits that a computer's CPU can process at once is known as the <strong>word size</strong> of the computer. It is the amount of data that a CPU register can hold and manipulate at any one time.</p>

          <p className="mt-3">A <strong>word</strong> is a unit of data defining the number of bits that move around a computer from one component to another. Words are stored in components and are processed.</p>

          <InfoBox title="Key Point" color="blue">
            Older computers may still use <strong>32 bits</strong> and newer ones use <strong>64 bits</strong>. Buses are built with 32 bits (4 bytes) and 64 bits (8 bytes). Since the buses are the transporters of data, CPUs, components and operating systems are built to accommodate the bus sizes and allow the computer to function as efficiently as possible.
          </InfoBox>

          <p className="mt-2"><strong>Effect on performance:</strong> A larger word size means the CPU can process more data in a single operation, which improves performance. A 64-bit processor can handle larger numbers and more memory addresses than a 32-bit processor.</p>

          <InfoBox title="CPU Architecture" color="green">
            The CPU contains the <strong>Arithmetic Logic Unit (ALU)</strong>, <strong>Registers</strong>, and <strong>Control Logic</strong>. Registers hold data using the word size. A 64-bit register can hold a value up to 2⁶⁴ − 1 ≈ 18.4 quintillion.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to define word size, explain its relationship to registers and buses, and explain why a larger word size improves computer performance.
          </div>
        </Section>

        {/* ══════════════ 1.2 COMBINATIONS ══════════════ */}
        <Section id="combinations" title="1.2 Possible Combinations of a Fixed Number of Bits" completed={completed["combinations"]} onToggle={() => toggle("combinations")}>
          <p>Binary representation is the core of computer operation: a "0" represents "off" and a "1" represents "on". Bits are grouped together usually in multiples of 8 in registers or buses.</p>

          <p className="mt-3">The number of possible combinations that can be represented by any fixed number of bits can be calculated by raising 2 to the power of how many bits are to be used.</p>

          <InfoBox title="Formula" color="green">
            <div className="text-center text-lg font-bold">Number of combinations = 2ⁿ</div>
            <div className="text-center mt-1">where <strong>n</strong> = number of bits</div>
          </InfoBox>

          <p className="mt-2">For example, if 3 bits are used:</p>
          <p className="font-mono ml-4">2 × 2 × 2 = 2³ = 8 combinations</p>

          <p className="mt-3">All 8 combinations with 3 bits: 000, 001, 010, 011, 100, 101, 110, 111</p>

          <Table
            headers={["Number of Bits", "Calculation", "Combinations"]}
            rows={[
              ["3", "2³", "8"],
              ["4", "2⁴", "16"],
              ["7", "2⁷", "128"],
              ["8", "2⁸", "256"],
              ["10", "2¹⁰", "1 024"],
            ]}
          />

          <div className="mt-4 font-semibold text-slate-700">Practice:</div>
          <p className="mt-1">How many unique combinations can be represented using 12 bits?</p>
          <Spoiler>
{`2¹² = 4 096 unique combinations`}
          </Spoiler>
        </Section>

        {/* ══════════════ 1.3 BINARY ADDITION ══════════════ */}
        <Section id="binary-addition" title="1.3 Binary Addition" completed={completed["binary-addition"]} onToggle={() => toggle("binary-addition")}>
          <p>In decimal, when you add 9 and 1, you get 10. The carry of 1 is added to the next position. Binary addition works the same way:</p>

          <Table
            headers={["Operation", "Result", "Carry"]}
            rows={[
              ["0₂ + 0₂", "0₂", "0"],
              ["0₂ + 1₂", "1₂", "0"],
              ["1₂ + 0₂", "1₂", "0"],
              ["1₂ + 1₂", "10₂ (write 0)", "1"],
              ["1₂ + 1₂ + 1₂", "11₂ (write 1)", "1"],
            ]}
          />

          <Worked title="Example 1 — Add 47₁₀ and 31₁₀ in binary">
{`47₁₀ = 101111₂        31₁₀ = 11111₂

  Carry:  111111
           101111₂
        +  011111₂
        ----------
         1001110₂

Verify: 1001110₂ = 64 + 8 + 4 + 2 = 78₁₀
Check:  47 + 31 = 78 ✓`}
          </Worked>

          <Worked title="Example 2 — Add 125₁₀ and 75₁₀ in binary">
{`125₁₀ = 1111101₂      75₁₀ = 1001011₂

  Carry:  1111111
          1111101₂
        + 1001011₂
        ----------
         11001000₂

Verify: 11001000₂ = 128 + 64 + 8 = 200₁₀
Check:  125 + 75 = 200 ✓`}
          </Worked>

          <div className="mt-4 font-semibold text-slate-700">Practice:</div>
          <p className="mt-1">Perform binary addition on 101101₂ and 111001₂. Convert the binary answer back to decimal to verify.</p>
          <Spoiler>
{`  Carry:  111001
          101101₂
        + 111001₂
        ----------
         1100110₂

Verify: 1100110₂ = 64 + 32 + 4 + 2 = 102₁₀
Check:  101101₂ = 32+8+4+1 = 45
        111001₂ = 32+16+8+1 = 57
        45 + 57 = 102 ✓`}
          </Spoiler>

          <p className="mt-3">Add 58₁₀ and 39₁₀ in binary:</p>
          <Spoiler>
{`58₁₀ = 111010₂       39₁₀ = 100111₂

  Carry: 111110
         111010₂
       + 100111₂
       ----------
        1100001₂

Verify: 1100001₂ = 64 + 32 + 1 = 97₁₀
Check:  58 + 39 = 97 ✓`}
          </Spoiler>
        </Section>

        {/* ══════════════ 1.4 FIXED BITS ══════════════ */}
        <Section id="fixed-bits" title="1.4 Representing Integers in a Fixed Number of Bits" completed={completed["fixed-bits"]} onToggle={() => toggle("fixed-bits")}>
          <p>All numbers stored in a computer are represented by a fixed number of bits, usually dictated by the word size. We are taking numbers that are infinite in nature and limiting them to a set number of bits. This has consequences.</p>

          <p className="mt-3">In programming languages, integers (byte, short, long) are all allocated a fixed number of bits. By limiting the bits, the number has a minimum and maximum value.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">1.4.1 Unsigned Integers (Positive Numbers)</h3>
          <p>When we know the number of bits, we write the number with leading 0s to pad it to match the required number of bits.</p>

          <InfoBox color="blue">
            9 = 1001₂ in pure binary, but in 8 bits: 9 = <strong>00001001₂</strong> (four 0s added for padding).
            <br />255 in 8 bits = 11111111₂ and 255 in 10 bits = 0011111111₂
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">1.4.2.1 Sign and Size</h3>
          <p>Sign and size uses the <strong>left-most bit</strong> to indicate the sign: <strong>0 = positive</strong>, <strong>1 = negative</strong>. The remaining bits represent the magnitude.</p>

          <Worked title="Example 3 — Represent −9 in 8-bit Sign and Size">
{`Step 1: 9 in binary    = 1001₂
Step 2: Pad to 8 bits  = 00001001₂
Step 3: Change first bit to 1 (negative)
Result: 10001001₂`}
          </Worked>

          <Worked title="Example 4 — Represent −47 in 8-bit Sign and Size">
{`Step 1: 47 in binary   = 101111₂
Step 2: Pad to 8 bits  = 00101111₂
Step 3: Change first bit to 1 (negative)
Result: 10101111₂`}
          </Worked>

          <InfoBox title="Problem with Sign and Size" color="red">
            Sign and size <strong>cannot be used in arithmetic</strong>. Adding −9 + 9 using sign and size does NOT give 0.
            <br />00001001₂ + 10001001₂ = 10010010₂ (which is −18, NOT 0!)
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">1.4.2.2 Two's Complement</h3>
          <p>Think of an odometer: if it reads 0000 and you reverse 1 km, it shows 9999. Similarly, in 8-bit two's complement, 0 is 00000000₂ and −1 is 11111111₂.</p>

          <InfoBox title="Algorithm — Represent a negative integer using Two's Complement" color="green">
            <div>1. Convert the positive number to binary</div>
            <div>2. Pad with leading zeros to the required number of bits</div>
            <div>3. Complement: swap all 0s and 1s (this is one's complement)</div>
            <div>4. Add 1 to complete two's complement</div>
          </InfoBox>

          <Worked title="Example 5 — Represent −47 in 8-bit Two's Complement">
{`Step 1: 47₁₀ = 101111₂
Step 2: Pad to 8 bits = 00101111₂
Step 3: Complement    = 11010000₂
Step 4: Add 1:
          11010000₂
        +        1₂
        ----------
          11010001₂

−47 in 8-bit two's complement = 11010001₂`}
          </Worked>

          <Worked title="Example 6 — Represent −123 in 8-bit Two's Complement">
{`Step 1: 123₁₀ = 1111011₂
Step 2: Pad to 8 bits = 01111011₂
Step 3: Complement    = 10000100₂
Step 4: Add 1:
          10000100₂
        +        1₂
        ----------
          10000101₂

−123 in 8-bit two's complement = 10000101₂`}
          </Worked>

          <Worked title="Verify: 123 + (−123) = 0 in binary">
{`  Carry: 11111111
         01111011₂    (123)
       + 10000101₂    (−123)
       -----------
       1|00000000₂

The 9th bit (carry) is discarded in 8-bit arithmetic.
Result = 00000000₂ = 0 ✓`}
          </Worked>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Converting Two's Complement Back to Decimal</h3>
          <InfoBox title="Algorithm — Two's Complement to Decimal" color="green">
            <div>1. If the leftmost bit is 0, the number is positive — convert normally.</div>
            <div>2. If the leftmost bit is 1, the number is negative:</div>
            <div className="ml-4">a. Swap all 0s and 1s</div>
            <div className="ml-4">b. Add 1</div>
            <div className="ml-4">c. Convert the result to decimal</div>
            <div className="ml-4">d. Add a negative sign</div>
          </InfoBox>

          <Worked title="Example 7 — Convert 10000101₂ (two's complement) to decimal">
{`Leftmost bit = 1 → negative number
Step 1: Swap:  01111010₂
Step 2: Add 1: 01111010₂ + 1₂ = 01111011₂
Step 3: Convert: 64+32+16+8+2+1 = 123
Step 4: Add sign: −123₁₀`}
          </Worked>

          <Worked title="Example 8 — Convert 11010001₂ (two's complement) to decimal">
{`Leftmost bit = 1 → negative number
Step 1: Swap:  00101110₂
Step 2: Add 1: 00101110₂ + 1₂ = 00101111₂
Step 3: Convert: 32+8+4+2+1 = 47
Step 4: Add sign: −47₁₀`}
          </Worked>

          <div className="mt-4 font-semibold text-slate-700">Practice:</div>
          <p className="mt-1">Represent −87 in 8-bit Sign and Size:</p>
          <Spoiler>
{`87₁₀ = 1010111₂
Pad to 8 bits:  01010111₂
Set sign bit:   11010111₂

−87 in sign and size = 11010111₂`}
          </Spoiler>

          <p className="mt-3">Represent −87 in 8-bit Two's Complement:</p>
          <Spoiler>
{`87₁₀ = 1010111₂
Pad to 8 bits:  01010111₂
Complement:     10101000₂
Add 1:          10101000₂ + 1₂ = 10101001₂

−87 in two's complement = 10101001₂`}
          </Spoiler>

          <p className="mt-3">Prove which method works for arithmetic by adding −87 and 87:</p>
          <Spoiler>
{`Sign and Size:
  01010111₂  (87)
+ 11010111₂  (−87)
-----------
 100101110₂  ← This is NOT 0! Sign and size fails. ✗

Two's Complement:
  Carry: 11111111
         01010111₂  (87)
       + 10101001₂  (−87)
       -----------
       1|00000000₂

Discard carry → 00000000₂ = 0 ✓
Two's complement is suitable for arithmetic.`}
          </Spoiler>

          <p className="mt-3">Convert 11010101₂ (8-bit two's complement) to decimal:</p>
          <Spoiler>
{`Leftmost bit = 1 → negative number
Step 1: Swap:  00101010₂
Step 2: Add 1: 00101010₂ + 1₂ = 00101011₂
Step 3: Convert: 32 + 8 + 2 + 1 = 43
Step 4: Add sign: −43₁₀`}
          </Spoiler>

          <InfoBox title="Remember" color="orange">
            If a number is <strong>positive</strong>, the Sign and Size and Two's Complement representations are <strong>the same</strong> (e.g. +123 = 01111011₂ in both methods).
          </InfoBox>
        </Section>

        {/* ══════════════ 1.4.3 MIN/MAX ══════════════ */}
        <Section id="min-max" title="1.4.3 Formulas for Maximum and Minimum Values" completed={completed["min-max"]} onToggle={() => toggle("min-max")}>
          <h3 className="font-bold text-slate-800 mt-2 mb-2 text-base">Unsigned Integers</h3>
          <p>Since there is no bit reserved for the sign, we can only store positive numbers (and zero).</p>

          <Table
            headers={["", "Formula", "Example (8 bits)"]}
            rows={[
              ["Smallest", "0", "0"],
              ["Largest", "2ⁿ − 1", "2⁸ − 1 = 255"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Signed Integers (Two's Complement)</h3>
          <p>The first (left-most) bit indicates the sign (0 for positive, 1 for negative).</p>

          <Table
            headers={["", "Formula", "Example (8 bits)"]}
            rows={[
              ["Smallest (most negative)", "−2ⁿ⁻¹", "−2⁷ = −128"],
              ["Largest", "2ⁿ⁻¹ − 1", "2⁷ − 1 = 127"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Summary Table</h3>
          <Table
            headers={["n unsigned bits", "Smallest", "Largest"]}
            rows={[
              ["n", "0", "2ⁿ − 1"],
            ]}
          />
          <Table
            headers={["n signed bits", "Smallest", "Largest"]}
            rows={[
              ["n", "−2ⁿ⁻¹", "2ⁿ⁻¹ − 1"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Worked Examples</h3>
          <Table
            headers={["Bits", "Largest Binary", "Largest Decimal", "Smallest Decimal"]}
            rows={[
              ["8 unsigned", "11111111₂", "2⁸ − 1 = 255", "0"],
              ["8 signed", "01111111₂", "2⁷ − 1 = 127", "−2⁷ = −128"],
              ["6 unsigned", "111111₂", "2⁶ − 1 = 63", "0"],
              ["6 signed", "011111₂", "2⁵ − 1 = 31", "−2⁵ = −32"],
              ["4 unsigned", "1111₂", "2⁴ − 1 = 15", "0"],
              ["4 signed", "0111₂", "2³ − 1 = 7", "−2³ = −8"],
            ]}
          />

          <div className="mt-4 font-semibold text-slate-700">Practice:</div>
          <p className="mt-1">Complete the table:</p>
          <Table
            headers={["", "Smallest Number", "Largest Number"]}
            rows={[
              ["6 unsigned bits", "?", "?"],
              ["6 signed bits", "?", "?"],
              ["10 unsigned bits", "?", "?"],
              ["10 signed bits", "?", "?"],
            ]}
          />
          <Spoiler>
{`6 unsigned bits:  Smallest = 0,      Largest = 2⁶ − 1 = 63
6 signed bits:    Smallest = −2⁵ = −32,  Largest = 2⁵ − 1 = 31
10 unsigned bits: Smallest = 0,      Largest = 2¹⁰ − 1 = 1023
10 signed bits:   Smallest = −2⁹ = −512, Largest = 2⁹ − 1 = 511`}
          </Spoiler>
        </Section>

        {/* ══════════════ 1.4.4 OVERFLOW ══════════════ */}
        <Section id="overflow" title="1.4.4 Overflow and Underflow" completed={completed["overflow"]} onToggle={() => toggle("overflow")}>
          <p>What happens if we try to store a value in a data type that is outside the range of the data type?</p>

          <h3 className="font-bold text-slate-800 mt-4 mb-2 text-base">Unsigned Integers — Overflow</h3>
          <p>If we try to store 17 in 4 unsigned bits (max = 15), the number "wraps around". 17 in binary is 10001₂ — a 5-bit number. In 4 bits, only the last 4 bits are kept: 0001₂ = 1.</p>

          <p className="mt-2">Imagine the values 0–15 arranged in a circle. To get to 17, you go 2 past 15 (clockwise), producing 1. This is <strong>overflow</strong>.</p>

          <InfoBox title="Overflow Error" color="red">
            An <strong>overflow error</strong> occurs when a value is too large to be stored in the allocated number of bits. The extra bits are lost, producing an incorrect (wrapped-around) result.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Signed Integers — Overflow</h3>
          <p>For 4 signed bits, the range is −8 to 7. Storing 8 would also cause overflow, wrapping around to −8.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Why Overflow is a Logical Error</h3>
          <InfoBox title="Key Concept" color="orange">
            Overflow is a <strong>logical error</strong> because:
            <br />• The program compiles without errors (it's not a syntax error).
            <br />• The program runs without crashing (it's not a runtime error).
            <br />• But the <strong>values will not be what is expected</strong> — the program produces incorrect results silently.
            <br />• Logical errors are <strong>difficult to detect</strong>, unlike syntax and runtime errors.
            <br /><br />
            <strong>Why it's dangerous:</strong> The program appears to work correctly, but it produces wrong answers. A programmer may not even know the error exists until the consequences become apparent (e.g. financial calculations, rocket guidance, medical dosages).
          </InfoBox>

          <p className="mt-3"><strong>How to prevent overflow:</strong> Use data types that are appropriate for the problem at hand (e.g. use a <code>long</code> instead of a <code>byte</code> when dealing with large numbers).</p>

          <div className="mt-4 font-semibold text-slate-700">Practice:</div>
          <p>What would the result be if 17 was stored in 4 unsigned bits? Explain.</p>
          <Spoiler>
{`17₁₀ = 10001₂ (needs 5 bits)
In 4 unsigned bits, max = 15 (1111₂).
17 causes overflow — the extra bit is lost.
Only 0001₂ is stored = 1₁₀.
The value wraps around: 17 − 16 = 1.`}
          </Spoiler>

          <p className="mt-3">A programmer stores a large number but the computer shows a much smaller number. What error is this? How can it be corrected?</p>
          <Spoiler>
{`This is an overflow error.
It can be corrected by using a larger data type 
(more bits) that can store the larger value.`}
          </Spoiler>
        </Section>

        {/* ══════════════ 1.5 REAL NUMBERS ══════════════ */}
        <Section id="real-numbers" title="1.5 Representing Real Numbers" completed={completed["real-numbers"]} onToggle={() => toggle("real-numbers")}>
          <h3 className="font-bold text-slate-800 mt-2 mb-2 text-base">Scientific Notation (Recap)</h3>
          <p>Scientific notation represents very large or small numbers compactly:</p>
          <p className="font-mono ml-4 my-2">±n.nnn… × 10<sup>±k</sup></p>
          <p>Examples: 5.23654 × 10⁷ = 52365400 and 5.23654 × 10⁻⁷ = 0.000000523654</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Normalised Form</h3>
          <p>Computers use <strong>normalised form</strong>, which is similar to scientific notation. Instead of a single digit before the decimal point, all digits are shifted to the right of the decimal point:</p>

          <InfoBox title="Normalised Form" color="green">
            <div className="text-center text-lg font-bold">0.nnn × 10<sup>n</sup></div>
            <div className="mt-2">The "0." portion can be excluded from storage since it's always there. The number is represented using two parts:</div>
            <div className="mt-1"><strong>Mantissa</strong> — the digits after the decimal point (the significant digits)</div>
            <div><strong>Exponent</strong> — the power to which the base is raised</div>
            <div className="mt-1">Both the mantissa and exponent are signed (can be positive or negative).</div>
          </InfoBox>

          <Worked title="Example — Normalise 52365400">
{`52365400 = 0.523654 × 10⁸

Mantissa = 523654
Exponent = 8`}
          </Worked>

          <Worked title="Example — Normalise 0.0000523654">
{`0.0000523654 = 0.523654 × 10⁻⁴

Mantissa = 523654
Exponent = −4`}
          </Worked>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">1.5.1 Mantissa and Exponent Storage</h3>
          <p>A real number can be stored with a fixed number of digits for the exponent and mantissa. For example, with 8 bits for the exponent and 32 bits for the mantissa (with sign bits for each).</p>

          <p className="mt-3">The number of bits allocated to the exponent determines the <strong>range</strong> (how large or small the numbers can be). The number of bits allocated to the mantissa determines the <strong>precision</strong> (how many significant digits).</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Simplified Model: 2-digit Exponent + 4-digit Mantissa</h3>
          <p>For our purposes, we use <strong>digits</strong> instead of bits. With 2 digits for the exponent (1 for sign, 1 for value) and 4 digits for the mantissa (1 for sign, 3 for digits):</p>

          <div className="my-3 flex items-center gap-2 font-mono text-sm">
            <div className="flex">
              <div className="border border-slate-400 px-2 py-1 bg-sky-100 text-center">S</div>
              <div className="border border-slate-400 px-2 py-1 bg-sky-50 text-center">9</div>
            </div>
            <span className="text-xs text-slate-500">Exp</span>
            <div className="flex">
              <div className="border border-slate-400 px-2 py-1 bg-amber-100 text-center">S</div>
              <div className="border border-slate-400 px-2 py-1 bg-amber-50 text-center">n</div>
              <div className="border border-slate-400 px-2 py-1 bg-amber-50 text-center">n</div>
              <div className="border border-slate-400 px-2 py-1 bg-amber-50 text-center">n</div>
            </div>
            <span className="text-xs text-slate-500">Mantissa</span>
          </div>

          <p className="text-sm">The exponent digit (excluding sign) can be 0–9. The mantissa has 3 significant digits. Any mantissa with more than 3 digits will lose accuracy.</p>

          <Worked title="Example 10 — Store 203">
{`Normalised form: 0.203 × 10³

Exponent: +3 (sign = +, digit = 3) → fits (0–9) ✓
Mantissa: +203 (sign = +, digits = 203) → 3 digits, fits ✓

  Exp       Mantissa
| + | 3 | | + | 2 | 0 | 3 |

No problems — no overflow, no loss of accuracy.`}
          </Worked>

          <Worked title="Example 11 — Store 0.00000000001">
{`Normalised form: 0.1 × 10⁻¹⁰

Exponent: −10 (sign = −, digit = 10)
But we only have 1 digit for the exponent value!
10 is outside the range 0–9.

→ OVERFLOW in the exponent!

The mantissa (1) fits fine — only 1 digit needed.`}
          </Worked>

          <Worked title="Store −0.00056718 (from the test)">
{`Normalised form: −0.56718 × 10⁻³

Exponent: −3 → sign = −, digit = 3 → fits ✓
Mantissa: −56718 → sign = −, digits = 56718
But we only have 3 digit slots!
56718 has 5 digits → loss of accuracy (truncation/rounding)

Stored mantissa: 567 (truncated) or 567 (rounded)
Lost digits: 18

  Exp       Mantissa
| − | 3 | | − | 5 | 6 | 7 |

Problem: Loss of accuracy in the mantissa 
(truncation error — digits 1 and 8 are lost).`}
          </Worked>

          <Worked title="Store 8471625389 (from the test)">
{`Normalised form: 0.8471625389 × 10¹⁰

Exponent: +10 → sign = +, digit = 10
But 10 is outside the range 0–9!
→ OVERFLOW in the exponent!

Mantissa: +8471625389 → 10 digits
But we only have 3 digit slots!
→ Loss of accuracy (truncation)

  Exp       Mantissa
| + | ? | | + | 8 | 4 | 7 |

Problems: BOTH overflow in the exponent AND 
loss of accuracy in the mantissa.`}
          </Worked>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">1.5.2 Truncation vs Rounding</h3>
          <InfoBox title="Truncation vs Rounding" color="blue">
            When the mantissa does not fit the allocated space, digits must be removed.
            <br /><br />
            <strong>Truncation:</strong> Simply cut off the extra digits. Example: 56718 → 567 (digits 1 and 8 are discarded).
            <br /><br />
            <strong>Rounding:</strong> Round to the nearest value based on the next digit. Example: 56718 → 567 (since the next digit, 1, is less than 5, we round down — still 567 in this case). But 56758 → 568 (next digit is 5, so round up).
            <br /><br />
            Both methods result in a <strong>loss of precision</strong> (also called a <strong>truncation error</strong> or <strong>rounding error</strong>).
          </InfoBox>

          <div className="mt-4 font-semibold text-slate-700">Practice:</div>
          <p>Explain the difference between truncating and rounding when the mantissa does not fit:</p>
          <Spoiler>
{`Truncating simply removes the extra digits from the end.
Rounding looks at the next digit to decide whether 
to round up or keep the last stored digit the same, 
which can give a more accurate result.`}
          </Spoiler>
        </Section>

        {/* ══════════════ EXERCISES ══════════════ */}
        <Section id="exercises" title="Practice Exercises (Mixed)" completed={completed["exercises"]} onToggle={() => toggle("exercises")}>
          <InfoBox title="Instructions" color="blue">
            Work through each exercise on paper first, then check your answer. These exercises cover all topics and are similar to test questions.
          </InfoBox>

          <div className="space-y-5 mt-4">
            <div>
              <p className="font-semibold">Exercise 1 — Definitions</p>
              <p className="text-sm mt-1">Give the most appropriate term for each:</p>
              <p className="text-sm ml-4 mt-1">a) The amount of data that a CPU register can hold and manipulate at any one time.</p>
              <Spoiler label="Answer">Word size</Spoiler>
              <p className="text-sm ml-4">b) The method of representing negative numbers where binary digits are inverted and then 1 is added.</p>
              <Spoiler label="Answer">Two's complement</Spoiler>
              <p className="text-sm ml-4">c) The digits after the decimal point in the normalised form of a floating-point number.</p>
              <Spoiler label="Answer">Mantissa</Spoiler>
              <p className="text-sm ml-4">d) The error that occurs when a value is too large to be stored in the allocated number of bits.</p>
              <Spoiler label="Answer">Overflow (error)</Spoiler>
              <p className="text-sm ml-4">e) The part of a floating-point number that indicates the power to which the base is raised.</p>
              <Spoiler label="Answer">Exponent</Spoiler>
              <p className="text-sm ml-4">f) A unit of data consisting of 4 bits, also called a half-byte.</p>
              <Spoiler label="Answer">Nibble (or nybble)</Spoiler>
              <p className="text-sm ml-4">g) The loss of precision that occurs when digits are removed from a number to fit the available storage space.</p>
              <Spoiler label="Answer">Truncation error / Rounding error</Spoiler>
              <p className="text-sm ml-4">h) The representation method where the left-most bit indicates the sign (0 = positive, 1 = negative).</p>
              <Spoiler label="Answer">Sign and size (sign and magnitude)</Spoiler>
              <p className="text-sm ml-4">i) A number system using base-2 that consists only of 0s and 1s.</p>
              <Spoiler label="Answer">Binary (number system)</Spoiler>
              <p className="text-sm ml-4">j) The process of writing a real number in the form 0.nnn × 10ⁿ for computer storage.</p>
              <Spoiler label="Answer">Normalisation / Normalised form</Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 2 — Binary Addition</p>
              <p className="text-sm mt-1">Add 84₁₀ and 44₁₀ in binary. Convert back to decimal to verify.</p>
              <Spoiler>
{`84₁₀ = 1010100₂      44₁₀ = 101100₂

  Carry: 1111
         1010100₂
       + 0101100₂
       ----------
        10000000₂

Verify: 10000000₂ = 128₁₀
Check:  84 + 44 = 128 ✓`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 3 — Two's Complement Conversions</p>
              <p className="text-sm mt-1">a) Represent −55 in 8-bit two's complement.</p>
              <Spoiler>
{`55₁₀ = 110111₂
Pad:       00110111₂
Complement: 11001000₂
Add 1:      11001001₂

−55 in two's complement = 11001001₂`}
              </Spoiler>
              <p className="text-sm mt-2">b) Convert 10110010₂ (two's complement) to decimal.</p>
              <Spoiler>
{`Leftmost bit = 1 → negative
Swap:  01001101₂
Add 1: 01001110₂
Convert: 64 + 8 + 4 + 2 = 78
Answer: −78₁₀`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 4 — Min/Max Values</p>
              <p className="text-sm mt-1">What is the range of values for:</p>
              <p className="text-sm ml-4">a) 8 unsigned bits?</p>
              <Spoiler label="Answer">{`Smallest = 0, Largest = 2⁸ − 1 = 255`}</Spoiler>
              <p className="text-sm ml-4">b) 8 signed bits?</p>
              <Spoiler label="Answer">{`Smallest = −2⁷ = −128, Largest = 2⁷ − 1 = 127`}</Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 5 — Overflow</p>
              <p className="text-sm mt-1">Explain why overflow is a logical error and why it's dangerous in programs.</p>
              <Spoiler>
{`Overflow is a logical error because:
• The code compiles (no syntax error)
• The program runs without crashing (no runtime error)
• BUT the output values are incorrect — the result 
  wraps around silently

It is dangerous because the programmer may not realise 
the values are wrong. The program appears to work but 
produces incorrect results, which could lead to serious 
consequences in financial, medical, or engineering 
applications.`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 6 — Advantages of Two's Complement</p>
              <p className="text-sm mt-1">Explain TWO advantages of using two's complement over sign and size.</p>
              <Spoiler>
{`1. Two's complement works correctly with arithmetic 
   operations (addition/subtraction). Sign and size 
   does not — adding a positive and negative number 
   gives the wrong result.

2. Two's complement has only ONE representation of 
   zero (00000000₂), while sign and size has TWO 
   (+0 = 00000000₂ and −0 = 10000000₂), which 
   wastes a combination and causes confusion.

(Also acceptable: Two's complement can represent 
one extra negative number, e.g. −128 in 8 bits 
vs only −127 in sign and size.)`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 7 — Real Number Storage</p>
              <p className="text-sm mt-1">Using 2 digits for the exponent (1 sign + 1 digit) and 4 digits for the mantissa (1 sign + 3 digits), store the number 504:</p>
              <Spoiler>
{`Normalised form: 0.504 × 10³

Exponent: +3 → sign = +, digit = 3 → fits ✓
Mantissa: +504 → sign = +, digits = 504 → fits ✓

  Exp       Mantissa
| + | 3 | | + | 5 | 0 | 4 |

No problems.`}
              </Spoiler>
              <p className="text-sm mt-2">Store −94532:</p>
              <Spoiler>
{`Normalised form: −0.94532 × 10⁵

Exponent: +5 → sign = +, digit = 5 → fits ✓
Mantissa: −94532 → sign = −, digits = 94532
Only 3 slots → stored as 945 (truncated)

  Exp       Mantissa
| + | 5 | | − | 9 | 4 | 5 |

Problem: Loss of accuracy in the mantissa 
(digits 3 and 2 are lost).`}
              </Spoiler>
              <p className="text-sm mt-2">Store 0.00004562867:</p>
              <Spoiler>
{`Normalised form: 0.4562867 × 10⁻⁴

Exponent: −4 → sign = −, digit = 4 → fits ✓
Mantissa: +4562867 → sign = +, digits = 4562867
Only 3 slots → stored as 456 (truncated)

  Exp       Mantissa
| − | 4 | | + | 4 | 5 | 6 |

Problem: Loss of accuracy in the mantissa.`}
              </Spoiler>
              <p className="text-sm mt-2">Store 109583728594:</p>
              <Spoiler>
{`Normalised form: 0.109583728594 × 10¹²

Exponent: +12 → sign = +, digit = 12
But 12 is outside the range 0–9!
→ OVERFLOW in the exponent!

Mantissa: +109583728594 → 12 digits
Only 3 slots → stored as 109 (truncated)
→ Loss of accuracy in the mantissa!

Problems: BOTH overflow in the exponent AND 
loss of accuracy in the mantissa.`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 8 — Word Size</p>
              <p className="text-sm mt-1">Explain what is meant by the word size of a computer and describe its effect on performance.</p>
              <Spoiler>
{`Word size is the number of bits that a CPU register 
can hold and process at one time. It defines the 
amount of data that moves between components.

Effect on performance:
• A larger word size allows the CPU to process more 
  data per instruction cycle, increasing speed.
• A 64-bit processor can address more memory than 
  a 32-bit processor (2⁶⁴ vs 2³² addresses).
• Larger word sizes allow for bigger numbers to be 
  represented and calculated in a single operation.`}
              </Spoiler>
            </div>
          </div>
        </Section>

        {/* ══════════════════════════════════════════════════════════════════════
             LEARNING UNIT 2 — HARDWARE
             ══════════════════════════════════════════════════════════════════════ */}

        <div className="mt-12 mb-8 pt-6 border-t-2 border-amber-400">
          <h2 className="text-2xl font-bold text-amber-800 text-center" style={{ fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" }}>Learning Unit 2 — Hardware</h2>
          <p className="text-center text-sm text-slate-500 mt-1">CPU, Memory, Motherboard, Storage &amp; Caching</p>
        </div>

        {/* ══════════════ 2.1 CPU DESIGN ══════════════ */}
        <Section id="cpu-design" title="2.1 CPU Design" completed={completed["cpu-design"]} onToggle={() => toggle("cpu-design")}>
          <p>The CPU has two main sections: the <strong>Control Unit (CU)</strong> and the <strong>Arithmetic Logic Unit (ALU)</strong>, which both contain small storage locations called <strong>registers</strong>. The CPU is connected to main memory via the <strong>address bus</strong>, <strong>data bus</strong>, and <strong>control bus</strong>.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.1.1 Control Unit (CU)</h3>
          <p>The CU controls the execution of each instruction. It sends read or write signals to memory and the address of the instruction (or data) to be fetched or stored. The instruction/data fetched from memory is stored in the registers in the CU or in the ALU.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.1.2 Arithmetic Logic Unit (ALU)</h3>
          <p>Calculations and logical comparisons take place in the ALU. Once processed, data results may be sent back to memory. The current instruction is broken into parts and stored in the registers. The <strong>accumulator</strong> is the register that stores the result of each execution.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.1.3 Registers</h3>
          <p>Registers store the current instruction and its data. They are made from <strong>static RAM (SRAM)</strong>, which is extremely fast but very expensive. SRAM does not need constant refreshing like DRAM.</p>
          <p className="mt-2">Registers can be 32 bits or 64 bits. A 64-bit CPU processes data in groups of 64 bits in a single stage, improving performance over 32-bit.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.1.4 Serial Processing</h3>
          <p>Historically, CPUs could only process one instruction at a time. The processor had a single core where the problem was broken into a set of instructions executed sequentially, one after the other, on a single processor.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.1.5 Parallel Processing</h3>
          <p>Parallel processing simultaneously breaks up and runs program instructions on multiple microprocessors, reducing processing time. Each part is broken into individual instructions run on different processors.</p>

          <InfoBox title="Hyperthreading" color="blue">
            Hyperthreading was developed before multi-core CPUs existed. It duplicates the <strong>registers</strong> on a single processor chip, allowing fast <strong>context switching</strong> between two processes. The OS sees two <strong>logical CPUs</strong> where there is only one physical CPU. This allows two programs to be processed as separate threads.
          </InfoBox>

          <InfoBox title="Multiprocessing" color="green">
            Multiprocessing means having <strong>multiple CPUs (cores)</strong> on a single chip. Instead of just duplicating registers, the entire core is duplicated. Multiple processes can genuinely run at the same time. Modern CPUs combine both: multiple cores, each with hyperthreading (e.g. 8 cores × 2 threads = 16 logical processors).
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.1.6 Processor Cache</h3>
          <p>The processor is much faster than RAM. To avoid waiting for data from slower RAM, a small amount of fast memory called <strong>cache</strong> is placed on or near the CPU. Cache stores blocks of instructions and data pre-fetched from RAM in the hope they will be needed next.</p>

          <p className="mt-2">Cache is made from <strong>SRAM</strong> (static RAM), which has faster access than <strong>DRAM</strong> (dynamic RAM used for main memory).</p>

          <KeyTerm term="Cache hit">When the requested data is found in the cache — served quickly.</KeyTerm>
          <KeyTerm term="Cache miss">When the data is NOT in the cache — must be fetched from slower RAM.</KeyTerm>

          <InfoBox title="Cache Levels" color="orange">
            <strong>Level 1 (L1):</strong> Smallest, fastest. Built into the CPU chip at the same speed as the CPU's internal speed. Each core has its own L1 cache.
            <br /><br />
            <strong>Level 2 (L2):</strong> Slightly larger and slower. Located on the CPU chip but slightly further from the core circuits. Runs at about half the CPU's internal speed.
            <br /><br />
            <strong>Level 3 (L3):</strong> Largest and slowest cache. Often shared between multiple cores. Located on the motherboard or on the CPU die. Has slower access than L1 and L2.
          </InfoBox>

          <Table
            headers={["Cache Level", "Size", "Speed", "Location"]}
            rows={[
              ["L1", "Smallest (typically 32–64KB per core)", "Fastest (CPU speed)", "Built into CPU core"],
              ["L2", "Medium (typically 256KB–1MB per core)", "Fast (½ CPU speed)", "On CPU chip, near core"],
              ["L3", "Largest (typically 4–32MB shared)", "Slower than L1/L2", "Shared on CPU die or motherboard"],
            ]}
          />

          <InfoBox title="RAM vs Processor — Comparison" color="blue">
            <strong>RAM:</strong> Measured in size, type, and access speed (e.g. 16 GB DDR3 @ 1866MHz). Acts as storage for information. Handles opened programs (good for multi-tasking). Less expensive.
            <br /><br />
            <strong>Processor:</strong> Measured in clock speed, cores, and cache (e.g. quad-core @ 4.0 GHz, 8M cache). Acts as the brain of the system. Opens and executes programs. More expensive.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to explain the roles of CU and ALU, differentiate serial vs parallel processing, explain hyperthreading vs multiprocessing, and describe the three cache levels.
          </div>
        </Section>

        {/* ══════════════ 2.2 RAM & MEMORY ══════════════ */}
        <Section id="ram-memory" title="2.2 RAM & Memory Hierarchy" completed={completed["ram-memory"]} onToggle={() => toggle("ram-memory")}>
          <p>RAM (Random Access Memory) is the main memory in chips on modules attached to dedicated slots on the motherboard. RAM stores the <strong>programs currently in use</strong> and the <strong>data associated with them</strong>.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">Types of RAM</h3>
          <KeyTerm term="DRAM">Dynamic RAM — each chip contains a capacitor which loses its charge and must be refreshed many times per second.</KeyTerm>
          <KeyTerm term="SDRAM">Synchronous DRAM — refreshing is synchronised with the CPU clock so the CPU does not have to wait while refreshing takes place.</KeyTerm>
          <KeyTerm term="DDR">Double Data Rate — data is transferred twice every clock pulse (on the rise and fall). Evolutions: DDR, DDR2, DDR3, DDR4, DDR5.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.2.1 Latency</h3>
          <p><strong>Latency</strong> is the time taken for a component to respond. All RAM has latency — a certain number of clock ticks must pass between reads. L1 cache has less latency than L2; reading directly from RAM has many times greater latency than cache.</p>

          <InfoBox title="Key Concept" color="orange">
            Latency leads to wasted clock ticks. The fewer there are, the faster the processor will appear to be. This is why cache (especially L1) is so important — it dramatically reduces the wait time.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.2.2 Memory Hierarchy</h3>
          <p>Computer memory is organised in a pyramid/hierarchy from fastest and most expensive (top) to slowest and cheapest (bottom):</p>

          <Table
            headers={["Level", "Type", "Speed", "Size", "Cost per Byte"]}
            rows={[
              ["L0", "Registers (SRAM)", "Fastest", "Smallest", "Most expensive"],
              ["L1", "On-chip L1 cache (SRAM)", "Very fast", "Very small", "Very expensive"],
              ["L2", "Off-chip L2 cache (SRAM)", "Fast", "Small", "Expensive"],
              ["L3", "Main memory (DRAM)", "Moderate", "Large", "Moderate"],
              ["L4", "Local secondary storage (HDD/SSD)", "Slow", "Very large", "Cheap"],
              ["L5", "Remote storage (Cloud)", "Slowest", "Largest", "Cheapest"],
            ]}
          />

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Know the memory hierarchy from registers to cloud. Be able to list memory in order of cost or speed. Understand what DDR SDRAM means and why latency matters.
          </div>
        </Section>

        {/* ══════════════ 2.3 MOTHERBOARD & BUSES ══════════════ */}
        <Section id="motherboard" title="2.3 Motherboard, Clock & Buses" completed={completed["motherboard"]} onToggle={() => toggle("motherboard")}>
          <p>A <strong>motherboard</strong> is the central printed circuit board that connects components and devices. It hosts the CPU and RAM. <strong>Busses</strong> connect components, <strong>slots</strong> connect devices like graphics cards and hard drives, and <strong>ports</strong> connect external devices like USB and audio.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.3.1 System Clock</h3>
          <p>The clock is a microchip containing a crystal that vibrates at a specific frequency when electricity is applied. The shortest time any computer can perform is one clock cycle (one vibration). Clock speed is measured in <strong>Hz</strong>: 1 MHz = 1 million cycles/second, 1 GHz = 1 billion cycles/second.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.3.1.1 Overclocking</h3>
          <p>Overclocking is making computer components run at speeds faster than designed by manipulating clock frequencies. It can be done in two ways:</p>
          <InfoBox title="Two Methods of Overclocking" color="green">
            <strong>Per component:</strong> The CPU operates faster than the system clock by changing its own clock multiplication factor. This is also known as <strong>clock multiplication</strong>.
            <br /><br />
            <strong>Whole system:</strong> The system clock itself is increased, affecting all components that detect the system clock and multiply by a factor.
          </InfoBox>

          <InfoBox title="Risks of Overclocking" color="red">
            • Components may run faster than designed, causing instability or failure
            <br />• Increased power consumption generates more heat
            <br />• Heat must be dispersed or the chip will fail
            <br />• Cooling methods include fans, liquid cooling, and heat sinks
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.3.2 Speed vs Throughput</h3>
          <KeyTerm term="Speed / Bandwidth">The theoretical/maximum speed of data transfer. Measured in Mbps or Gbps. This is the maximum the bus can handle.</KeyTerm>
          <KeyTerm term="Throughput">The actual speed of data transfer in practice. Due to latency and other factors, throughput is usually lower than bandwidth.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.3.3 Internal Bus / Front Side Bus (FSB)</h3>
          <p>The FSB (also called the <strong>internal bus</strong> or <strong>system bus</strong>) was initially a parallel bus connecting all components on the motherboard. Front-side buses usually connect the CPU and RAM. The FSB consists of three buses:</p>

          <InfoBox title="The Three Buses" color="blue">
            <strong>Data Bus:</strong> Transfers the actual instruction or data between the CPU and RAM. The width (number of bits) dictates the register size and determines throughput. A wider data bus is like a highway with more lanes.
            <br /><br />
            <strong>Address Bus:</strong> Transfers the physical memory address between CPU and RAM. The width determines how much memory can be addressed. A 32-bit address bus can address 2³² = 4 GB; a 64-bit bus can address 2⁶⁴ locations.
            <br /><br />
            <strong>Control Bus:</strong> Carries control commands between CPU and RAM — for example, whether the data is being read or written.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.3.4 External Buses</h3>
          <p>External buses connect peripheral and storage devices to the motherboard.</p>

          <h3 className="font-bold text-slate-800 mt-4 mb-2 text-sm">PCI Express</h3>
          <p>PCI Express (<strong>Peripheral Component Interconnect Express</strong>) slots connect graphics cards, RAID cards, Wi-Fi cards, and SSDs. Generations have evolved from Gen 1 (8GB/s at 2.5GHz) to Gen 5 (128GB/s at 32GHz), with speed and throughput nearly doubling each generation.</p>

          <h3 className="font-bold text-slate-800 mt-4 mb-2 text-sm">SATA</h3>
          <p><strong>Serial Advanced Technology Attachment</strong> connects mass storage devices (hard drives, SSDs) to the motherboard. SATA interfaces are backwards compatible.</p>
          <Table
            headers={["Generation", "Speed", "Throughput"]}
            rows={[
              ["SATA I", "1.5GHz", "150MB/s"],
              ["SATA II", "3GHz", "300MB/s"],
              ["SATA III", "3GHz", "600MB/s"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-4 mb-2 text-sm">USB</h3>
          <p><strong>Universal Serial Bus</strong> standardises the connection of peripherals to computers, providing both communication and electrical power.</p>
          <Table
            headers={["Generation", "Data Transfer Rate"]}
            rows={[
              ["USB 1.0/Low-Speed", "1.5Mbps"],
              ["USB 1.1/Full-Speed", "12Mbps"],
              ["USB 2.0/Full-Speed", "480Mbps"],
              ["USB 3.0/SuperSpeed", "5Gbps"],
              ["USB 3.1/SuperSpeed", "10Gbps"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-4 mb-2 text-sm">NVMe</h3>
          <p><strong>Non-Volatile Memory Express</strong> is a bus interface that connects SSDs to the motherboard. The bus allows SSDs to read/write at a much higher speed than via a SATA connection, using PCI Express lanes directly.</p>

          <h3 className="font-bold text-slate-800 mt-4 mb-2 text-sm">M.2 Format</h3>
          <p>With the increase in speed of SSDs, SATA was becoming too slow and bulky. M.2 is a compact form factor designed for high-speed devices that require very little power. M.2 cards include Wi-Fi, Bluetooth radios, and SSDs. The M.2 slot can use: <strong>SATA 3.0, PCI Express 3.0, USB 3.0, and NVMe</strong>.</p>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Know the three FSB buses and their functions, the difference between bandwidth and throughput, the two methods of overclocking and risks, and the different external bus types (SATA, USB, NVMe, M.2).
          </div>
        </Section>

        {/* ══════════════ 2.4 SECONDARY STORAGE ══════════════ */}
        <Section id="secondary-storage" title="2.4 Secondary Storage" completed={completed["secondary-storage"]} onToggle={() => toggle("secondary-storage")}>
          <p>Secondary storage devices are either housed inside the computer case (hard drives, SSDs) or external (flash drives, external HDDs). We compare them in terms of <strong>size</strong>, <strong>speed</strong>, and <strong>cost</strong>.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.4.1 Size/Speed/Cost Comparison</h3>
          <p>Cost per GB is shown as a relative value (1 = cheapest, 6 = most expensive):</p>
          <Table
            headers={["Type", "Size", "Speed", "Cost"]}
            rows={[
              ["Flash memory", "Up to 1TB", "Up to 10Gbps", "6 (most expensive)"],
              ["HDD - PC", "1 to 8TB", "6Gbps", "3"],
              ["HDD - Laptop", "0.5 to 4TB", "3 to 6Gbps", "4"],
              ["SSD", "120 to 960GB", "500 to 1000MBps", "5"],
              ["External HDD 3.5\"", "4 to 8TB", "6Gbps", "1 (cheapest)"],
              ["External HDD 2.5\"", "1 to 5TB", "3 to 6Gbps", "2"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.4.2 Cloud Storage</h3>
          <p>Cloud storage is not a physical device attached to your computer. Instead, it is remote storage connected via the Internet.</p>

          <InfoBox title="Cloud Storage — Size, Speed, Cost" color="blue">
            <strong>Size:</strong> Popular services like Google Drive and Dropbox provide limited free space (15GB and 2GB respectively). Paid plans allow several Terabytes.
            <br /><br />
            <strong>Speed:</strong> Determined by your Internet connection speed, not the device itself. Generally slower than local storage.
            <br /><br />
            <strong>Cost:</strong> Twofold — the storage subscription charge plus bandwidth costs (if applicable). Approximate: 100GB under $100/year, 1TB $100–$200/year, 2TB $100–$200/year.
          </InfoBox>

          <p className="mt-3">Popular cloud providers include: <strong>Google Drive, OneDrive, Dropbox, pCloud, MEGA, Box</strong>.</p>
          <p className="mt-2">Other features besides storage space: file syncing across devices, sharing and collaboration, automatic backups, security features, and version history.</p>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to compare local vs cloud storage in terms of size, speed, and cost. Know the storage comparison table and be able to analyse which device suits a given scenario.
          </div>
        </Section>

        {/* ══════════════ 2.5 CACHING ══════════════ */}
        <Section id="caching" title="2.5 Other Types of Caching" completed={completed["caching"]} onToggle={() => toggle("caching")}>
          <p>The technique of caching is not limited to the CPU. It has been extended to any device or component that can use something faster to temporarily store data and speed up access time.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.5.1 Disk Cache</h3>
          <p>Disk cache is a hardware mechanism for improving the time it takes to read from or write to a hard disk. It is usually reserved space in main memory (RAM). Instead of reading data line by line from the slower disk drive, the line and the next few lines are read into the cache, so subsequent reads come from the faster cache.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.5.2 Browser Cache</h3>
          <p>Every time a website is accessed, a copy is kept in the <strong>browser cache</strong> on your local hard disk. This cache also stores all files downloaded from the web including images, music, and videos, as well as cookies.</p>
          <p className="mt-2">If web pages are accessed from the browser cache, they load faster than from a remote web server. However, the cached pages may not be the latest version. You can clear the browser cache in your browser settings.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">2.5.3 Web Cache / Proxy Cache</h3>
          <p>A <strong>web cache</strong> or <strong>proxy server cache</strong> stores recently accessed web pages on a server in a network. Unlike browser caches (which are private to each user), a web cache is a shared store on an organisation's server.</p>

          <InfoBox title="How a Proxy Cache Works" color="green">
            1. The first user requests a web page → the request goes to the internet → the page is stored in the proxy server's cache AND in the user's browser cache.
            <br /><br />
            2. When someone else in the organisation requests the same page → the proxy server delivers it from its local cache WITHOUT accessing the internet.
            <br /><br />
            This is much quicker and reduces overall network traffic. However, the cached version may not be the latest.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be able to describe disk cache, browser cache, and web/proxy cache. Understand the difference between browser cache (private, per user) and proxy cache (shared, organisation-wide). Know how proxy caching speeds up subsequent requests.
          </div>
        </Section>

        {/* ══════════════ LU2 EXERCISES ══════════════ */}
        <Section id="exercises-lu2" title="LU2 Practice Exercises (Hardware)" completed={completed["exercises-lu2"]} onToggle={() => toggle("exercises-lu2")}>
          <InfoBox title="Instructions" color="blue">
            Work through each exercise on paper first, then check your answer. These exercises cover all Learning Unit 2 topics.
          </InfoBox>

          <div className="space-y-5 mt-4">
            <div>
              <p className="font-semibold">Exercise 1 — CPU Components</p>
              <p className="text-sm mt-1">Explain the role of the Control Unit (CU) and the Arithmetic Logic Unit (ALU) during the execution of an instruction.</p>
              <Spoiler>
{`Control Unit (CU):
The CU controls the execution of each instruction. 
It sends read or write signals to memory and 
provides the address of the instruction or data 
to be fetched or stored.

Arithmetic Logic Unit (ALU):
Calculations and logical comparisons take place 
in the ALU. The current instruction is broken into 
parts and stored in registers. The accumulator 
stores the result of each execution. Processed 
data may be sent back to memory.`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 2 — Serial vs Parallel Processing</p>
              <p className="text-sm mt-1">Explain how serial and parallel processing work, referring to how instructions are processed and the hardware layout of each.</p>
              <Spoiler>
{`Serial Processing:
• Single core processor
• Instructions executed one after the other (sequentially)
• Problem broken into a set of instructions all 
  processed on one processor

Parallel Processing:
• Multiple processors/cores
• Problem broken into separate parts
• Each part has its own set of instructions
• Instructions run simultaneously on different processors
• Reduces overall processing time`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 3 — Hyperthreading vs Multiprocessing</p>
              <p className="text-sm mt-1">A computer has a single 8-core processor with hyperthreading. How many logical processors does the OS see? Explain the difference between hyperthreading and multiprocessing.</p>
              <Spoiler>
{`The OS sees 16 logical processors (8 cores × 2 threads).

Hyperthreading: Duplicates the REGISTERS on each core, 
allowing fast context switching between two processes. 
The OS sees two logical CPUs per core, but there is 
only one physical execution unit per core.

Multiprocessing: The ENTIRE CORE is duplicated on the 
chip. Multiple processes genuinely run at the same 
time on separate physical execution units.`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 4 — Cache Memory</p>
              <p className="text-sm mt-1">a) Explain the purpose of CPU cache and why it improves processing speed.</p>
              <Spoiler>
{`CPU cache stores frequently used instructions and data 
pre-fetched from RAM, in fast SRAM memory close to the 
CPU. Since the CPU is much faster than RAM, the cache 
reduces the time the CPU spends waiting for data (reduces 
latency), improving overall processing speed.`}
              </Spoiler>
              <p className="text-sm mt-2">b) Differentiate between L1, L2, and L3 cache in terms of size, speed, and location.</p>
              <Spoiler>
{`L1: Smallest, fastest. Built into the CPU core at the 
    same speed as the CPU. Each core has its own L1.

L2: Slightly larger and slower. Located on the CPU 
    chip but further from the core. Runs at about 
    half the CPU's internal speed.

L3: Largest and slowest cache. Shared between multiple 
    cores. Located on the motherboard or CPU die.`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 5 — Buses</p>
              <p className="text-sm mt-1">a) Name the three buses of the Front Side Bus and describe the function of each.</p>
              <Spoiler>
{`Data Bus: Transfers the actual instruction or data 
between CPU and RAM. Width determines throughput.

Address Bus: Transfers the physical memory address 
between CPU and RAM. Width determines how much 
memory can be addressed.

Control Bus: Carries control commands between CPU 
and RAM (e.g. read or write signals).`}
              </Spoiler>
              <p className="text-sm mt-2">b) Explain the difference between bandwidth and throughput.</p>
              <Spoiler>
{`Bandwidth is the theoretical/maximum speed of data 
transfer (the speed the bus is rated for).

Throughput is the actual speed of data transfer 
achieved in practice. Due to latency and other 
factors, throughput is usually lower than bandwidth.`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 6 — Overclocking</p>
              <p className="text-sm mt-1">a) Explain what overclocking is and describe the two ways it can be done.</p>
              <Spoiler>
{`Overclocking is making components run at speeds faster 
than designed by manipulating clock frequencies.

Method 1 — Per component: The CPU operates faster 
than the system clock by changing its own clock 
multiplication factor.

Method 2 — Whole system: The system clock itself 
is increased, affecting all components that detect it.`}
              </Spoiler>
              <p className="text-sm mt-2">b) State two risks of overclocking.</p>
              <Spoiler>
{`1. Components may become unstable, crash, or fail 
   (running beyond designed speed).
2. Increased power consumption and heat generation, 
   potentially damaging the chip without proper cooling.
(Also: voided warranty, reduced component lifespan)`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 7 — SATA vs NVMe</p>
              <p className="text-sm mt-1">Explain one key difference between SATA and NVMe in terms of speed or how they connect to the motherboard.</p>
              <Spoiler>
{`NVMe connects SSDs directly to the motherboard via 
PCI Express bus, bypassing the SATA controller. This 
allows much higher speeds — NVMe can achieve speeds 
of several GB/s, while SATA is limited to 600MB/s 
(SATA III maximum).`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 8 — Storage Comparison</p>
              <p className="text-sm mt-1">Your friend claims that increasing RAM will always improve a computer's speed. Give one reason why cache memory might have a bigger impact in certain situations.</p>
              <Spoiler>
{`Cache memory is much faster than RAM (made from SRAM 
vs DRAM) and is located closer to the CPU. If the 
CPU frequently accesses the same data/instructions, 
having more cache reduces latency significantly more 
than adding RAM. Adding RAM helps with multitasking, 
but cache helps with raw processing speed.`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 9 — Web/Proxy Cache</p>
              <p className="text-sm mt-1">A company notices that employees accessing the same website for the first time experience slow loading, but subsequent visits by other employees are much faster. Explain how a web/proxy cache causes this behaviour.</p>
              <Spoiler>
{`When the first employee accesses the website, the 
request goes to the remote web server via the internet 
and a copy of the web page is stored on the 
organisation's proxy server cache.

When subsequent employees access the same site, the 
proxy server delivers the cached copy directly from 
its local storage — without accessing the internet — 
which is much faster.`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 10 — Local vs Cloud Storage</p>
              <p className="text-sm mt-1">Compare local and cloud storage in terms of speed and cost.</p>
              <Spoiler>
{`Speed: Local storage is generally faster because it 
connects directly to the computer. Cloud storage 
speed depends on the internet connection and is 
usually slower.

Cost: Local storage has a once-off purchase cost. 
Cloud storage has ongoing subscription costs for 
storage space and possibly bandwidth, which can 
become expensive over time.`}
              </Spoiler>
            </div>
          </div>
        </Section>

        {/* ══════════════════════════════════════════════════════════════════════
             LEARNING UNIT 3 — SYSTEM SOFTWARE
             ══════════════════════════════════════════════════════════════════════ */}

        <div className="mt-12 mb-8 pt-6 border-t-2 border-amber-400">
          <h2 className="text-2xl font-bold text-amber-800 text-center" style={{ fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" }}>Learning Unit 3 — System Software</h2>
          <p className="text-center text-sm text-slate-500 mt-1">Boot, Machine Cycle, Processing, Interrupts, Virtual Memory &amp; Translators</p>
        </div>

        {/* ══════════════ 3.1 BOOT PROCESS ══════════════ */}
        <Section id="boot-process" title="3.1 Boot Process" completed={completed["boot-process"]} onToggle={() => toggle("boot-process")}>
          <p>At Grade 10 you met the operating system as the program that loads and runs other programs. Grade 11 zooms into <em>how</em> the OS gets loaded in the first place. The handoff sequence is <strong>Firmware → Bootloader → Kernel → User-space</strong>. Each layer&rsquo;s job is to set up the next layer and step out of the way.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.1.1 Firmware — BIOS and UEFI</h3>
          <p><strong>Firmware</strong> is software permanently stored on a chip on the motherboard. It is the very first code the CPU runs after power-on, before any operating system exists.</p>
          <p className="mt-2"><strong>BIOS (Basic Input/Output System)</strong> is the legacy firmware standard from the 1980s, stored in a ROM chip on the motherboard. <strong>UEFI (Unified Extensible Firmware Interface)</strong> is the modern replacement. Both do the same job &mdash; wake the hardware up, find the operating system, hand control over &mdash; but UEFI does it better.</p>

          <InfoBox title="UEFI vs BIOS — what UEFI improves" color="blue">
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Faster boot:</strong> UEFI initialises hardware in parallel.</li>
              <li><strong>Drives larger than 2 TB:</strong> BIOS&rsquo;s old MBR partition table cannot address them; UEFI&rsquo;s GPT can.</li>
              <li><strong>Secure Boot:</strong> only signed bootloaders are allowed to run, blocking boot-sector malware.</li>
              <li><strong>Richer setup interface:</strong> graphical, mouse-driven, instead of BIOS&rsquo;s text-only menus.</li>
            </ul>
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.1.2 CMOS — where firmware settings live</h3>
          <p><strong>CMOS</strong> is a small amount of battery-backed RAM on the motherboard that stores the firmware&rsquo;s <strong>settings</strong> &mdash; boot order, system date and time, fan-speed profiles, overclock settings. A coin-cell battery keeps it alive when the computer is unplugged. Removing that battery (or the &ldquo;clear CMOS&rdquo; jumper) resets the firmware to defaults.</p>
          <p className="mt-2 text-sm italic text-slate-600">Note: CMOS stores <em>settings</em>; the BIOS or UEFI firmware code itself lives in a separate ROM chip.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.1.3 The boot sequence, step by step</h3>
          <p>From the moment you press the power button to the moment you see the login screen:</p>
          <ol className="list-decimal list-inside space-y-1 mt-2 text-sm">
            <li><strong>Power on.</strong> Voltage reaches the motherboard; the firmware in flash ROM begins executing. Reads its settings from CMOS.</li>
            <li><strong>POST (Power-On Self-Test).</strong> The firmware checks RAM, CPU, GPU, and drives. Beeps or on-screen errors signal failure.</li>
            <li><strong>Find the bootloader.</strong> The firmware looks at the boot order in CMOS, picks the first bootable drive, and finds the <strong>bootloader</strong> on it.</li>
            <li><strong>Bootloader runs.</strong> Loaded into RAM by the firmware. Locates the OS <strong>kernel</strong> on disk and loads it into RAM. Examples: GRUB on Linux, Windows Boot Manager.</li>
            <li><strong>Kernel takes over.</strong> Initialises drivers (storage, GPU, network, USB), mounts the filesystem, starts the first user-space process (<code>init</code> / <code>systemd</code> / Windows session manager).</li>
            <li><strong>User-space.</strong> Background services launch (audio, network, accessibility); the login screen appears.</li>
          </ol>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Know the difference between BIOS, UEFI, and CMOS. Be able to list at least <strong>two</strong> ways UEFI improves on BIOS. Know that the kernel is loaded by the bootloader, not by the firmware directly.
          </div>
        </Section>

        {/* ══════════════ 3.2 MACHINE CYCLE ══════════════ */}
        <Section id="machine-cycle" title="3.2 Machine Cycle" completed={completed["machine-cycle"]} onToggle={() => toggle("machine-cycle")}>
          <p>Once the OS is running, the CPU executes program instructions one after another by following the <strong>machine cycle</strong> &mdash; a four-step rhythm that repeats <em>billions</em> of times per second.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.2.1 The four stages</h3>
          <Table
            headers={["Stage", "Who", "What happens"]}
            rows={[
              ["Fetch", "Control Unit", "Copies the next instruction from RAM into a register inside the CPU. Uses the address bus to ask, the data bus to receive."],
              ["Decode", "Control Unit", "Works out what the instruction means: which operation, which registers, where the operands are."],
              ["Execute", "ALU (or other unit)", "Performs the operation. Arithmetic and logic happen in the ALU; memory reads/writes use the bus; jumps update the program counter."],
              ["Store", "Control Unit", "Writes the result to a register, RAM, or output device. Then loops back to Fetch with the next instruction."],
            ]}
          />

          <InfoBox title="Why this matters for performance" color="green">
            A 4 GHz CPU loops through Fetch → Decode → Execute → Store roughly four billion times per second. Most of the &ldquo;time&rdquo; is spent in <strong>Fetch</strong> &mdash; waiting for the next instruction to arrive from cache or RAM. This is exactly why cache (LU2 §2.5) matters: a cache hit makes Fetch fast, a cache miss makes the whole cycle wait. Real-world speed is mostly a story about cache hit rates.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.2.2 Worked trace — one ADD instruction</h3>
          <p>The CPU is about to execute <code>ADD R1, R2</code> &mdash; add register R2 to register R1. The instruction is at RAM address <code>0x4000</code>.</p>
          <Table
            headers={["Stage", "Activity"]}
            rows={[
              ["Fetch", "CU places 0x4000 on the address bus; reads the encoded instruction off the data bus into the instruction register."],
              ["Decode", "CU recognises the opcode as ADD. Identifies R1 and R2 as the operands; R1 as the destination."],
              ["Execute", "ALU performs R1 + R2. The CU has already routed both register values to the ALU's inputs."],
              ["Store", "CU writes the result back into R1. Increments the program counter to 0x4004 (the next instruction)."],
            ]}
          />

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Know the four stages <strong>in order</strong>: Fetch → Decode → Execute → Store. Be able to describe each in one sentence. Examiners love this question.
          </div>
        </Section>

        {/* ══════════════ 3.3 PROCESSING TECHNIQUES ══════════════ */}
        <Section id="processing-techniques" title="3.3 Processing Techniques" completed={completed["processing-techniques"]} onToggle={() => toggle("processing-techniques")}>
          <p>A modern computer runs dozens of programs at once &mdash; or appears to. The OS uses two software techniques to share the CPU between them, and the hardware (LU2 §2.1) gives it some help.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.3.1 Process vs Thread</h3>
          <p>A <strong>process</strong> is one running program with its own memory space and files (Word, Chrome, Spotify each are processes). A <strong>thread</strong> is a single sequence of instructions inside a process. One process can have many threads sharing its memory but executing in parallel.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.3.2 Multitasking — sharing across processes</h3>
          <p><strong>Multitasking</strong> is the OS rapidly switching the CPU between <em>different processes</em> so they all appear to run at once. On a single-core CPU it is just rapid time-slicing &mdash; only one program runs at any <em>instant</em>. On a multi-core CPU it is genuine parallel running plus time-slicing within each core.</p>
          <p className="mt-2">Two key supporting concepts:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>Time slice</strong> &mdash; the small block of CPU time (a few milliseconds) the OS gives a process before pausing it.</li>
            <li><strong>Context switch</strong> &mdash; the work the OS does at the end of a time slice: save the paused process&rsquo;s registers and program counter, load the next process&rsquo;s, then resume.</li>
          </ul>
          <InfoBox title="Pre-emptive multitasking" color="blue">
            All modern OSes use <strong>pre-emptive multitasking</strong> &mdash; the OS can <em>forcefully</em> pause a running process when its time slice ends. The opposite (cooperative) requires programs to voluntarily yield, and a single buggy program could lock up the whole computer.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.3.3 Multithreading — parallelism within one process</h3>
          <p><strong>Multithreading</strong> is the OS (or the program itself) running <em>multiple threads inside a single process</em> so different parts of the same program execute in parallel. Useful when one part of a program is waiting (for the network, disk, user input) while another part can keep working.</p>
          <p className="mt-2 text-sm"><em>Example:</em> a web browser uses one thread to render the page, another to download images in the background, another to run JavaScript &mdash; all inside one Chrome process.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.3.4 How software maps onto hardware</h3>
          <p>Multitasking and multithreading are <em>what the OS does</em>. <strong>Hyper-threading</strong> and <strong>multi-processing</strong> (LU2 §2.1) are <em>what the hardware provides</em> to make those software techniques work better.</p>
          <Table
            headers={["Technique", "Type", "What it does"]}
            rows={[
              ["Multitasking", "Software (OS)", "Switches CPU between different processes."],
              ["Multithreading", "Software (OS / program)", "Runs multiple threads inside one process in parallel."],
              ["Hyper-threading", "Hardware (one core)", "One physical core presents as two logical cores; the OS schedules two threads on it."],
              ["Multi-processing", "Hardware (multiple cores)", "Multiple physical cores; OS schedules processes/threads on different cores. Genuine parallelism."],
            ]}
          />

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>distinguish</strong> multitasking from multithreading <em>with examples</em>. Know that hyper-threading is a hardware trick, not software, and that it doesn&rsquo;t add real cores &mdash; only logical ones.
          </div>
        </Section>

        {/* ══════════════ 3.4 INTERRUPTS ══════════════ */}
        <Section id="interrupts" title="3.4 Interrupts" completed={completed["interrupts"]} onToggle={() => toggle("interrupts")}>
          <p>How does the CPU know when you press a key, when a network packet arrives, or when the disk finishes a read? It does not <em>poll</em> &mdash; that would waste cycles. Instead, devices <strong>interrupt</strong> the CPU when they need attention.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.4.1 What an interrupt is</h3>
          <p>An <strong>interrupt</strong> is a signal that pauses the CPU&rsquo;s current task and asks it to handle something more urgent. The CPU finishes its current cycle, saves its state, runs the interrupt handler, then resumes whatever it was doing &mdash; all in microseconds.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.4.2 IRQ and IO Range</h3>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>IRQ (Interrupt Request)</strong> &mdash; the numbered line a device uses to request CPU attention. IRQ 1 traditionally = keyboard; IRQ 12 = PS/2 mouse. Modern systems assign IRQs dynamically, but the concept stays.</li>
            <li><strong>Interrupt handler (ISR)</strong> &mdash; the small piece of code (in the OS or driver) that runs in response to a particular interrupt. Reads the keystroke, acknowledges the disk, updates the clock &mdash; then returns control to whatever was running.</li>
            <li><strong>IO range</strong> &mdash; the range of memory addresses (or port numbers) the OS reserves for talking to a particular device. Combined with its IRQ, the IO range is how the OS knows <em>which</em> device sent <em>which</em> interrupt.</li>
          </ul>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.4.3 Walkthrough — a single keypress</h3>
          <ol className="list-decimal list-inside space-y-1 mt-2 text-sm">
            <li>You press the letter <strong>A</strong> while typing in Word.</li>
            <li>The keyboard controller raises <strong>IRQ 1</strong>.</li>
            <li>The CPU finishes its current instruction, then interrupts Word: saves its registers, freezes its state.</li>
            <li>The OS looks up IRQ 1&rsquo;s interrupt handler &mdash; the keyboard driver.</li>
            <li>The driver reads the scancode for &ldquo;A&rdquo; from the keyboard&rsquo;s IO range.</li>
            <li>The driver translates the scancode into the character &ldquo;A&rdquo; and pushes it into the OS&rsquo;s input queue for the focused window.</li>
            <li>Driver finishes; OS restores Word&rsquo;s registers and resumes Word from exactly where it stopped.</li>
            <li>Word reads &ldquo;A&rdquo; from the queue and inserts it at the cursor.</li>
          </ol>
          <p className="mt-2 text-sm italic text-slate-600">The whole sequence completes in microseconds. Between your keystrokes the CPU is doing this thousands of times per second for mouse moves, network packets, disk completions, and timer ticks.</p>

          <InfoBox title="Interrupts are normal — not errors" color="orange">
            A common mistake: confusing &ldquo;interrupt&rdquo; with &ldquo;error&rdquo;. Every keystroke, mouse move, network packet and timer tick fires an interrupt. The CPU is constantly being interrupted &mdash; it&rsquo;s how it stays responsive without polling every device in turn.
          </InfoBox>
        </Section>

        {/* ══════════════ 3.5 VIRTUAL MEMORY ══════════════ */}
        <Section id="virtual-memory" title="3.5 Virtual Memory" completed={completed["virtual-memory"]} onToggle={() => toggle("virtual-memory")}>
          <p>What happens when the programs you have open need more RAM than the computer physically has? <strong>Virtual memory</strong> is the trick the OS uses to keep things running anyway.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.5.1 The basic idea</h3>
          <p>The OS pretends each program has more RAM than physically exists by treating part of the disk as overflow. Programs are written as if they have all the RAM they need; the OS quietly moves chunks between actual RAM and disk in the background.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.5.2 Pages, Paging, and the Swap File</h3>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>Page</strong> &mdash; a fixed-size chunk of memory (typically 4 KB). The OS treats it as the unit for moving between RAM and disk.</li>
            <li><strong>Paging</strong> &mdash; moving a page out of RAM to disk to make room for another that&rsquo;s needed right now.</li>
            <li><strong>Swap file (page file)</strong> &mdash; the area of disk reserved for paged-out memory. On Windows this is <code>pagefile.sys</code>; on Linux it&rsquo;s a swap partition or <code>/swapfile</code>.</li>
            <li><strong>Swapping</strong> &mdash; loosely, the act of moving pages between RAM and the swap file. (Strictly: <em>paging</em> moves individual pages; <em>swapping</em> originally meant moving whole processes in older OSes.)</li>
          </ul>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.5.3 Effect on processing speed</h3>
          <Table
            headers={["Scenario", "What happens", "User experience"]}
            rows={[
              ["RAM not full", "All active pages stay in RAM. The swap file is barely touched.", "Programs run at full RAM speed; no slowdown."],
              ["RAM almost full", "OS pages out less-active pages to make room for new ones; reads them back when needed.", "Slight slowdown; brief stalls when paged-out data is needed."],
              ["RAM saturated (Thrashing)", "Every newly-active page forces another to be paged out, then re-paged-in shortly after. CPU spends more time paging than computing.", "Disk light is on solid; computer crawls; opening anything new takes seconds."],
            ]}
          />
          <InfoBox title="Cure for thrashing" color="orange">
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Close programs</strong> to free up RAM so paging stops being needed (immediate fix).</li>
              <li><strong>Add more RAM</strong> &mdash; the durable fix; lets all active pages stay resident.</li>
              <li><strong>Replace HDD with SSD</strong> &mdash; when paging does happen, an SSD is ~100× faster than an HDD, so the symptoms are much milder.</li>
            </ul>
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Virtual memory is a <strong>capacity</strong> feature, not a <strong>speed</strong> feature. It lets the computer run more programs than fit in RAM, but it does <em>not</em> make programs run faster &mdash; quite the opposite once paging starts. Adding RAM is what speeds things up.
          </div>
        </Section>

        {/* ══════════════ 3.6 TRANSLATORS ══════════════ */}
        <Section id="translators" title="3.6 Translators" completed={completed["translators"]} onToggle={() => toggle("translators")}>
          <p>The CPU only understands <strong>machine code</strong> &mdash; raw 1s and 0s. Programmers write in human-readable languages (Java, Python, Delphi). A <strong>translator</strong> bridges the gap.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.6.1 High-level vs Low-level languages</h3>
          <Table
            headers={["Level", "How close to the CPU", "Examples"]}
            rows={[
              ["High-level", "Far. Hides registers, memory addresses, instructions. Easier to read and write.", "Java, Python, Delphi (Pascal), C#, JavaScript"],
              ["Low-level", "Close. Programmer manipulates memory and instructions directly. Hard to read; very efficient.", "Assembly language, Machine code"],
            ]}
          />
          <p className="mt-2 text-sm"><strong>Assembly language</strong> uses short mnemonics (<code>MOV</code>, <code>ADD</code>, <code>JMP</code>) that map almost one-to-one onto CPU instructions. Different CPU families have different assembly languages.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.6.2 The three translator types</h3>
          <Table
            headers={["Translator", "What it translates", "When"]}
            rows={[
              ["Compiler", "Entire source program → machine code (or bytecode)", "Ahead of time. Saved as a separate file. Errors all reported at compile time, before the program runs."],
              ["Interpreter", "One statement at a time → executed immediately", "At run-time. No separate executable. Errors only surface as the bad line is reached."],
              ["Assembler", "Assembly language → machine code", "Ahead of time. Specialised compiler for low-level code; near one-to-one translation."],
            ]}
          />

          <InfoBox title="JIT compilers — the hybrid" color="blue">
            A <strong>JIT (Just-In-Time) compiler</strong> starts running like an interpreter, but <em>compiles hot (frequently used) sections to native machine code on the fly</em>. Used by the JVM and modern JavaScript engines. Bridges the flexibility of interpretation with the speed of compilation.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.6.3 One-stage vs Two-stage compilers</h3>
          <p>Compilers come in two architectural flavours:</p>
          <Table
            headers={["Type", "What it produces", "Examples", "Trade-off"]}
            rows={[
              ["One-stage", "Native machine code (.exe / ELF) for a specific CPU + OS, in one step.", "C, C++, Delphi/Pascal", "Runs natively at full speed. But: a separate build is needed for every target platform."],
              ["Two-stage", "Stage 1: bytecode (e.g. .class). Stage 2: a runtime interprets / JIT-compiles it on the user's machine.", "Java (javac → JVM), C# (csc → CLR)", "Same bytecode runs on any platform with the runtime — portable. Slightly slower; user needs the runtime installed."],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.6.4 Why Java is portable but C is not</h3>
          <p>A Java compiler (<code>javac</code>) produces <strong>bytecode</strong> (<code>.class</code> files) &mdash; a platform-independent intermediate form that is not specific to any OS or CPU. The <strong>JVM (Java Virtual Machine)</strong> is built specifically for each platform (Windows JVM, macOS JVM, Linux JVM); the same <code>.class</code> file runs on all of them.</p>
          <p className="mt-2">A C compiler produces <strong>native machine code</strong> for the specific OS and CPU it ran on. Move the binary to a different OS &mdash; even with the same CPU &mdash; and it won&rsquo;t load. To run a C program on another OS, the source must be re-compiled there.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">3.6.5 Errors and the IDE</h3>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>Syntax error</strong> &mdash; a violation of the language&rsquo;s grammar (missing semicolon, misspelt keyword). The compiler refuses to build; the interpreter halts at that line. Easy to fix because the translator points at the location.</li>
            <li><strong>Runtime error</strong> &mdash; an error that only happens <em>while</em> the program is running (divide-by-zero, null reference, file-not-found). The translator can&rsquo;t catch these; the bug only manifests with certain inputs.</li>
            <li><strong>IDE</strong> &mdash; bundled editor + compiler/interpreter + debugger (NetBeans, IntelliJ, VS Code, Visual Studio). Pressing &ldquo;Run&rdquo; hides several tools behind one button; the translation step still happens.</li>
            <li><strong>Debugger</strong> &mdash; lets you step through a running program one line at a time, pause it (breakpoints), and inspect variables. Catches runtime errors that the compiler misses.</li>
          </ul>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>distinguish</strong> compiler from interpreter <em>and discuss two consequences</em> (speed, error reporting, distribution, edit-test cycle). Know why Java is portable using the term <strong>bytecode</strong> in your answer. Be able to recommend a translator for a given scenario with at least three justifications.
          </div>
        </Section>

        {/* ══════════════ LU3 PRACTICE EXERCISES ══════════════ */}
        <Section id="exercises-lu3" title="LU3 Practice Exercises" completed={completed["exercises-lu3"]} onToggle={() => toggle("exercises-lu3")}>
          <p className="mb-4 text-sm italic text-slate-600">
            Work through each exercise on paper first, then check your answer. These exercises cover all Learning Unit 3 topics. Where a question is paraphrased from a past paper, the source is noted.
          </p>

          <div className="space-y-6 text-sm">
            <div>
              {/* Source: ieb_it_2023_p2 Q1.4 — paraphrased */}
              <p className="font-semibold">Exercise 1 &mdash; Match the term (Virtual Memory)</p>
              <p className="text-sm mt-1">Which term describes <em>a memory-management technique where secondary storage can be used as if it were main memory</em>?</p>
              <Spoiler>
{`Virtual memory.

The OS treats part of the disk as if it were extra RAM,
moving pages in and out of RAM as needed.`}
              </Spoiler>
            </div>

            <div>
              {/* Source: ieb_it_2023_p2 Q2.4 / Q2.5 — paraphrased */}
              <p className="font-semibold">Exercise 2 &mdash; Match the term (IO Range)</p>
              <p className="text-sm mt-1">Which term describes <em>the range of memory addresses the OS reserves for talking to a particular device, used together with the device&rsquo;s IRQ to identify which device sent which interrupt</em>?</p>
              <Spoiler>
{`IO range.

Combined with the IRQ, the IO range tells the OS
which device to communicate with — the CPU writes
to / reads from those addresses, and the device responds.`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 3 &mdash; The Machine Cycle</p>
              <p className="text-sm mt-1">Name the four stages of the machine cycle, in order, and briefly state what happens in each stage. <em>(4 marks)</em></p>
              <Spoiler>
{`1. Fetch — the Control Unit copies the next instruction
   from RAM into a register inside the CPU. (1)
2. Decode — the CU works out what the instruction means
   and where its operands are. (1)
3. Execute — the relevant unit (ALU for arithmetic and
   logic) performs the operation. (1)
4. Store — the result is written back to a register,
   to RAM, or to an output device, and the CPU advances
   to the next instruction. (1)`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 4 &mdash; BIOS vs UEFI</p>
              <p className="text-sm mt-1">Distinguish between BIOS and UEFI, giving TWO ways UEFI improves on BIOS. <em>(4 marks)</em></p>
              <Spoiler>
{`BIOS — legacy firmware (1980s design) stored in ROM
on the motherboard. First code to run at power-on;
initialises hardware and loads the bootloader. (1)

UEFI — modern replacement for BIOS, performing the
same job. (1)

Two improvements (any 2):
• Boots faster — UEFI initialises hardware in parallel.
• Supports drives larger than 2 TB (BIOS's MBR cannot).
• Supports Secure Boot — only signed bootloaders allowed.
• Richer setup interface — graphical, mouse-driven.
                                        (2 marks for any 2)`}
              </Spoiler>
            </div>

            <div>
              {/* Source: ieb_it_2023_p2 Q3.4.2 — paraphrased; expanded with examples */}
              <p className="font-semibold">Exercise 5 &mdash; Multitasking vs Multithreading</p>
              <p className="text-sm mt-1">Multitasking and multithreading are two commonly used techniques to improve performance. Define each concept and give ONE example of how each can improve performance. <em>(4 marks)</em></p>
              <Spoiler>
{`Multitasking — the OS rapidly switches the CPU
between MULTIPLE PROCESSES (different programs), giving
the illusion that they all run at once. (1)
Example: Chrome, Word and Spotify all running on a
single laptop — the user can switch between them
without closing any. (1)

Multithreading — the OS or program runs MULTIPLE
THREADS INSIDE ONE PROCESS so different parts of the
same program execute in parallel. (1)
Example: a web browser uses one thread to render the
page, another to download images, another to run
JavaScript — the page stays responsive while images
load. (1)`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 6 &mdash; Interrupts</p>
              <p className="text-sm mt-1">Explain how interrupts allow the CPU to share its attention between many devices and programs. Use the terms <em>IRQ</em> and <em>interrupt handler</em> in your answer. <em>(5 marks)</em></p>
              <Spoiler>
{`• An interrupt is a signal that pauses the CPU's
  current task and asks it to attend to something more
  urgent. (1)
• Each device that needs the CPU's attention has an
  IRQ number it uses to raise an interrupt — keyboard,
  mouse, disk, network, system timer. (1)
• When an interrupt fires, the CPU finishes the current
  instruction, saves its registers, and runs that
  device's interrupt handler — a small routine in the
  OS or driver. (1)
• After the handler finishes, the CPU restores its
  previous state and resumes whatever it was doing. (1)
• This makes the CPU event-driven — it responds to
  thousands of interrupts per second without polling
  each device in turn, sharing its attention
  efficiently across them all. (1)`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 7 &mdash; Virtual Memory and Thrashing</p>
              <p className="text-sm mt-1">A computer has 8 GB of RAM and an HDD as its main drive. The user opens many programs until the disk light is constantly on and the computer feels frozen. Explain what is happening using the terms <em>paging</em>, <em>swap file</em>, and <em>thrashing</em>. Suggest TWO ways to fix the problem. <em>(6 marks)</em></p>
              <Spoiler>
{`• The 8 GB of RAM has filled up. The OS responds by
  PAGING less-active pages out of RAM into the SWAP
  FILE on the HDD to make room for active ones. (1)
• Because the user keeps opening programs, every newly-
  active page forces another to be paged out, then
  re-paged-in shortly afterwards. (1)
• The system enters THRASHING — the CPU spends most of
  its time waiting for pages to come back from disk.
  The constant disk light is the swap file being read
  and written. (1)
• The HDD makes this dramatically worse: an SSD would
  be ~100× faster for paging and the slowdown would be
  much milder. (1)

Fixes (any 2):
• Close programs to free up RAM (immediate). (1)
• Add more RAM (durable fix; lets all active pages
  stay resident). (1)
• Replace HDD with SSD (paging becomes much faster). (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Source: ieb_it_2023_p2 Q1.1 / Q2.6 — paraphrased; expanded to discussion */}
              <p className="font-semibold">Exercise 8 &mdash; Compiler vs Interpreter</p>
              <p className="text-sm mt-1">Distinguish between a compiler and an interpreter. Discuss TWO consequences of using one rather than the other. <em>(6 marks)</em></p>
              <Spoiler>
{`Distinction (2 marks):
• A COMPILER translates the entire source program
  ahead of time and saves the result as a separate
  executable; once compiled, the program runs without
  the compiler. (1)
• An INTERPRETER translates and runs the source code
  line-by-line at run-time; no separate executable is
  produced. (1)

Consequences (any 2, 2 marks each):
• Speed — compiled code runs at native speed;
  interpreted code is slower because translation
  happens during execution. (2)
• Error reporting — a compiler reports all syntax
  errors at compile time; an interpreter only finds
  them as it reaches each line, so a program may run
  partially before halting on the bad line. (2)
• Distribution — a compiled program ships as a single
  executable; an interpreted one needs both the source
  and the interpreter on the user's machine. (2)
• Edit-test cycle — interpreters give instant feedback
  (no compile step), helpful for learning and quick
  scripts; compilers require a build step but reward
  it with faster execution. (2)`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 9 &mdash; Java Portability</p>
              <p className="text-sm mt-1">Explain why a Java program is <em>portable</em> across operating systems but a C program is not. Use the term <em>bytecode</em> in your answer. <em>(4 marks)</em></p>
              <Spoiler>
{`• A Java compiler (javac) produces BYTECODE
  (.class files) — a platform-independent intermediate
  form that is not specific to any OS or CPU. (1)
• The bytecode is run by a JVM, which is built
  specifically for each platform (Windows JVM, macOS
  JVM, Linux JVM). The same .class file runs unchanged
  on every platform that has a JVM. (1)
• A C compiler produces NATIVE MACHINE CODE for the
  specific OS and CPU it ran on. The resulting .exe
  only runs on that platform. (1)
• To move a C program to another OS, the source must
  be re-compiled there; to move a Java program, only
  the JVM has to be present. (1)`}
              </Spoiler>
            </div>

            <div>
              <p className="font-semibold">Exercise 10 &mdash; Recommend a Translator (L3)</p>
              <p className="text-sm mt-1">A small school administration program needs to be developed in three months by a team of 5 students. The program must run on the school&rsquo;s mixed Windows + macOS computers and store data in a SQL database. Recommend a programming language <em>and</em> a translator type, justifying your choice across at least THREE factors. <em>(7 marks)</em></p>
              <Spoiler>
{`Sample answer — Java with a TWO-STAGE compiler:

• Portability — the school has Windows AND macOS
  machines. A two-stage compiler emits bytecode that
  runs unchanged on both via the JVM. A one-stage
  compiled language (C, C++, Delphi) would require
  a separate executable for each OS. (2)
• Developer productivity — Java is high-level with
  strong libraries (JDBC for the SQL database,
  Swing/JavaFX for the UI). Five students can be
  productive within three months. (2)
• Performance — adequate for a school admin system.
  JIT-compiled bytecode is fast enough; speed is not
  the dominant axis here. (1)
• Tooling — IDEs like NetBeans or IntelliJ ship
  excellent Java support, including a debugger. (1)
• Trade-off — slightly slower than C++, requires a
  JRE on each machine. Acceptable for this workload. (1)

Other defensible answers: Python (interpreted) for
ease of development if the school will install Python;
C# with .NET as a Windows-leaning two-stage equivalent.
Full marks for any defensible choice supported by
≥3 factors AND an honest trade-off note.`}
              </Spoiler>
            </div>
          </div>
        </Section>

        {/* ══════════════════════════════════════════════════════════════════════
             LEARNING UNIT 4 — LOCAL AREA NETWORKS
             ══════════════════════════════════════════════════════════════════════ */}

        <div className="mt-12 mb-8 pt-6 border-t-2 border-amber-400">
          <h2 className="text-2xl font-bold text-amber-800 text-center" style={{ fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" }}>Learning Unit 4 — Local Area Networks</h2>
          <p className="text-center text-sm text-slate-500 mt-1">Media · Topology · Ethernet · Addressing · Protocols · Devices · OSI · Wireless</p>
        </div>

        {/* ══════════════ 4.1 PHYSICAL ASPECTS OF NETWORK MEDIA ══════════════ */}
        <Section id="lan-physical" title="4.1 Physical Aspects of Network Media" completed={completed["lan-physical"]} onToggle={() => toggle("lan-physical")}>
          <p>Grade 10 networking treated <em>what</em> is on a network &mdash; nodes, classes (PAN/HAN/LAN/WAN/GAN), client&ndash;server vs peer-to-peer. Grade 11 zooms into <strong>how the network actually carries data</strong>: the physical signal, the cables and air-waves, the addresses, and the rules that hold it all together. The whole topic builds bottom-up. Layer one is the physical medium &mdash; what data physically travels on.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.1.1 Bounded vs Unbounded Media</h3>
          <Table
            headers={["", "Bounded media", "Unbounded media"]}
            rows={[
              ["Means", "Cables — UTP, STP, fibre, coax", "Wireless — Wi-Fi, Bluetooth, microwave, radio"],
              ["Signal travels through", "A physical conductor (copper or glass)", "Air or space, as electromagnetic waves"],
              ["Predictability", "Speed and quality are stable per cable run", "Affected by interference, range, weather, walls"],
              ["Security exposure", "Requires physical access to tap", "Anyone in radio range can listen — encryption is the real defence"],
              ["Mobility", "Fixed — the device is tethered to the cable run", "High — the device can roam"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.1.2 Two recurring axes</h3>
          <p>Almost every network design choice in LU4 boils down to balancing the same handful of factors:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>Bandwidth</strong> &mdash; the data rate the medium can sustain. Fibre &gt; modern Wi-Fi &gt; UTP &gt; older Wi-Fi &gt; Bluetooth.</li>
            <li><strong>Reach</strong> &mdash; the distance the signal can travel before it has to be regenerated. Fibre (kilometres) &gt; UTP (100 m) &gt; Wi-Fi (tens of metres) &gt; Bluetooth (~10 m).</li>
            <li><strong>Reliability / interference</strong> &mdash; how vulnerable the signal is to outside disruption. Fibre is immune; UTP affected by EMI; wireless affected by interference, range, and weather.</li>
            <li><strong>Security exposure</strong> &mdash; how easy it is to eavesdrop. Fibre hardest; copper next; wireless easiest.</li>
            <li><strong>Cost &amp; mobility</strong> &mdash; UTP cheap, fibre expensive, wireless flexible.</li>
          </ul>

          <InfoBox title="The Gr11 skill verb" color="blue">
            The Gr11 SAG verb for this topic is <strong>&ldquo;discusses ... configurations, devices and architectures&rdquo;</strong>. Exam questions ask you to <em>recommend, justify, distinguish, explain</em> &mdash; not just identify. A good answer always names the trade-offs, not only the winner.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>distinguish bounded from unbounded media</strong> with at least two contrasts. Know the five recurring decision axes &mdash; bandwidth, reach, reliability, security, cost/mobility &mdash; and be able to apply them to a recommendation question.
          </div>
        </Section>

        {/* ══════════════ 4.2 BOUNDED MEDIA ══════════════ */}
        <Section id="bounded-media" title="4.2 Bounded Media" completed={completed["bounded-media"]} onToggle={() => toggle("bounded-media")}>
          <p>Bounded media confine the signal inside a physical conductor. Four cable types matter for Grade 11:</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.2.1 The four cable types</h3>
          <KeyTerm term="UTP (Unshielded Twisted Pair)">The standard LAN cable. Four pairs of copper wires twisted together &mdash; the twisting cancels much of its self-interference. Cheap, easy to terminate, ≤ 100 m per run, common speeds 1 Gbps and 10 Gbps. Cat 5e / Cat 6 / Cat 6a are the modern grades.</KeyTerm>
          <KeyTerm term="STP (Shielded Twisted Pair)">UTP with a foil or braid shield around the pairs. Resists external EMI better than UTP. More expensive, heavier, harder to install. Used near heavy electrical machinery, fluorescent lights, lift motors.</KeyTerm>
          <KeyTerm term="Fibre-optic cable">A glass or plastic core that carries data as pulses of light. Very high bandwidth (10 Gbps to 400 Gbps and beyond), long reach (kilometres), immune to electrical interference and to lightning, hard to tap passively. Expensive to install; used for <strong>backbone</strong> runs.</KeyTerm>
          <KeyTerm term="Coaxial cable">A copper core surrounded by an insulator and a metallic shield. Used historically for early LANs and still common for cable-TV / cable-internet last-mile delivery. Less common inside modern LANs.</KeyTerm>

          <Table
            headers={["Cable", "Typical speed", "Max run", "Cost", "Resistance to EMI"]}
            rows={[
              ["UTP (Cat 6)", "1–10 Gbps", "100 m", "Low", "Low (relies on twisting)"],
              ["STP", "1–10 Gbps", "100 m", "Medium", "Medium (shield helps)"],
              ["Fibre", "10–400+ Gbps", "Kilometres", "High", "Immune (carries light, not electricity)"],
              ["Coaxial", "1 Mbps – 1 Gbps", "Hundreds of metres", "Medium", "Medium"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.2.2 Channel weaknesses</h3>
          <p>Every cable type has weaknesses that drive design choices. The four examinable ones:</p>

          <KeyTerm term="Attenuation">The gradual loss of signal strength as it travels along a medium. Doubling the cable length more than doubles the loss. Why UTP has a 100 m limit; why long runs need fibre or a <strong>repeater</strong>.</KeyTerm>
          <KeyTerm term="Cross-talk">Signal &ldquo;leaking&rdquo; from one wire to a neighbouring wire in the same cable bundle. Why UTP wires are <em>twisted</em> in pairs (the twist cancels most of it) and why STP adds a shield for noisy environments.</KeyTerm>
          <KeyTerm term="EMI (Electromagnetic Interference)">Corruption of a signal by external electromagnetic fields &mdash; fluorescent lights, motors, microwave ovens, lightning, other radio sources. Mitigated by shielding (STP), routing cables away from sources, or using fibre (which is immune).</KeyTerm>
          <KeyTerm term="Eavesdropping">An attacker passively listening to traffic on the channel. Wireless is most exposed; copper cables can be tapped with physical access; fibre is the hardest to tap. <strong>Encryption</strong> (WPA2/WPA3, HTTPS) is the real defence.</KeyTerm>

          <InfoBox title="Symptom → cable problem matching" color="orange">
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li><strong>130 m UTP run; intermittent connection</strong> → <em>attenuation</em> (UTP exceeds 100 m). Fix: insert a repeater/switch, or replace with fibre.</li>
              <li><strong>Cables under fluorescent lighting; errors when lights flicker</strong> → <em>EMI</em>. Fix: STP, route away, or fibre.</li>
              <li><strong>Two long UTPs in one tight conduit; ghost data corruption at peak hours</strong> → <em>cross-talk</em>. Fix: separate the cables, or STP.</li>
              <li><strong>Open Wi-Fi; someone in the parking lot reading internal email</strong> → <em>eavesdropping</em>. Fix: WPA3 with a strong passphrase.</li>
            </ul>
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be able to <strong>name the four cable types</strong> with one distinguishing feature each. Be able to <strong>explain three weaknesses</strong> with a symptom <em>and</em> a mitigation for each. Examiners reward the symptom-then-fix shape.
          </div>
        </Section>

        {/* ══════════════ 4.3 UNBOUNDED MEDIA ══════════════ */}
        <Section id="unbounded-media" title="4.3 Unbounded Media" completed={completed["unbounded-media"]} onToggle={() => toggle("unbounded-media")}>
          <p>Unbounded media carry the signal through air or space as electromagnetic waves. No cable required &mdash; convenient and mobile, but more exposed to interference and eavesdropping.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.3.1 The four wireless types</h3>
          <KeyTerm term="Wi-Fi">The dominant unbounded LAN technology. IEEE 802.11 family of standards (a/b/g/n/ac/ax/be &mdash; newer = faster). Range tens of metres indoors; speeds 100 Mbps to 9 Gbps depending on standard, channel, and number of antennas.</KeyTerm>
          <KeyTerm term="Bluetooth">Short-range (~10 m typical) wireless for <strong>PAN</strong>-scale links: earbuds, mice, file transfer between phones. Lower power and lower data rate than Wi-Fi; designed for pairing two devices, not a whole LAN.</KeyTerm>
          <KeyTerm term="Microwave">High-frequency radio used in narrow point-to-point beams between fixed antennas (e.g. ISP tower to ISP tower). Long reach, requires line-of-sight, weather-affected.</KeyTerm>
          <KeyTerm term="Radio waves">Broad term for omnidirectional wireless &mdash; Wi-Fi, cellular, Bluetooth all use radio waves at different frequencies. Convenient and flexible; lower bandwidth and more interference than cabled media.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.3.2 Bounded vs unbounded — where each wins</h3>
          <Table
            headers={["Aspect", "Bounded (cable)", "Unbounded (wireless)"]}
            rows={[
              ["Bandwidth (typical)", "1–10 Gbps (UTP); 10+ Gbps (fibre)", "100 Mbps – 9 Gbps (Wi-Fi 6/7)"],
              ["Mobility", "None — device is tethered", "High — roam within range"],
              ["Interference", "EMI/cross-talk on copper; fibre immune", "Walls, microwaves, neighbours' Wi-Fi"],
              ["Eavesdropping", "Requires physical access", "Anyone in radio range"],
              ["Cost (deployment)", "Cabling labour per run; one-time", "AP equipment; less labour"],
              ["Best for…", "Backbone, fixed workstations, server links", "Laptops, phones, classrooms full of mobile devices"],
            ]}
          />

          <InfoBox title="Wired AND wireless" color="green">
            Real LANs use both. The backbone between buildings is fibre; the floor switches are linked over UTP; the laptops connect over Wi-Fi to access points wired into those switches. Each medium is used where it wins; none does the whole job alone.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>recommend wired vs wireless</strong> for a scenario, naming at least three relevant axes. Know that <strong>Bluetooth is PAN-scale</strong>, not LAN-scale, and that <strong>Microwave is point-to-point</strong>, not omnidirectional.
          </div>
        </Section>

        {/* ══════════════ 4.4 BROADCAST VS POINT-TO-POINT ══════════════ */}
        <Section id="broadcast-pp" title="4.4 Broadcast vs Point-to-point" completed={completed["broadcast-pp"]} onToggle={() => toggle("broadcast-pp")}>
          <p>Once a frame is on the LAN, it's addressed in one of two ways:</p>

          <KeyTerm term="Broadcast">A transmission addressed to <em>every</em> node on the LAN. ARP uses broadcast (&ldquo;who has this IP?&rdquo;); DHCP discovery uses broadcast (&ldquo;is there a DHCP server?&rdquo;). Switches by default flood a broadcast frame out every port.</KeyTerm>
          <KeyTerm term="Point-to-point">A transmission addressed to one specific recipient. Most useful traffic &mdash; file transfer, web requests, video streams &mdash; is point-to-point. The switch forwards it only to the destination port.</KeyTerm>

          <Table
            headers={["", "Broadcast", "Point-to-point"]}
            rows={[
              ["Destination", "All nodes on the LAN", "One specific node"],
              ["Switch behaviour", "Flooded out every port", "Sent only to the destination port"],
              ["Typical use", "Discovery — ARP, DHCP", "Application traffic — web, files, video"],
              ["Frequency", "Small minority of frames", "Vast majority of frames"],
            ]}
          />

          <InfoBox title="Why broadcast is rare" color="blue">
            Broadcast is expensive at scale &mdash; every node on the LAN has to look at the frame. So protocols use it only where there is no choice: discovering things you don't yet have an address for. Once both ends know each other's MAC and IP, every later frame is point-to-point.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Know that <strong>ARP and DHCP use broadcast</strong>; everything else is point-to-point. Be able to explain why broadcast is reserved for discovery.
          </div>
        </Section>

        {/* ══════════════ 4.5 TOPOLOGY ══════════════ */}
        <Section id="topology" title="4.5 Topology" completed={completed["topology"]} onToggle={() => toggle("topology")}>
          <p>Topology is the <em>shape</em> of the network &mdash; how the cables and devices are arranged. Topology determines reliability, cost, and how badly one broken cable hurts the whole LAN.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.5.1 The five topologies</h3>
          <Table
            headers={["Topology", "Cabling cost", "Resilience to one cable break", "Resilience to central device failure", "Where you see it"]}
            rows={[
              ["Star", "Moderate (one cable per node back to switch)", "High — only that one node is affected", "None — switch failure breaks the whole LAN", "Standard modern Ethernet LAN"],
              ["Bus", "Lowest (one shared cable)", "None — break anywhere kills the network", "N/A (no central device)", "Mostly obsolete; some industrial buses"],
              ["Ring", "Moderate (one cable to each of two neighbours)", "None unless dual ring; one break splits the ring", "N/A", "Older Token Ring; modern metro fibre as dual ring"],
              ["Mesh (full)", "Highest — n × (n−1)/2 cables for n nodes", "High — many alternate paths", "N/A", "Internet backbone; mesh Wi-Fi"],
              ["Hybrid", "Depends — combines its parts", "Combines best of its parts", "Depends", "Real-world LAN: 'star of stars'"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.5.2 Why star dominates</h3>
          <p>The dominant choice for inside-a-building LANs is <strong>star</strong>:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li>Cheap to extend &mdash; just plug a new cable into a free switch port.</li>
            <li>Failure is contained &mdash; only the one node with the bad cable goes offline.</li>
            <li>The central switch is the only single point of failure &mdash; redundant switches address that.</li>
          </ul>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.5.3 The school is hybrid</h3>
          <p>A typical school LAN is a <em>star of stars</em> &mdash; a hybrid:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li>One <strong>core switch</strong> in the server room.</li>
            <li>A <strong>building-level switch</strong> per building, each connected to the core (forming a star at the campus level).</li>
            <li>A <strong>classroom switch</strong> per lab or staffroom, connected to its building-level switch (forming a smaller star).</li>
            <li>Workstations connected to the classroom switches (the leaves of the third-level star).</li>
          </ul>
          <p className="mt-2 text-sm italic text-slate-600">Each level is a star; the whole thing is a hybrid.</p>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Know the <strong>five topologies</strong> with one distinguishing feature each. Be able to <strong>justify a topology</strong> for a given scenario in terms of cabling cost <em>and</em> resilience to failure. Mesh has maximum redundancy <strong>and</strong> maximum cabling cost &mdash; both must be named.
          </div>
        </Section>

        {/* ══════════════ 4.6 ETHERNET ══════════════ */}
        <Section id="ethernet" title="4.6 Ethernet" completed={completed["ethernet"]} onToggle={() => toggle("ethernet")}>
          <p><strong>Ethernet</strong> is the dominant LAN protocol &mdash; the agreed format for how data is laid out on the wire so that any compliant switch and any compliant NIC can talk to each other. Ethernet defines the <strong>frame</strong> &mdash; the unit of data on a single LAN hop.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.6.1 The Ethernet frame</h3>
          <Table
            headers={["Field", "Bytes", "Purpose"]}
            rows={[
              ["Destination MAC", "6", "The MAC address of the intended recipient on this LAN"],
              ["Source MAC", "6", "The MAC address of the sender"],
              ["VLAN tag (optional)", "4", "The VLAN this frame belongs to, if VLANs are used"],
              ["EtherType / length", "2", "What kind of payload is inside (IPv4, IPv6, ARP, …)"],
              ["Payload", "46–1500", "The IP packet (or ARP message) being carried"],
              ["Frame Check Sequence (FCS)", "4", "A checksum so the receiver can detect a corrupted frame"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.6.2 Headers and payload</h3>
          <KeyTerm term="Header">The bookkeeping fields at the front of a frame or packet: who it is to, who it is from, what's inside, how long it is.</KeyTerm>
          <KeyTerm term="Payload">The actual data being carried (e.g. part of a web request); the cargo, as opposed to the headers (the labelling).</KeyTerm>
          <KeyTerm term="Frame check sequence (FCS)">A checksum at the end of an Ethernet frame. The receiver recomputes it; if it doesn't match, the frame is discarded as corrupted (likely from EMI or attenuation). The data-link layer's defence against transmission errors.</KeyTerm>
          <KeyTerm term="VLAN (Virtual LAN)">A logical division of one physical LAN into multiple isolated networks. The <strong>VLAN tag</strong> added to a frame tells switches which VLAN it belongs to. Used to separate, for example, staff and student traffic on one switch.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.6.3 Frame vs Packet</h3>
          <p>The two units are easy to confuse but operate at different layers:</p>
          <Table
            headers={["", "Frame (data-link / Layer 2)", "Packet (network / Layer 3)"]}
            rows={[
              ["Carries", "An IP packet", "An application-layer payload (e.g. HTTP request)"],
              ["Addressed by", "MAC addresses (source + destination)", "IP addresses (source + destination)"],
              ["Lifetime", "One LAN hop — discarded by the next router", "End-to-end across many LANs / hops"],
              ["Devices that handle it", "Switches", "Routers"],
            ]}
          />

          <InfoBox title="A packet rides inside successive frames" color="blue">
            When a packet leaves your laptop, it is wrapped in an Ethernet frame (with your MAC + the router's MAC). The router strips that frame, looks at the IP, decides where to forward, and puts the same packet inside a <em>different</em> frame on the next hop. The packet survives end-to-end; the frames change every hop.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be able to <strong>list the main Ethernet frame fields</strong>: source MAC, destination MAC, payload, FCS (and VLAN tag if used). Be able to <strong>distinguish frame from packet</strong> using the <em>MAC vs IP</em> + <em>one hop vs end-to-end</em> contrast.
          </div>
        </Section>

        {/* ══════════════ 4.7 NETWORK ADDRESSING ══════════════ */}
        <Section id="network-addressing" title="4.7 Network Addressing" completed={completed["network-addressing"]} onToggle={() => toggle("network-addressing")}>
          <p>Three address types matter for Grade 11. They are <em>not</em> alternatives &mdash; they are layered, with each doing a different job.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.7.1 MAC address — the LAN identifier</h3>
          <KeyTerm term="MAC address">A 48-bit hardware address burned into every NIC at the factory. Unique per network card worldwide. Written as six hex pairs: <code>00:1A:2B:3C:4D:5E</code>. Used by switches to deliver a frame inside a LAN.</KeyTerm>
          <p className="mt-2 text-sm">A MAC address never changes (unless you spoof it). It identifies the NIC; it doesn't move with the user. Two laptops on the same Wi-Fi have different MACs.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.7.2 IPv4 vs IPv6</h3>
          <KeyTerm term="IPv4">A 32-bit network-layer address, written as four decimal octets: <code>192.168.1.5</code>. Total ≈ 4.3 billion addresses, exhausted at the global level. Still the most common LAN addressing.</KeyTerm>
          <KeyTerm term="IPv6">A 128-bit replacement for IPv4, written as eight 16-bit hex groups: <code>2001:0db8:85a3:0000:0000:8a2e:0370:7334</code>. Effectively unlimited address space (≈ 3.4 × 10³⁸). Adoption is partial, growing.</KeyTerm>

          <Table
            headers={["", "IPv4", "IPv6"]}
            rows={[
              ["Length", "32 bits", "128 bits"],
              ["Notation", "192.168.1.5", "2001:db8::1"],
              ["Address space", "≈ 4.3 × 10⁹", "≈ 3.4 × 10³⁸"],
              ["Status", "Exhausted globally; still dominant on LANs", "Replacement; partial deployment"],
              ["Configuration", "Often manual or via DHCP", "Often automatic (SLAAC) or DHCPv6"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.7.3 Why both address types — MAC and IP?</h3>
          <p>A MAC address is fixed to the hardware; an IP address is assigned by software (often by DHCP) and reflects <em>where the device currently is</em> on the network. Both are needed:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>MAC</strong> tells the switch <em>which port on this LAN</em> to deliver the frame to.</li>
            <li><strong>IP</strong> tells the routers along the way <em>which network</em> the destination is on, possibly across the planet.</li>
            <li>The two are bridged by <strong>ARP</strong> (next section).</li>
          </ul>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be able to <strong>distinguish MAC and IPv4</strong> by their <em>length, who assigns them, what layer they live at, and what device uses them</em>. Know that <strong>IPv6 = 128 bits</strong>, written as hex groups.
          </div>
        </Section>

        {/* ══════════════ 4.8 INTERNET PROTOCOLS ══════════════ */}
        <Section id="internet-protocols" title="4.8 Internet Protocols" completed={completed["internet-protocols"]} onToggle={() => toggle("internet-protocols")}>
          <p>Four protocols make addressing actually work on a real LAN. They are introduced here at LAN scale; LU5 / LU6 expand on the WAN-side protocols (TCP, UDP, HTTP, SMTP, &hellip;).</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.8.1 DNS — Names → IP addresses</h3>
          <KeyTerm term="DNS (Domain Name System)">The global service that translates human-friendly names (<code>www.helpmekaar.co.za</code>) into IP addresses (<code>196.36.250.45</code>). Without DNS you would have to memorise IP addresses to use the Internet.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.8.2 DHCP — Hands out IP addresses</h3>
          <KeyTerm term="DHCP (Dynamic Host Configuration Protocol)">The LAN service that hands out IP addresses to nodes when they join the network, on a <strong>lease</strong>. Why your laptop just &ldquo;works&rdquo; on Wi-Fi without you typing in an IP. The DHCP server is usually built into the router.</KeyTerm>
          <p className="mt-2 text-sm">DHCP also gives the laptop other useful pieces: the <strong>gateway</strong> address (where to send traffic that's leaving the LAN), the <strong>DNS server</strong> address, and the <strong>subnet mask</strong>. Everything you need to talk to the rest of the world.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.8.3 ARP — IP → MAC on the LAN</h3>
          <KeyTerm term="ARP (Address Resolution Protocol)">The LAN-side mechanism that maps an <strong>IP address</strong> back to a <strong>MAC address</strong>. Before sending a frame to <code>192.168.1.5</code>, your computer broadcasts <em>&ldquo;who has 192.168.1.5?&rdquo;</em>; the owner replies with its MAC; the sending computer caches the result and uses it.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.8.4 TCP/IP — the umbrella</h3>
          <p><strong>TCP/IP</strong> is the protocol <em>suite</em> that powers the Internet: a collection of protocols centred on IP and TCP. Saying &ldquo;TCP/IP&rdquo; is shorthand for &ldquo;Internet networking&rdquo;. The members covered in this LU and LU5 include:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>IP</strong> &mdash; addressing and routing (LAN-side covered here, WAN-side in LU5).</li>
            <li><strong>ARP / DHCP / DNS</strong> &mdash; the addressing helpers covered above.</li>
            <li><strong>TCP / UDP</strong> &mdash; transport (covered in LU5 as part of WAN protocols).</li>
            <li><strong>HTTP, HTTPS, SMTP, IMAP, POP3, FTP</strong> &mdash; application protocols (LU5 / LU6).</li>
          </ul>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.8.5 Worked example — a laptop opens helpmekaar.co.za</h3>
          <Table
            headers={["#", "What happens", "Protocol"]}
            rows={[
              ["1", "Laptop's NIC associates with the AP for 'Helpmekaar-Students'.", "Wi-Fi (radio link)"],
              ["2", "Laptop has no IP. It broadcasts a DHCP Discover.", "DHCP"],
              ["3", "Router's DHCP server replies: 'have 10.0.0.42, lease 24h, gateway 10.0.0.1, DNS 8.8.8.8'.", "DHCP"],
              ["4", "Laptop accepts the lease — it now has an IPv4 address.", "DHCP"],
              ["5", "User types helpmekaar.co.za. Laptop asks DNS server '8.8.8.8' for the IP.", "DNS"],
              ["6", "DNS replies: 196.36.250.45.", "DNS"],
              ["7", "Laptop wants to send to 196.36.250.45 — outside the LAN. Frame goes to the gateway (10.0.0.1).", "IP routing"],
              ["8", "Laptop doesn't yet know the router's MAC. ARP: 'who has 10.0.0.1?'", "ARP"],
              ["9", "Router replies with its MAC.", "ARP"],
              ["10", "Laptop sends the HTTP request: dest MAC = router's, dest IP = 196.36.250.45.", "Ethernet + IP"],
            ]}
          />
          <p className="mt-2 text-sm italic text-slate-600">Five separate addressing mechanisms cooperated to load one webpage. Each does a small, well-defined job.</p>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be able to <strong>state the role of DHCP, DNS, and ARP</strong> in one sentence each. Examiners ask scenario questions: <em>&ldquo;a laptop joins the Wi-Fi and tries to load a page &mdash; explain the role of DHCP and ARP&rdquo;</em> is a perennial.
          </div>
        </Section>

        {/* ══════════════ 4.9 LAN DEVICES ══════════════ */}
        <Section id="lan-devices" title="4.9 LAN Devices" completed={completed["lan-devices"]} onToggle={() => toggle("lan-devices")}>
          <p>Five wired devices to know. They differ by <em>what they connect</em> and <em>how they decide where to forward traffic</em>.</p>

          <KeyTerm term="NIC (Network Interface Card)">The hardware that lets a device connect to the network. Has a unique factory-burned <strong>MAC address</strong>. Built into laptops/desktops/servers; sometimes USB or PCIe add-in.</KeyTerm>
          <KeyTerm term="Hub (legacy, for comparison only)">Repeats every signal it receives to every port. No intelligence; cannot tell which port a packet should go to. Replaced by switches in the 2000s.</KeyTerm>
          <KeyTerm term="Switch">Connects multiple devices in a LAN. <em>Learns</em> which MAC address is on which port and forwards each frame <strong>only to the destination port</strong>. Operates at the data-link layer (Layer 2 in the OSI model).</KeyTerm>
          <KeyTerm term="Bridge">Connects two LAN segments together so they behave as one. Like a switch with two ports. Mostly absorbed into modern multi-port switches; still used conceptually to describe joining two media types (e.g. wireless-to-wired bridge).</KeyTerm>
          <KeyTerm term="Router">Connects two or more <em>different</em> networks (e.g. your LAN to the Internet) and decides where to forward each packet based on its <strong>IP address</strong>. Operates at the network layer (Layer 3 in OSI).</KeyTerm>

          <Table
            headers={["Device", "Forwards based on", "OSI Layer", "Inside one LAN, or between LANs?"]}
            rows={[
              ["NIC", "n/a — it is the endpoint", "1+2", "Endpoint"],
              ["Hub", "Nothing — broadcasts everything", "1", "Inside one LAN"],
              ["Switch", "MAC address", "2", "Inside one LAN"],
              ["Bridge", "MAC address (for two segments)", "2", "Joining two LAN segments"],
              ["Router", "IP address", "3", "Between two or more networks"],
            ]}
          />

          <InfoBox title="Hub vs Switch — why hubs are obsolete" color="orange">
            A hub repeats every frame to every port. On a busy LAN, every device sees every other device's traffic &mdash; collisions are common, eavesdropping is trivial, and bandwidth is shared by everyone. A switch sends each frame only to the destination port. Each port gets full bandwidth; collisions vanish; eavesdropping requires deliberate tools. The 2000s replaced every hub with a switch.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be able to <strong>distinguish switch / bridge / router</strong> in one sentence each, naming <em>what each connects</em> and <em>how each forwards</em>. Examiners reward the &ldquo;forwards based on&rdquo; column shape over abstract definitions.
          </div>
        </Section>

        {/* ══════════════ 4.10 OSI MODEL ══════════════ */}
        <Section id="osi-model" title="4.10 OSI Model" completed={completed["osi-model"]} onToggle={() => toggle("osi-model")}>
          <p>The <strong>OSI (Open Systems Interconnection) model</strong> is the seven-layer reference framework that organises everything in this LU into a tidy stack. Each layer uses the one below it and provides a service to the one above it. Real protocols don't always fit one layer cleanly; the model is conceptual but useful.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.10.1 The seven layers</h3>
          <Table
            headers={["#", "Layer", "What it does", "Examples"]}
            rows={[
              ["7", "Application", "User-visible protocols — web, email, file transfer, name lookup", "HTTP, HTTPS, SMTP, IMAP, POP3, FTP, DNS"],
              ["6", "Presentation", "Encoding, compression, encryption (where the layered model puts TLS)", "TLS/SSL, JPEG, MP3"],
              ["5", "Session", "Sets up, manages, and tears down conversations between two endpoints", "NetBIOS, RPC"],
              ["4", "Transport", "Reliability, ordering, multiplexing by port", "TCP, UDP"],
              ["3", "Network", "Packets, IP addresses, routing", "IP, ARP, ICMP"],
              ["2", "Data link", "Frames, MAC addresses, FCS error detection", "Ethernet, Wi-Fi (802.11)"],
              ["1", "Physical", "Cables, radio, voltages, light pulses — raw bits on the medium", "UTP, fibre, Wi-Fi radios"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.10.2 Memory aid</h3>
          <p className="text-sm"><em>&ldquo;<strong>Please Do Not Throw Sausage Pizza Away</strong>&rdquo;</em> &mdash; Physical, Data link, Network, Transport, Session, Presentation, Application. (From the bottom up. Pick whichever direction sticks.)</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.10.3 Where each device sits</h3>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>Hub / cable / repeater</strong> &mdash; <em>Layer 1 (Physical)</em>.</li>
            <li><strong>Switch / bridge</strong> &mdash; <em>Layer 2 (Data link)</em>. Reads MAC addresses.</li>
            <li><strong>Router</strong> &mdash; <em>Layer 3 (Network)</em>. Reads IP addresses.</li>
            <li><strong>Firewall (basic)</strong> &mdash; usually <em>Layer 3 / 4</em>; modern next-gen firewalls reach <em>Layer 7</em>.</li>
          </ul>

          <InfoBox title="Why a layered model helps" color="green">
            Each layer hides the messiness of the one below. The application-layer programmer doesn't think about voltages on a wire; the data-link engineer doesn't think about HTTP. Standardised layers also let you mix and match &mdash; the same Ethernet frame can carry IPv4 or IPv6; the same TCP can run over Ethernet, Wi-Fi, or fibre.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Memorise the <strong>seven layers in order</strong>. Know <strong>which layer each device operates at</strong> &mdash; switch = 2, router = 3 is the common exam target. The mnemonic helps under exam pressure.
          </div>
        </Section>

        {/* ══════════════ 4.11 WIRELESS LAN ══════════════ */}
        <Section id="wireless-lan" title="4.11 Wireless LAN" completed={completed["wireless-lan"]} onToggle={() => toggle("wireless-lan")}>
          <p>The wireless side of a modern LAN is built from three device types and a handful of operational concepts. Most of the protocol stack (frames, addresses, ARP, DHCP) is the same as wired Ethernet &mdash; the difference is the physical layer and the encryption.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.11.1 Wireless devices</h3>
          <KeyTerm term="Wireless access point (WAP / AP)">A device that lets Wi-Fi clients join a wired LAN. Plugs into the LAN by cable; broadcasts a Wi-Fi network. Usually a separate device in larger sites; built into the home router in domestic settings.</KeyTerm>
          <KeyTerm term="Wireless bridge">A device that links two separate wired LANs over a wireless link (e.g. between two buildings without a fibre run between them). Acts as a bridge; the wireless hop is invisible to the rest of the network.</KeyTerm>
          <KeyTerm term="Wireless router">The consumer-grade box that combines four roles: <strong>router</strong> (LAN ↔ Internet), <strong>switch</strong> (a few wired LAN ports), <strong>wireless access point</strong> (Wi-Fi), and <strong>firewall</strong>. The &ldquo;Wi-Fi router&rdquo; you have at home.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.11.2 Operational concepts</h3>
          <KeyTerm term="SSID (Service Set Identifier)">The human-readable name of a Wi-Fi network (e.g. <code>Helpmekaar-Staff</code>). What you pick when joining Wi-Fi.</KeyTerm>
          <KeyTerm term="Hotspot">A temporary or public Wi-Fi access point &mdash; the airport, the coffee shop, your phone tethering its mobile data to a laptop. Same Wi-Fi technology; different operational context (often shared, often unencrypted, so eavesdropping risk is higher).</KeyTerm>
          <KeyTerm term="Backbone">The high-bandwidth, long-distance core of a network. Inside a campus this is usually fibre between buildings; on the Internet it is fibre between cities and continents.</KeyTerm>
          <KeyTerm term="Repeater">A device that receives a weakening signal and re-transmits it at full strength. Extends a cable or wireless link beyond its native reach. A Wi-Fi extender is a repeater for wireless.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">4.11.3 Wired vs Wi-Fi — operational comparison</h3>
          <Table
            headers={["Aspect", "Wired Ethernet", "Wi-Fi"]}
            rows={[
              ["Bandwidth", "1 Gbps – 10 Gbps stable per port", "100 Mbps – 9 Gbps shared per AP"],
              ["Latency", "Lower; very stable", "Higher; varies with congestion"],
              ["Mobility", "None — tethered to the cable", "High — roam in range"],
              ["Security exposure", "Physical access required", "Anyone in radio range; encryption (WPA3/WPA2) is the defence"],
              ["Best for…", "Server links, fixed workstations", "Laptops, phones, classrooms full of mobile devices"],
            ]}
          />

          <InfoBox title="Wi-Fi 'speeds' vs reality" color="orange">
            Modern Wi-Fi (Wi-Fi 6/6E/7) advertises gigabit speeds, but in practice cabled Ethernet has lower latency, far less interference, and more stable throughput. For a server you cable; for a laptop you Wi-Fi; for a desktop at a fixed desk you usually cable. Each medium where it wins.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be able to <strong>distinguish WAP, wireless bridge, and wireless router</strong>. Know that the home Wi-Fi box <em>combines</em> router + switch + AP + firewall &mdash; this is the most common exam-time confusion. Know that <strong>encryption</strong> (WPA2/WPA3), not the SSID, is what actually protects Wi-Fi.
          </div>
        </Section>

        {/* ══════════════ LU4 PRACTICE EXERCISES ══════════════ */}
        <Section id="exercises-lu4" title="LU4 Practice Exercises" completed={completed["exercises-lu4"]} onToggle={() => toggle("exercises-lu4")}>
          <p className="mb-4 text-sm italic text-slate-600">
            Work through each exercise on paper first, then check your answer. These exercises cover all Learning Unit 4 topics. Where a question is paraphrased from a past paper, the source is noted.
          </p>

          <div className="space-y-6 text-sm">
            <div>
              {/* Source: ieb_it_2023_p2 Q1.3 — paraphrased */}
              <p className="font-semibold">Exercise 1 &mdash; Match the term (Broadcast)</p>
              <p className="text-sm mt-1">Which term describes <em>a form of network transmission where packets are sent to all nodes on the network rather than to a specific device</em>? <em>(1 mark)</em></p>
              <Spoiler>
{`Broadcast.

A broadcast frame is addressed to every node on
the LAN. Switches by default flood broadcast
frames out every port. Used by ARP ('who has
this IP?') and by DHCP discovery.`}
              </Spoiler>
            </div>

            <div>
              {/* Source: ieb_it_2023_p2 Q1.6 — paraphrased */}
              <p className="font-semibold">Exercise 2 &mdash; Match the term (Payload)</p>
              <p className="text-sm mt-1">Which term describes <em>the portion of a packet which contains the data being transmitted</em>? <em>(1 mark)</em></p>
              <Spoiler>
{`Payload.

The payload is the actual data being carried —
the cargo. The other parts (header, FCS, etc.)
are bookkeeping that label the cargo. The payload
of an Ethernet frame is typically an IP packet;
the payload of an IP packet is typically a TCP
or UDP segment.`}
              </Spoiler>
            </div>

            <div>
              {/* Source: ieb_it_2023_p2 Q4.1 — paraphrased */}
              <p className="font-semibold">Exercise 3 &mdash; What does DHCP do?</p>
              <p className="text-sm mt-1">DHCP is a network protocol. Which of the following BEST describes what it does? <em>(1 mark)</em></p>
              <ul className="list-[upper-alpha] list-inside space-y-1 mt-1 ml-2">
                <li>It sets up transmission security.</li>
                <li>It allocates IP addresses to devices.</li>
                <li>It allows data transfer on mobile networks.</li>
                <li>It links URLs and IP addresses.</li>
              </ul>
              <Spoiler>
{`B — DHCP allocates IP addresses to devices.

DHCP (Dynamic Host Configuration Protocol) hands
out IP addresses to nodes when they join the
network, on a lease. It also gives the node the
gateway address, the DNS server address, and the
subnet mask — everything needed to talk to the
rest of the world.

Distractors:
A — that's encryption (WPA3 / TLS).
C — that's cellular tech (3G/4G/5G).
D — that's DNS.`}
              </Spoiler>
            </div>

            <div>
              {/* Source: ieb_it_2023_p2 Q4.3 — paraphrased */}
              <p className="font-semibold">Exercise 4 &mdash; What is a MAC address?</p>
              <p className="text-sm mt-1">Which of the following is TRUE about MAC addresses? <em>(1 mark)</em></p>
              <ul className="list-[upper-alpha] list-inside space-y-1 mt-1 ml-2">
                <li>They uniquely identify network cards.</li>
                <li>They uniquely identify nodes on a network.</li>
                <li>They uniquely identify switch ports.</li>
                <li>None of the above.</li>
              </ul>
              <Spoiler>
{`A — MAC addresses uniquely identify NETWORK
CARDS (NICs).

A MAC is a 48-bit hardware address burned into
each NIC at the factory. A node may have several
NICs (wired + Wi-Fi + Bluetooth) — each with its
own MAC. So 'identifies the NIC' is exact; 'the
node' is loose; 'the switch port' is wrong (the
port has no MAC; it just learns which MAC is on
it).`}
              </Spoiler>
            </div>

            <div>
              {/* Source: ieb_it_2023_p2 Q1.9 — paraphrased */}
              <p className="font-semibold">Exercise 5 &mdash; Match the term (HTTPS)</p>
              <p className="text-sm mt-1">Which term describes <em>a secure data transmission protocol for viewing Internet pages with a web browser</em>? <em>(1 mark)</em></p>
              <Spoiler>
{`HTTPS (Hypertext Transfer Protocol Secure).

HTTPS is HTTP wrapped in SSL/TLS encryption.
The padlock in the browser address bar. Protects
against eavesdropping and tampering on the path
between browser and server. Today essentially
every login page, banking site, and serious
website uses HTTPS.`}
              </Spoiler>
            </div>

            <div>
              {/* Source: ieb_it_2023_p2 Q5.1.1 — paraphrased */}
              <p className="font-semibold">Exercise 6 &mdash; Function of a wireless access point</p>
              <p className="text-sm mt-1">What is the function of a <em>wireless access point (WAP)</em> on a LAN? <em>(2 marks)</em></p>
              <Spoiler>
{`A wireless access point (WAP) lets Wi-Fi clients
(laptops, phones) join the wired LAN. (1)

The WAP is itself plugged into the LAN by a
cable; it broadcasts a Wi-Fi network (SSID) and
forwards traffic between the wireless clients
and the wired infrastructure. The wireless
hop is invisible to the rest of the network. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Source: ieb_it_2023_p2 Q5.1.2 — paraphrased */}
              <p className="font-semibold">Exercise 7 &mdash; Wireless-only — advantage and disadvantage (L2)</p>
              <p className="text-sm mt-1">State ONE <strong>advantage</strong> and ONE <strong>disadvantage</strong> of laptops and mobile devices using <em>only wireless</em> connections. The two answers may not be opposites of each other. <em>(2 marks)</em></p>
              <Spoiler>
{`Sample answers (others defensible — 1 mark each).

Advantage (any 1):
• MOBILITY — users can roam anywhere within
  range without unplugging.
• Cheaper to deploy than running a cable to
  every desk.
• Easier to extend — add an AP rather than pull
  more cable.

Disadvantage (any 1, not the opposite of the
chosen advantage):
• Bandwidth and latency vary with signal strength
  and the number of users on the AP.
• Vulnerable to eavesdropping; encryption (WPA3)
  is the only real defence.
• Subject to interference from walls, microwaves,
  neighbours' Wi-Fi.

(Examiner rule: 'mobility' and 'tethered to a
cable' would be opposites; 'mobility' and
'eavesdropping risk' are not.)`}
              </Spoiler>
            </div>

            <div>
              {/* Source: ieb_it_2023_p2 Q5.1.3 — paraphrased */}
              <p className="font-semibold">Exercise 8 &mdash; Topology recommendation (L2)</p>
              <p className="text-sm mt-1">A school's network design has every workstation connected by its own cable back to a central switch. <strong>Which topology</strong> does this network use? Justify your answer with ONE reason that highlights this topology's characteristic. <em>(3 marks)</em></p>
              <Spoiler>
{`STAR topology. (1)

Justification (any 1, with the reasoning):
• Every node has its own dedicated cable to a
  central device — that is the defining
  characteristic of a star. (1)
• Failure of one cable affects only that node;
  the rest of the LAN keeps working. (1)
• Easy to extend — adding a new workstation
  is a single cable into a free switch port. (1)

Trap: 'hybrid' is sometimes a defensible answer
when the question shows multiple stars connected
together. The simplest one-switch arrangement
is just a star.`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — combines material from §4.9 — no past-paper question on this exact distinction */}
              <p className="font-semibold">Exercise 9 &mdash; Distinguish Switch / Bridge / Router</p>
              <p className="text-sm mt-1">Distinguish between a <em>switch</em>, a <em>bridge</em>, and a <em>router</em>. For each, state <strong>what it connects</strong> and <strong>how it forwards traffic</strong>. <em>(6 marks)</em></p>
              <Spoiler>
{`2 marks each (1 for what it connects; 1 for
how it forwards):

• SWITCH — connects multiple devices inside a
  single LAN. (1)
  Forwards each frame ONLY to the destination
  port, by looking up its MAC address. Operates
  at OSI Layer 2. (1)

• BRIDGE — connects TWO LAN SEGMENTS so they
  appear as one. (1)
  Conceptually a two-port switch; forwards
  frames between the segments by MAC address.
  Operates at OSI Layer 2. (1)

• ROUTER — connects two or more DIFFERENT
  networks (e.g. a LAN to the Internet). (1)
  Forwards each packet by IP address, looking
  up the destination network in its routing
  table. Operates at OSI Layer 3. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — recommendation question, design-doc §6 L3 target */}
              <p className="font-semibold">Exercise 10 &mdash; Recommend a network for a small office (L3)</p>
              <p className="text-sm mt-1">A start-up rents a small open-plan office (≈ 30 staff, one floor). Recommend a <strong>topology</strong>, a <strong>cabling type</strong>, the <strong>network devices</strong> needed, and how to provide <strong>Wi-Fi</strong> for visitors. Justify each choice. <em>(8 marks)</em></p>
              <Spoiler>
{`Sample answer (others defensible).

Topology — STAR. (2)
• Every workstation connects to a central
  switch; if one cable fails only that user is
  affected; easy to expand by adding ports.

Cabling — UTP (Cat 6 or Cat 6a). (2)
• All runs are well within 100 m on one floor;
  UTP is cheap, fast (1–10 Gbps), and easy to
  terminate.

Devices needed: (2 — half mark per item, any 4)
• NIC in each workstation (built in).
• ONE SWITCH with enough ports for all 30
  workstations.
• ONE ROUTER to connect the office LAN to
  the ISP.
• FIREWALL in the router to filter inbound
  Internet traffic.

Wi-Fi for visitors — wireless access point
on the LAN. (2)
• Cable a separate AP into the switch (more
  reliable than a consumer 'all-in-one' router
  for an office of 30).
• Configure two SSIDs:
   - STAFF on WPA3, full network access.
   - VISITOR on an isolated VLAN, internet
     only — no access to staff resources.

Award full marks for any defensible scheme that
correctly names the topology, cabling, devices,
and a security-aware Wi-Fi plan.`}
              </Spoiler>
            </div>
          </div>
        </Section>

        {/* ══════════════════════════════════════════════════════════════════════
             LEARNING UNIT 5 — WIDE AREA NETWORKS
             ══════════════════════════════════════════════════════════════════════ */}

        <div className="mt-12 mb-8 pt-6 border-t-2 border-amber-400">
          <h2 className="text-2xl font-bold text-amber-800 text-center" style={{ fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" }}>Learning Unit 5 — Wide Area Networks</h2>
          <p className="text-center text-sm text-slate-500 mt-1">Backbone · WAN architecture · Gateways · Long-distance transmission · Connection tech · Application protocols</p>
        </div>

        {/* ══════════════ 5.1 FIBRE OPTIC BACKBONE ══════════════ */}
        <Section id="wan-backbone" title="5.1 Fibre Optic Backbone" completed={completed["wan-backbone"]} onToggle={() => toggle("wan-backbone")}>
          <p>LU4 covered what happens <em>inside</em> a single building or site &mdash; a LAN. This LU is about what happens when the data has to <em>leave</em> that building and reach another city, country, or continent. The infrastructure that makes that possible is the <strong>backbone</strong> &mdash; mostly high-bandwidth fibre &mdash; and the rules that govern long-distance traffic are <strong>protocols</strong>.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.1.1 The Internet as a network of networks</h3>
          <p>The Internet is not a single network. It is a <em>network of networks</em>: every ISP runs its own backbone, and they peer with each other at exchange points so traffic can cross between them. Your packet from Helpmekaar to a server in Cape Town might cross 3&ndash;5 different ISPs' networks before it arrives.</p>

          <KeyTerm term="Internet">The largest WAN: a global network of networks connected via standard protocols (mainly TCP/IP). Owned and operated by no single party; comprises ISPs, content providers, and end users.</KeyTerm>
          <KeyTerm term="ISP (Internet Service Provider)">The company that sells you Internet access (Vodacom, MTN, Telkom, RAIN, Afrihost, Frogfoot). The ISP terminates your last-mile connection and forwards traffic to and from the rest of the Internet.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.1.2 Backbone vs last mile</h3>
          <p>Long-distance Internet traffic moves through two very different parts of the network:</p>

          <KeyTerm term="Backbone">The high-bandwidth core of a WAN. Inside a country, fibre cables between cities; between continents, undersea fibre cables. The backbone moves enormous traffic volumes &mdash; typically Gbps to Tbps per fibre pair.</KeyTerm>
          <KeyTerm term="Last mile">The final physical link from the ISP's network to the customer &mdash; fibre to the home, copper for ADSL, cellular for mobile, coax for cable. Often the slowest part of the path.</KeyTerm>

          <InfoBox title="Where the bottlenecks live" color="blue">
            The backbone is fast. Undersea cables between continents now carry hundreds of <strong>terabits per second</strong>. Inside SA, fibre rings between Johannesburg, Cape Town, and Durban are similarly high-capacity. <strong>Speed bottlenecks live almost entirely in the last mile</strong> &mdash; the cellular tower that's congested, the ADSL line on old copper, the home Wi-Fi router that's the wrong side of a brick wall. That's where most user-felt slowness comes from, not the backbone.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.1.3 Why fibre dominates the backbone</h3>
          <p>Three properties make fibre the only reasonable choice for long-distance backhaul:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>Enormous bandwidth.</strong> One pair of fibre strands carries 100 Gbps to 10+ Tbps in modern systems &mdash; orders of magnitude beyond copper.</li>
            <li><strong>Long range without amplification.</strong> Fibre signals attenuate slowly &mdash; tens of kilometres between repeaters underground; thousands of kilometres for undersea cables with widely-spaced amplifiers.</li>
            <li><strong>Immunity to electrical interference.</strong> Light, not electricity, so EMI from power lines or lightning doesn't affect the signal.</li>
          </ul>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>distinguish backbone from last mile</strong> and explain why bottlenecks usually live in the last mile. Know that the Internet is <strong>a network of networks</strong>, not a single owned thing.
          </div>
        </Section>

        {/* ══════════════ 5.2 WAN ══════════════ */}
        <Section id="wan-overview" title="5.2 WAN" completed={completed["wan-overview"]} onToggle={() => toggle("wan-overview")}>
          <p>The defining feature of a WAN is <strong>geographic scale</strong> &mdash; it spans more than one site, often more than one city, sometimes more than one continent.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.2.1 Definition and scope</h3>
          <KeyTerm term="WAN (Wide Area Network)">A network that spans multiple physical sites &mdash; different cities or countries. May be private (a company's own links between branches) or public (the Internet itself).</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.2.2 WAN vs LAN</h3>
          <Table
            headers={["Aspect", "LAN", "WAN"]}
            rows={[
              ["Scale", "One building or site", "Cities, countries, continents"],
              ["Owner", "Usually one organisation (the school, the company)", "Many organisations (ISPs) interconnected"],
              ["Media", "Ethernet cable, Wi-Fi inside the building", "Fibre, cellular, satellite, microwave"],
              ["Bandwidth", "1 Gbps to 10 Gbps typical", "From a few Mbps last-mile to Tbps backbone"],
              ["Latency", "Sub-millisecond inside the LAN", "Milliseconds to hundreds of ms across the WAN"],
              ["Speed bottleneck", "Usually the wired-Wi-Fi boundary", "Usually the last-mile link"],
              ["Examples", "School computer lab, office network", "The Internet; a private corporate WAN linking branches"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.2.3 Public vs private WANs</h3>
          <KeyTerm term="Public WAN">The Internet &mdash; open, shared, used by anyone. Traffic is visible to ISPs along the path; encryption (HTTPS, VPNs) is what makes it usable for sensitive data.</KeyTerm>
          <KeyTerm term="Private WAN">A WAN owned (or rented) by a single organisation &mdash; private fibre between branches, MPLS lines, or VPN tunnels over the public Internet. Used when a company wants its own controlled, separate path.</KeyTerm>

          <p className="mt-3 text-sm">A school typically uses the <em>public</em> Internet for everything. A bank or a multi-branch retailer often runs <em>private</em> WAN links between data centres, with VPN backup over the Internet.</p>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Know <strong>WAN is defined by geography and scale</strong>, not by being public. Be ready to give <strong>two specific differences</strong> between WAN and LAN. Know that the Internet is the largest example of a WAN but not the only kind.
          </div>
        </Section>

        {/* ══════════════ 5.3 DEVICES & GATEWAYS ══════════════ */}
        <Section id="wan-gateways" title="5.3 Devices & Gateways" completed={completed["wan-gateways"]} onToggle={() => toggle("wan-gateways")}>
          <p>The device that bridges your LAN to the WAN is called a <strong>gateway</strong>. In practice this is a <strong>router</strong> &mdash; the same kind of device discussed in LU4 §4.9 &mdash; configured with a route to the Internet and an ISP-assigned public IP address.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.3.1 Gateway, router, and the home combo box</h3>
          <KeyTerm term="Gateway">The device that connects one network (your LAN) to another (the WAN / Internet). Usually a router; the term &ldquo;gateway&rdquo; emphasises its role of bridging two different networks.</KeyTerm>

          <p className="mt-3">In a home or small office, the device on the wall is rarely just a router &mdash; it's a <strong>combo</strong> that combines four roles in one box:</p>
          <Table
            headers={["Role", "What it does"]}
            rows={[
              ["Modem", "Converts between the ISP's last-mile signal (ADSL on copper, fibre signal on glass) and standard Ethernet."],
              ["Router", "Routes traffic between the LAN and the Internet. Holds the public IP given by the ISP; performs NAT for the LAN's private IPs."],
              ["Switch", "A handful of wired Ethernet ports for plugging in PCs, printers, set-top boxes."],
              ["Wireless access point", "Broadcasts the home's Wi-Fi network (the SSID staff and visitors connect to)."],
              ["Firewall (basic)", "Blocks unsolicited inbound traffic from the Internet by default."],
            ]}
          />

          <KeyTerm term="Combo home router">The single box at home that combines modem, router, switch, Wi-Fi access point, and basic firewall. Sold as &ldquo;ADSL router&rdquo; or &ldquo;fibre router&rdquo;. In a school, those roles are usually split across separate, more capable devices.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.3.2 Why a school's setup is different</h3>
          <p>In a school the same five roles still exist, but they're usually split across <em>separate</em> devices for capacity, security, and manageability:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>An ONT</strong> (optical network terminal) at the fibre handoff &mdash; the modem equivalent for fibre.</li>
            <li><strong>A dedicated business router/firewall</strong> &mdash; manages traffic, enforces firewall policy, runs the school's VPN.</li>
            <li><strong>Managed switches</strong> &mdash; multiple, bigger, with VLAN support.</li>
            <li><strong>Multiple wireless access points</strong> &mdash; one combo box can't cover a multi-building campus.</li>
          </ul>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Define <strong>gateway</strong> and explain why the home box combines several roles. Be ready to <strong>list the four or five roles</strong> a home router/Wi-Fi box performs.
          </div>
        </Section>

        {/* ══════════════ 5.4 LONG-DISTANCE TRANSMISSION ══════════════ */}
        <Section id="wan-transmission" title="5.4 Long-distance Transmission" completed={completed["wan-transmission"]} onToggle={() => toggle("wan-transmission")}>
          <p>Long-distance traffic uses different physical media to LAN traffic. Three media dominate: <strong>backbone fibre</strong>, <strong>satellite</strong>, and <strong>microwave</strong>.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.4.1 Backbone fibre (recap from §5.1)</h3>
          <p>Already covered &mdash; high bandwidth, long range, EMI immunity. The default for any new long-distance link where digging or laying cable is feasible.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.4.2 Satellite</h3>
          <KeyTerm term="Satellite Internet (geostationary)">Customer has a dish; signal goes up to a satellite at 36 000 km altitude and down to a ground station connected to the Internet. Bandwidth 5&ndash;50 Mbps. <strong>Latency ~600 ms round-trip from the distance alone</strong> &mdash; feels sluggish for interactive use.</KeyTerm>
          <KeyTerm term="Satellite Internet (LEO / Starlink)">Low-earth-orbit satellites at ~550 km. Bandwidth 50&ndash;250 Mbps; latency ~30&ndash;50 ms (much closer to fibre). The current state of the art for rural areas with no fibre or cellular.</KeyTerm>

          <InfoBox title="Why distance dominates satellite latency" color="orange">
            The latency difference between geostationary (~600 ms) and LEO (~30 ms) is almost entirely about distance. 36 000 km up and back at the speed of light is ~240 ms minimum &mdash; before the ground station, ISP, and destination server are added in. 550 km up and back is ~3.7 ms minimum, leaving plenty of budget for everything else.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.4.3 Microwave</h3>
          <KeyTerm term="Microwave link">Long-range point-to-point wireless between fixed antennas. Bandwidth 100 Mbps to 10 Gbps over distances up to ~50 km. Used by ISPs to connect a tower in one neighbourhood to backhaul fibre several kilometres away.</KeyTerm>

          <p className="mt-3 text-sm">Three constraints distinguish microwave from satellite:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>Line of sight required.</strong> Both antennas must &ldquo;see&rdquo; each other. Trees, buildings, and the curvature of the Earth limit range.</li>
            <li><strong>Weather sensitivity.</strong> Heavy rain attenuates microwave signals (&ldquo;rain fade&rdquo;); heavy storms can drop the link.</li>
            <li><strong>Fixed antennas.</strong> Unlike cellular, microwave is point-to-point, not point-to-many; both ends are stationary.</li>
          </ul>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Know that <strong>geostationary satellite has ~600 ms latency from physics alone</strong>; LEO satellite is much better. Know that <strong>microwave needs line of sight and is weather-sensitive</strong>.
          </div>
        </Section>

        {/* ══════════════ 5.5 INTERNET CONNECTION TECHNOLOGY ══════════════ */}
        <Section id="wan-connection-tech" title="5.5 Internet Connection Technology" completed={completed["wan-connection-tech"]} onToggle={() => toggle("wan-connection-tech")}>
          <p>For a home, school, or office, the choice of <em>last-mile</em> technology determines what bandwidth, latency, and reliability you actually get. The SAG names the major options; understanding the trade-offs is the Gr11 skill.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.5.1 The major options</h3>
          <KeyTerm term="ADSL">Asymmetric Digital Subscriber Line &mdash; Internet over the copper telephone line. <strong>Asymmetric</strong> &mdash; download faster than upload (e.g. 10 Mbps down / 1 Mbps up). Cheap and widely deployed; being phased out as fibre rolls out.</KeyTerm>
          <KeyTerm term="VDSL">Very-high-bitrate DSL &mdash; faster successor to ADSL on copper; up to ~100 Mbps over short distances.</KeyTerm>
          <KeyTerm term="Fibre to the Home (FTTH)">Fibre-optic cable run all the way to the customer's premises. <strong>Symmetric</strong>, very high bandwidth (100 Mbps to 1 Gbps consumer plans), low latency, immune to electrical interference.</KeyTerm>
          <KeyTerm term="Cable Internet">Internet over the coax cable used by cable TV. Common in some countries; less so in South Africa.</KeyTerm>
          <KeyTerm term="Cellular (3G / 4G / 5G)">Cellular network access. Each generation roughly an order of magnitude faster and lower-latency than the previous. 3G is end-of-life in SA; 4G/LTE is ubiquitous; 5G is rolling out in cities.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.5.2 Comparison table</h3>
          <Table
            headers={["Technology", "Typical bandwidth", "Latency", "Mobility", "Best for"]}
            rows={[
              ["Fibre (FTTH)", "100 Mbps – 1 Gbps symmetric", "~5 ms to ISP", "None — fixed", "Permanent home or office; high-traffic users; video calls"],
              ["VDSL (copper)", "25–100 Mbps down", "~15 ms", "None", "Areas without fibre; lighter use"],
              ["ADSL (copper)", "4–20 Mbps down, ~1 Mbps up", "~25 ms", "None", "Legacy home access; backup link"],
              ["4G / LTE", "10–200 Mbps", "~30 ms", "High — moves with device", "Phones, mobile hotspots, fallback for fibre"],
              ["5G", "100 Mbps – 1+ Gbps", "~10 ms", "High", "High-bandwidth mobile; fixed-wireless replacement for fibre"],
              ["Satellite (LEO)", "50–250 Mbps", "~30–50 ms", "Semi-mobile", "Rural / remote with no fibre or cellular"],
              ["Satellite (geostationary)", "5–50 Mbps", "~600 ms", "None", "Last-resort connectivity; broadcast"],
              ["Microwave (point-to-point)", "100 Mbps – 10 Gbps", "Low", "None", "ISP backhaul; campus links without fibre"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.5.3 Bandwidth vs latency &mdash; the right axis matters</h3>
          <Table
            headers={["Workload", "Dominant axis", "Why"]}
            rows={[
              ["Downloading a large file", "Bandwidth", "How fast the bytes arrive overall."],
              ["Streaming video", "Sustained bandwidth", "Each second of video needs the next second of data in time."],
              ["Web browsing", "Mostly latency", "Many small requests; perceived speed is dominated by round-trips."],
              ["Live video call (Zoom)", "Latency + jitter", "Humans notice round-trips above ~150 ms; jitter audibly degrades quality."],
              ["Online gaming", "Latency", "Player input must reach the server fast; stale input loses fights."],
            ]}
          />

          <InfoBox title="Symmetric vs Asymmetric — when it matters" color="green">
            <strong>Asymmetric</strong> connections (ADSL, most cellular) prioritise download speed because most users <em>consume</em> more than they <em>send</em>. <strong>Symmetric</strong> (fibre, fixed-wireless 5G) gives equal upload &mdash; matters for video calls, hosting your own server, large file uploads, and cloud backup.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.5.4 The South African landscape</h3>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>Fibre coverage</strong> is fast in major suburbs and cities; many areas still have ADSL or 5G fixed-wireless as the realistic option.</li>
            <li><strong>5G rollout</strong> from MTN, Vodacom, RAIN is well underway in Joburg, Cape Town, Durban &mdash; competitive with mid-tier fibre plans.</li>
            <li><strong>Starlink</strong> is now operating in SA via roaming; reaches farms and townships outside fibre coverage.</li>
            <li><strong>3G shutdown</strong> by SA carriers is in progress &mdash; older devices will lose data first, then voice.</li>
          </ul>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>recommend a connection technology for a scenario</strong> with at least three justifying factors. Know <strong>fibre wins where it exists</strong>; <strong>5G fixed-wireless is a strong alternative</strong>; <strong>satellite (LEO) is the rural / no-coverage answer</strong>. Know <strong>latency vs bandwidth</strong> &mdash; which workload is dominated by which.
          </div>
        </Section>

        {/* ══════════════ 5.6 APPLICATION PROTOCOLS ══════════════ */}
        <Section id="app-protocols" title="5.6 Application Protocols" completed={completed["app-protocols"]} onToggle={() => toggle("app-protocols")}>
          <p>LU4 §4.8 covered the <em>plumbing</em> of the Internet &mdash; TCP/IP, ARP, DHCP, DNS. This section covers the <strong>application-layer</strong> protocols that ride on top &mdash; the ones you name when you say &ldquo;web&rdquo;, &ldquo;email&rdquo;, &ldquo;file transfer&rdquo;.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.6.1 Recap &mdash; TCP vs UDP</h3>
          <p>Application protocols sit on top of either TCP (reliable, ordered, connection-oriented) or UDP (fast, connectionless, no retries). Brief recap:</p>

          <KeyTerm term="TCP">Transmission Control Protocol. Reliable: establishes a connection, splits data into numbered segments, retransmits losses, reorders out-of-order packets, throttles when the network is congested. Used by web, email, file transfer.</KeyTerm>
          <KeyTerm term="UDP">User Datagram Protocol. Lightweight: sends individual datagrams with no connection setup, no retransmission, no ordering. Faster and lower-latency. Used where latency matters more than completeness &mdash; voice/video calls, online gaming, DNS queries.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.6.2 Web protocols &mdash; HTTP and HTTPS</h3>
          <KeyTerm term="HTTP (Hypertext Transfer Protocol)">The protocol web browsers and web servers use to exchange web pages. Plain text on the wire &mdash; no encryption. Port 80.</KeyTerm>
          <KeyTerm term="HTTPS (HTTP Secure)">HTTP wrapped in <strong>SSL/TLS</strong> encryption. The padlock in the browser address bar. Protects against eavesdropping and tampering on the path between browser and server. Port 443.</KeyTerm>
          <KeyTerm term="SSL / TLS">The encryption layer used by HTTPS (and many other protocols). SSL was the original; TLS is the modern successor. The terms are used interchangeably in everyday speech but TLS is what's actually deployed.</KeyTerm>

          <InfoBox title="Why HTTPS matters on a coffee-shop hotspot" color="orange">
            On open Wi-Fi, anyone with a packet sniffer can read every plain-HTTP login. With HTTPS, the browser and server agree on an encryption key before any data is sent. The <code>POST</code> with your username and password is <em>encrypted inside the TLS layer</em> before it leaves the browser; on the wire it is gibberish. Same Wi-Fi, same sniffer &mdash; but the captured frames are useless. <strong>Every login page, banking site, and serious app uses HTTPS today for exactly this reason.</strong>
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.6.3 Email protocols &mdash; SMTP, POP3, IMAP</h3>
          <KeyTerm term="SMTP (Simple Mail Transfer Protocol)">For <strong>sending</strong> email. Used by your mail client (Outlook, Thunderbird, the Gmail app) to push outgoing mail to your provider's outgoing mail server. Ports 25 / 587.</KeyTerm>
          <KeyTerm term="POP3 (Post Office Protocol v3)">For <strong>receiving</strong> email. Downloads messages from the server to the client and (traditionally) <strong>deletes them from the server</strong>. One device gets the mail; older single-device pattern. Port 110.</KeyTerm>
          <KeyTerm term="IMAP (Internet Message Access Protocol)">For <strong>receiving</strong> email. Keeps messages <strong>on the server</strong>; clients view them in place. Multiple devices stay synchronised &mdash; read on phone, also marked read on laptop. The dominant modern protocol. Port 143.</KeyTerm>

          <Table
            headers={["", "POP3", "IMAP"]}
            rows={[
              ["Where messages live", "Downloaded to one device; (usually) deleted from server", "On the server; client views them in place"],
              ["Multi-device sync", "No — only the device that downloaded sees the mail", "Yes — read-status, folders, drafts sync everywhere"],
              ["Best for", "Single device; intermittent / paid-by-MB connections", "Phone + laptop + webmail; the modern default"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.6.4 File transfer protocols</h3>
          <KeyTerm term="FTP (File Transfer Protocol)">Uploads and downloads files between a client and a server. <strong>Plain-text credentials</strong>. Insecure over the public Internet; still used inside controlled networks. Port 21.</KeyTerm>
          <KeyTerm term="SFTP (SSH File Transfer Protocol)">Secure FTP-like transfer running over SSH. Encrypted; the recommended modern replacement for FTP. Runs over port 22 (the SSH port).</KeyTerm>
          <KeyTerm term="WebDAV">HTTP-based protocol that lets remote folders be <strong>mounted</strong> as if they were local drives. Used by some collaboration tools and content systems.</KeyTerm>

          <InfoBox title="Why plain FTP is no longer acceptable on the public Internet" color="red">
            FTP sends your username and password &mdash; and every byte of the file &mdash; in <em>clear text</em>. Anyone listening on the network reads it. Inside a private LAN you may get away with it; on the public Internet you must use <strong>SFTP</strong> or an HTTPS-based file API (e.g. uploading via a web form). Same rule as HTTP: encryption is no longer optional.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">5.6.5 Port numbers cheat sheet</h3>
          <Table
            headers={["Service", "Port", "Protocol"]}
            rows={[
              ["HTTP (web)", "80", "TCP"],
              ["HTTPS (secure web)", "443", "TCP"],
              ["SMTP (send mail)", "25 / 587", "TCP"],
              ["IMAP (receive mail, sync)", "143 / 993 (TLS)", "TCP"],
              ["POP3 (receive mail, download)", "110 / 995 (TLS)", "TCP"],
              ["FTP (file transfer, plain)", "21", "TCP"],
              ["SSH / SFTP (secure shell + file transfer)", "22", "TCP"],
              ["DNS (domain name lookup)", "53", "UDP (TCP for big responses)"],
            ]}
          />

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>distinguish HTTP from HTTPS</strong> and explain why HTTPS is essential on public Wi-Fi. Be ready to <strong>recommend POP3 vs IMAP</strong> for a multi-device user. Know that <strong>FTP is insecure on the public Internet</strong>; SFTP or HTTPS is the modern answer. Memorise port numbers for HTTP/HTTPS/SMTP at minimum.
          </div>
        </Section>

        {/* ══════════════ LU5 PRACTICE EXERCISES ══════════════ */}
        <Section id="exercises-lu5" title="LU5 Practice Exercises" completed={completed["exercises-lu5"]} onToggle={() => toggle("exercises-lu5")}>
          <p className="mb-4 text-sm italic text-slate-600">
            Work through each exercise on paper first, then check your answer. These exercises cover all Learning Unit 5 topics. (Note: the past-paper aggregate has zero coverage on <code>11.2.2</code>; the four <code>11.2.3</code> questions either belong to LU4 or to LU6's compression section, so all LU5 exercises here are hand-authored.)
          </p>

          <div className="space-y-6 text-sm">
            <div>
              {/* Authored — no past paper covers 11.2.2 */}
              <p className="font-semibold">Exercise 1 &mdash; Define WAN</p>
              <p className="text-sm mt-1">Define a <em>WAN</em> and give TWO examples that distinguish it from a LAN. <em>(4 marks)</em></p>
              <Spoiler>
{`• WAN — Wide Area Network: a network that spans
  multiple physical sites, often in different cities
  or countries, joined by long-distance connections
  (fibre, cellular, satellite, microwave). (2)

Distinguishing examples (any 2):
• A WAN crosses many ownership boundaries (multiple
  ISPs); a LAN is usually one organisation's
  internal network. (1)
• A WAN's links span kilometres or continents; a LAN
  is one building or site. (1)
• The Internet itself is a WAN; a school computer
  lab is a LAN. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.2 */}
              <p className="font-semibold">Exercise 2 &mdash; ADSL vs Fibre (L2)</p>
              <p className="text-sm mt-1">A school is choosing between <em>ADSL</em> and <em>fibre to the home</em> for its administration block. Compare them across THREE factors and recommend ONE, with reasoning. <em>(6 marks)</em></p>
              <Spoiler>
{`Comparison (1 mark each axis):

• Bandwidth. ADSL: 4–20 Mbps down, ~1 Mbps up.
  Fibre: 100 Mbps – 1 Gbps, symmetric. Fibre wins. (1)
• Latency. ADSL: ~25 ms. Fibre: ~5 ms. Fibre
  wins. (1)
• Reliability / interference. ADSL on copper is
  sensitive to line quality, lightning, water in
  cabinets. Fibre is immune to electrical
  interference. Fibre wins. (1)

Recommendation:
• Fibre. (1)
• The administration block uses cloud-hosted email,
  file sharing, and HD video calls — all benefit
  from low latency and symmetric upload speed. (1)
• Worth the higher monthly cost; speed and
  reliability gains pay back in less downtime and a
  better user experience. ADSL is acceptable as a
  fallback or only if fibre is unavailable. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.2 */}
              <p className="font-semibold">Exercise 3 &mdash; TCP vs UDP (L2)</p>
              <p className="text-sm mt-1">Distinguish between <em>TCP</em> and <em>UDP</em>, and give ONE example of an application that uses each. <em>(5 marks)</em></p>
              <Spoiler>
{`• TCP — connection-oriented; guarantees reliable,
  in-order delivery; retransmits anything lost.
  Establishes a session before data flows. (2)

• UDP — connectionless; sends individual datagrams
  with no retransmission and no ordering; faster and
  lower-latency. (2)

• Examples:
  TCP — web browsing (HTTP/HTTPS); email; file
        download; anything where every byte must
        arrive.
  UDP — live voice/video call (Zoom, WhatsApp);
        DNS query; online gaming; live streaming. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.2 */}
              <p className="font-semibold">Exercise 4 &mdash; HTTP vs HTTPS (L2)</p>
              <p className="text-sm mt-1">Distinguish between <em>HTTP</em> and <em>HTTPS</em>, and explain why HTTPS is essential on a public Wi-Fi hotspot. <em>(5 marks)</em></p>
              <Spoiler>
{`• HTTP — sends web requests and responses in plain
  text; anyone on the same network can read them
  with a packet sniffer. (1)

• HTTPS — same as HTTP but wrapped in SSL/TLS
  encryption between browser and server. The
  traffic on the wire is encrypted gibberish to
  anyone in between. (1)

• On a public hotspot, attackers on the same
  network can capture all your traffic. (1)
• Without HTTPS, login credentials, cookies, and
  personal data travel in clear text and can be
  stolen. (1)
• HTTPS makes the eavesdropper's captured frames
  useless even if they have full Wi-Fi access. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.2 */}
              <p className="font-semibold">Exercise 5 &mdash; POP3 vs IMAP — recommend (L2)</p>
              <p className="text-sm mt-1">Distinguish between <em>POP3</em> and <em>IMAP</em>. Recommend which protocol Sipho should use if he reads email on his phone, his laptop, and webmail, and explain why. <em>(5 marks)</em></p>
              <Spoiler>
{`• POP3 — downloads messages from the server to the
  client and (traditionally) deletes them from the
  server. One device gets the mail. (1)

• IMAP — keeps messages on the server; clients view
  them in place. Multiple devices stay
  synchronised. (1)

Recommendation:
• IMAP for Sipho. (1)
• He uses three devices and wants the same inbox on
  all of them — read-status, folders, drafts, sent
  mail must match across all three. (1)
• POP3 would download mail to whichever device
  opened the inbox first and (depending on settings)
  leave the others without it; IMAP keeps everything
  on the server so all three devices see the same
  state. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.2 */}
              <p className="font-semibold">Exercise 6 &mdash; Cellular generations</p>
              <p className="text-sm mt-1">Distinguish between <em>3G</em>, <em>4G</em>, and <em>5G</em>. State which is best suited to today's smartphone use, with one reason. <em>(5 marks)</em></p>
              <Spoiler>
{`• 3G — third-generation cellular. Bandwidth a few
  Mbps. End-of-life in SA — carriers are shutting
  down 3G. (1)

• 4G / LTE — fourth-generation cellular. Typical
  10–200 Mbps depending on signal and tower load;
  latency ~30 ms. Ubiquitous in SA today. (1)

• 5G — fifth-generation cellular. 100 Mbps to
  1+ Gbps in good signal; latency ~10 ms.
  Currently rolling out in SA cities. (1)

Best suited today: 4G / LTE for general coverage.
5G where available — especially for high-bandwidth
use like video calls and fixed-wireless home
internet — because of its much higher bandwidth and
lower latency. (1)
Each generation roughly an order of magnitude
faster and lower-latency than the previous. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.2 */}
              <p className="font-semibold">Exercise 7 &mdash; Geostationary vs LEO satellite (L2)</p>
              <p className="text-sm mt-1">Geostationary satellites orbit at ~36 000 km; low-earth-orbit (LEO) satellites at ~550 km. Explain why this distance difference makes <em>latency</em> the dominant difference between the two satellite-Internet services. <em>(4 marks)</em></p>
              <Spoiler>
{`• Latency in any link is bounded below by the
  signal's travel time at the speed of light. (1)

• A geostationary satellite is 36 000 km up. A
  round-trip (up + down + back up + down) at the
  speed of light is roughly 240 ms minimum, before
  ground stations and servers add their own delay —
  total round-trip latency typically ~600 ms. (1)

• A LEO satellite (Starlink) is ~550 km up. The
  same round-trip is ~3.7 ms minimum, leaving
  almost the entire latency budget for ground
  stations, ISPs, and the destination — total
  typically ~30–50 ms, comparable to fibre. (1)

• Bandwidth and weather sensitivity differ too,
  but latency is the user-felt difference: a
  geostationary connection feels sluggish for
  interactive use (video calls, gaming, web
  browsing), while a LEO connection feels normal. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.2 */}
              <p className="font-semibold">Exercise 8 &mdash; NGO scenario — connection + protocols (L3)</p>
              <p className="text-sm mt-1">A small NGO with offices in Johannesburg and Cape Town wants to (a) host its public website, (b) run an internal email system that staff read on phones and laptops, and (c) move large client files between the two offices. Recommend a connection technology <em>for each office</em> and a <em>protocol for each task</em>. Justify each choice briefly. <em>(8 marks)</em></p>
              <Spoiler>
{`Connection technology

• Both offices: FIBRE. (2)
  Best bandwidth (100 Mbps – 1 Gbps symmetric) and
  lowest latency for a permanent staffed location
  with multiple concurrent users.
  Acceptable alternative: 5G fixed-wireless if
  fibre is unavailable in the building.

Protocols

(a) Public website — HTTPS over TCP/IP. (2)
    HTTPS is non-negotiable for any modern site —
    encryption, privacy, browser warnings; TCP
    guarantees pages arrive complete.

(b) Internal email — IMAP for receiving, SMTP for
    sending. (2)
    IMAP because staff use multiple devices (phone
    + laptop) and need synchronised folders; SMTP
    is the standard sending protocol.

(c) Large file transfer between offices — SFTP (or
    HTTPS-based file APIs). (2)
    Encrypted; reliable; survives the inter-city
    WAN hop. Plain FTP would expose credentials in
    clear text and is unsuitable on the public
    Internet.

Award up to 8 marks for any defensible mapping that
names a sensible connection technology, picks the
right protocol per task, and gives at least a
one-line justification per choice.`}
              </Spoiler>
            </div>
          </div>
        </Section>

        {/* ══════════════════════════════════════════════════════════════════════
             LEARNING UNIT 6 — INTERNET
             ══════════════════════════════════════════════════════════════════════ */}

        <div className="mt-12 mb-8 pt-6 border-t-2 border-amber-400">
          <h2 className="text-2xl font-bold text-amber-800 text-center" style={{ fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" }}>Learning Unit 6 — Internet</h2>
          <p className="text-center text-sm text-slate-500 mt-1">Web evolution · Web &amp; mobile applications · Plug-ins &amp; extensions · Searching · Multimedia &amp; compression</p>
        </div>

        {/* ══════════════ 6.1 EVOLUTION OF THE INTERNET ══════════════ */}
        <Section id="web-evolution" title="6.1 Evolution of the Internet" completed={completed["web-evolution"]} onToggle={() => toggle("web-evolution")}>
          <p>LU5 covered the <em>plumbing</em> of the Internet &mdash; the WAN, the connection technologies, the application protocols. LU6 zooms into <em>what runs on top</em>: the Web itself, mobile applications, search, and the multimedia formats that make modern services work. We start with a potted history of how the Web got to where it is.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.1.1 Static vs dynamic sites</h3>
          <KeyTerm term="Static site">Every visitor sees the SAME content, generated by hand-written HTML files. The server delivers a fixed file. Fast, cheap, easy to host; cannot personalise per user.</KeyTerm>
          <KeyTerm term="Dynamic site">Content generated PER REQUEST by a server-side program reading from a database, or by client-side scripts updating the page after it loads. Almost every modern site (Gmail, Facebook, online banking) is dynamic.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.1.2 Web 1.0 → 4.0 at a glance</h3>
          <Table
            headers={["Generation", "Roughly", "Defining idea", "Examples"]}
            rows={[
              ["Web 1.0", "1991–2004", "Read-only — published documents with hyperlinks", "Early Yahoo!, news sites, personal homepages"],
              ["Web 2.0", "2004–present", "Read-write — users CONTRIBUTE", "Wikipedia, YouTube, Facebook, Gmail, Instagram"],
              ["Web 3.0", "Emerging", "Semantic / intelligent — machines UNDERSTAND and act", "Voice assistants, knowledge graphs, AI search"],
              ["Web 4.0", "Speculative", "Ambient / AI-blended — woven into devices and physical space", "Always-on AI, AR/VR, IoT-mediated services"],
            ]}
          />
          <p className="mt-3 text-sm italic text-slate-600">Each generation <strong>builds on</strong> rather than <strong>replaces</strong> the previous one. A modern news site is mostly Web 2.0 (commenting, sharing) on a Web 1.0 substrate (articles), with bits of Web 3.0 (structured data for search engines) added on top.</p>

          <InfoBox title="Web 3.0 confusion" color="orange">
            In tech-marketing circles, &ldquo;<strong>Web3</strong>&rdquo; sometimes refers to <strong>blockchain-based</strong> decentralised apps. In the IEB SAG sense, <strong>Web 3.0 is the semantic Web</strong> &mdash; sites publishing data in a form computers can interpret. Same name, different idea. Use the SAG meaning in the exam.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.1.3 Client-side vs server-side scripting</h3>
          <p>When a web page does something dynamic, <em>where does the code run</em>?</p>

          <KeyTerm term="Client-side scripting">Code that runs IN THE USER'S BROWSER, after the page is downloaded. Typical jobs: validate a form before submitting, show/hide elements, animate, fetch new data without reloading. <strong>JavaScript</strong> is overwhelmingly the language used.</KeyTerm>
          <KeyTerm term="Server-side scripting">Code that runs ON THE WEB SERVER, before the response is sent. Typical jobs: read a database, build a personalised page, process payment. Languages: <strong>PHP</strong>, Python (Django, Flask), Node.js, Java (Spring), C# (.NET).</KeyTerm>

          <Table
            headers={["", "Client-side", "Server-side"]}
            rows={[
              ["Where code runs", "User's browser", "Web server"],
              ["Example language", "JavaScript", "PHP, Python, Node.js, Java, C#"],
              ["User can see code", "Yes — visible in browser dev tools", "No — code stays on the server"],
              ["Can access database directly", "No (must call the server)", "Yes"],
              ["Speed feel after first load", "Fast — no round trip", "Slower — server round trip per request"],
              ["Better for", "Form validation, animation, partial page updates", "Database queries, secure operations, authoritative validation"],
            ]}
          />

          <InfoBox title="Defence in depth — client-side validation is not enough" color="red">
            Validation that <em>is</em> done client-side <strong>must be repeated server-side</strong>. The user can disable JavaScript, edit the page, or call the form's submit URL directly with a tool like <code>curl</code>. Client-side validation is a UX improvement; <strong>server-side validation is the security guarantee</strong>.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.1.4 Cookies</h3>
          <KeyTerm term="Cookie">A small piece of data the server asks the browser to store. The browser sends it back with every subsequent request to the same site. Used for: keeping you logged in, remembering preferences, shopping carts.</KeyTerm>
          <KeyTerm term="Session cookie">Temporary; deleted when the browser closes. Used for short-lived state (the current login session, a shopping cart in progress).</KeyTerm>
          <KeyTerm term="Persistent cookie">Has an expiry date in the future; survives browser restarts. Used for &ldquo;remember me&rdquo; logins, language preferences.</KeyTerm>
          <KeyTerm term="Third-party cookie">Set by a domain OTHER THAN the one in the address bar (typically an ad network whose script is included on the page). Allows tracking a user across many sites. <strong>Increasingly blocked by default in modern browsers</strong> (privacy push).</KeyTerm>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>distinguish Web 1.0 from Web 2.0</strong> with one example each. Be ready to <strong>distinguish client-side from server-side scripting</strong> with one language each and one situation where each is the better choice. Define <strong>cookies</strong> and distinguish <strong>session</strong> from <strong>persistent</strong> cookies.
          </div>
        </Section>

        {/* ══════════════ 6.2 WEB-BASED APPLICATIONS ══════════════ */}
        <Section id="web-applications" title="6.2 Web-based Applications" completed={completed["web-applications"]} onToggle={() => toggle("web-applications")}>
          <p>A <strong>web-based application</strong> is software that lives <em>inside</em> a browser tab. There's nothing to install on the user's machine; updates land on the server and reach every user automatically.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.2.1 Definition and examples</h3>
          <KeyTerm term="Web-based application">Software that runs INSIDE a web browser; no install, no platform-specific build. Loaded from a URL, kept up-to-date by the server. Examples: Gmail, Google Docs, Office 365, Netflix's web player, Caliper itself.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.2.2 SaaS &mdash; how web apps are sold</h3>
          <KeyTerm term="SaaS (Software as a Service)">The business model under which most web apps are delivered today: pay (or subscribe) for ongoing access to a hosted service rather than for a one-off install. Office 365, Google Workspace, Netflix, and Spotify are all SaaS.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.2.3 SPAs &mdash; single-page applications</h3>
          <KeyTerm term="Single-page application (SPA)">A web app that loads ONE HTML page initially, then updates parts of the page in place using JavaScript and small data fetches. Feels closer to a desktop app than to a multi-page web site. Used by Gmail, Trello, modern dashboards, much of Caliper.</KeyTerm>

          <p className="mt-3 text-sm">Why SPAs took over: only one page load; subsequent navigation feels instant; less server round-tripping. Cost: more JavaScript to download up-front; the app is unusable until that JavaScript has loaded.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.2.4 Strengths and weaknesses of web apps</h3>
          <Table
            headers={["Strength", "Weakness"]}
            rows={[
              ["Single codebase serves every platform with a browser", "Limited access to device features (camera, biometrics, push)"],
              ["Updates instantly for every user — no &ldquo;please install version 2.4&rdquo;", "Requires connectivity (though service workers add offline support)"],
              ["No app-store gatekeeping or revenue share", "Performance is below native — heavy in-browser computation drains battery"],
              ["Cheapest to build and maintain", "Some perceptions of being &ldquo;less polished&rdquo; than native apps"],
            ]}
          />

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Know that <strong>web apps need no install</strong>, are <strong>updated by the server</strong>, and run inside the browser. Know that an <strong>SPA</strong> is one HTML page updated in place via JavaScript &mdash; the modern web-app pattern.
          </div>
        </Section>

        {/* ══════════════ 6.3 MOBILE WEBSITES ══════════════ */}
        <Section id="mobile-websites" title="6.3 Mobile Websites" completed={completed["mobile-websites"]} onToggle={() => toggle("mobile-websites")}>
          <p>A <strong>mobile website</strong> is a regular website that has been deliberately designed to work well on a phone. Same URL, same browser, but a layout that adapts to a 5-inch screen and a touch interface.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.3.1 Mobile-optimised vs &ldquo;just a website on a phone&rdquo;</h3>
          <p>Three things separate a mobile-optimised site from a desktop site that just happens to render on a phone:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>Responsive layout</strong> &mdash; columns collapse to a single column; menus collapse into a burger; text reflows. The same HTML, with CSS that responds to the screen width.</li>
            <li><strong>Large tap targets</strong> &mdash; buttons and links big enough for a finger (~44 px square minimum), not a mouse pointer.</li>
            <li><strong>Performance budget</strong> &mdash; smaller images, fewer scripts, deliberate work to keep the page small over a metered cellular connection.</li>
          </ul>

          <KeyTerm term="Mobile web application">A website that's MOBILE-OPTIMISED: responsive layout, large tap targets, designed to behave well on phones. Runs in the phone's browser. No install. Limited access to device features. <strong>Cheapest to build</strong> &mdash; one codebase serves desktop and mobile.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.3.2 What mobile websites can and can't do</h3>
          <Table
            headers={["Can", "Can't (or only with difficulty)"]}
            rows={[
              ["Display content; collect input; submit forms", "Send push notifications when the browser is closed"],
              ["Use the camera and microphone via browser APIs", "Access protected device hardware (fingerprint, secure enclave)"],
              ["Cache pages for re-visit; some service-worker offline support", "Reliable offline use — no guaranteed local storage budget"],
              ["Be linked to from anywhere on the Web", "Be discovered through the app store"],
            ]}
          />

          <p className="mt-3 text-sm italic text-slate-600">A mobile website is the <em>cheapest</em> mobile presence. For many use cases (information, content, simple forms), it is more than enough. The decision to go beyond it &mdash; to a hybrid or native app &mdash; is driven by needing what mobile websites can't do.</p>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Know what makes a website <strong>mobile-optimised</strong> (responsive layout, tap targets, performance). Know mobile-website <strong>limitations</strong> &mdash; no push, no app-store presence, limited device access.
          </div>
        </Section>

        {/* ══════════════ 6.4 MOBILE APPLICATIONS ══════════════ */}
        <Section id="mobile-applications" title="6.4 Mobile Applications" completed={completed["mobile-applications"]} onToggle={() => toggle("mobile-applications")}>
          <p>Beyond mobile websites, two kinds of installable mobile app exist: <strong>native</strong> and <strong>hybrid</strong>. The choice is one of cost, performance, and platform reach.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.4.1 Native applications</h3>
          <KeyTerm term="Native mobile application">Built for ONE mobile operating system using its native toolkit. Android: <strong>Java/Kotlin</strong> in Android Studio. iOS: <strong>Swift</strong> in Xcode. Distributed through the platform's app store. Highest performance and deepest device-feature access (camera, sensors, secure storage) but a SEPARATE codebase per OS.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.4.2 Hybrid applications</h3>
          <KeyTerm term="Hybrid application">A WEB APPLICATION WRAPPED IN A NATIVE SHELL. Built once with HTML/CSS/JavaScript, packaged into a native container (<strong>Apache Cordova</strong>, <strong>Ionic</strong>, <strong>React Native</strong>) that exposes some native device features. Distributed through the app store like a native app.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.4.3 Three approaches compared</h3>
          <Table
            headers={["", "Mobile Web", "Hybrid", "Native"]}
            rows={[
              ["Install required", "No", "Yes (app store)", "Yes (app store)"],
              ["Codebase", "One (HTML/JS/CSS)", "One mostly-shared + native shell per platform", "Two (Android Kotlin + iOS Swift)"],
              ["Performance", "Lowest", "Middle", "Best"],
              ["Device feature access", "Limited (browser APIs)", "Most via plugins", "Full"],
              ["Offline capability", "Limited (service workers)", "Good", "Best"],
              ["App-store presence", "No", "Yes", "Yes"],
              ["Build cost", "Cheapest", "Middle", "Most expensive"],
              ["Update path", "Server push — instant for everyone", "App store review — days", "App store review — days"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.4.4 The app store</h3>
          <KeyTerm term="App store">The platform-managed catalogue from which users install native and hybrid apps. <strong>Apple App Store</strong> (iOS), <strong>Google Play Store</strong> (Android). Vetting, discovery, payments, and updates flow through the store. Apple takes a percentage of every paid download and in-app purchase; Google does similarly.</KeyTerm>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>distinguish native from hybrid</strong>, with at least one strength and one weakness of each. Know that <strong>native = one codebase per OS</strong>; <strong>hybrid = a web app in a native shell</strong>; <strong>mobile web = no install</strong>. For a given scenario, recommend one and justify the choice.
          </div>
        </Section>

        {/* ══════════════ 6.5 MOBILE DESIGN FACTORS ══════════════ */}
        <Section id="mobile-design" title="6.5 Mobile Design Factors" completed={completed["mobile-design"]} onToggle={() => toggle("mobile-design")}>
          <p>Designing for phones is a different discipline from designing for desktops. The SAG names <strong>four constraints</strong> &mdash; the &ldquo;mobile design factors&rdquo; &mdash; that drive the differences.</p>

          <Table
            headers={["Factor", "Constraint", "Design implication"]}
            rows={[
              ["Screen size", "Phone screens are 5–6 inches; far less display area than desktops", "Single-column layouts; large tap targets; prioritise the most important content; less text per screen"],
              ["Processing demands", "Mobile CPUs are weaker than desktop; battery is a hard limit", "Avoid heavy in-browser computation; minimise JavaScript work; use efficient codecs"],
              ["Storage demands", "Phones often have less storage than laptops; users juggle photos, apps, music", "Keep app size small (<50 MB ideal); modest local caches; don't bloat with seldom-used assets"],
              ["Bandwidth requirements", "Mobile users frequently have METERED data plans (cost per GB) and intermittent connections", "Compress images aggressively; minimise asset count; design for offline graceful-degradation; don't auto-play video"],
            ]}
          />

          <KeyTerm term="Screen size">Phones have far less display area than desktops; layouts must collapse to single columns, prioritise the most important content, and use larger type and tap targets.</KeyTerm>
          <KeyTerm term="Processing demands">Mobile CPUs are less powerful than desktop CPUs and share resources with calls, messages, and background tasks. Heavy in-browser computation drains battery and slows the device.</KeyTerm>
          <KeyTerm term="Storage demands">Phones often have less storage than laptops; local caches must be modest. App size matters &mdash; a 200 MB app installs slowly and may not fit on entry-level phones.</KeyTerm>
          <KeyTerm term="Bandwidth requirements">Mobile users frequently have metered data plans (charged per GB) and intermittent connections. A page that downloads 5 MB of images per visit is fine on home Wi-Fi and painful on a 1 GB monthly plan.</KeyTerm>

          <InfoBox title="Mobile design isn't desktop design scaled down" color="blue">
            Mobile imposes constraints &mdash; touch input, no hover, narrow screens, bandwidth, processing, storage &mdash; that change <em>what</em> belongs on the screen at all, not just how big to make it. A good mobile design starts from &ldquo;what does the user need <em>here</em>?&rdquo; rather than &ldquo;how do I shrink the desktop?&rdquo;
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Memorise <strong>all four mobile design factors</strong> in order: <strong>Screen size · Processing demands · Storage demands · Bandwidth requirements</strong>. For each, give a concrete design implication. Examiners reward concrete examples (&ldquo;use a 50 MB install&rdquo;) over abstract definitions (&ldquo;keep it small&rdquo;).
          </div>
        </Section>

        {/* ══════════════ 6.6 PLUG-INS & EXTENSIONS ══════════════ */}
        <Section id="plugins-extensions" title="6.6 Plug-ins & Extensions" completed={completed["plugins-extensions"]} onToggle={() => toggle("plugins-extensions")}>
          <p>Both plug-ins and extensions <em>add</em> capability to the browser, but they sit at very different layers and have very different histories.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.6.1 Plug-ins (mostly extinct)</h3>
          <KeyTerm term="Plug-in">A separately-installed module that adds support for a NEW CONTENT TYPE OR RUNTIME to the browser. Historically vital &mdash; <strong>Adobe Flash</strong>, <strong>Java applets</strong>, Apple QuickTime, Microsoft Silverlight. Almost entirely retired now.</KeyTerm>

          <p className="mt-3">Why they died:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>Modern browsers handle audio/video/animation natively</strong> via HTML5 &mdash; the need disappeared.</li>
            <li><strong>Security weaknesses</strong> &mdash; Flash had many critical vulnerabilities; running third-party native code inside the browser was always risky.</li>
            <li><strong>Performance drag</strong> &mdash; plug-ins often crashed; consumed memory aggressively; failed silently on mobile.</li>
            <li><strong>Mobile unfriendly</strong> &mdash; iOS never supported Flash; Android dropped it. The mobile transition forced the issue.</li>
          </ul>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.6.2 Browser extensions (everywhere)</h3>
          <KeyTerm term="Browser extension">A small program installed INTO the browser that modifies its UI or behaviour. Examples: ad blockers (uBlock Origin), password managers (1Password, Bitwarden), grammar checkers (Grammarly), translation tools, dark-mode tools. Distributed through extension stores (Chrome Web Store, Firefox Add-ons).</KeyTerm>

          <Table
            headers={["", "Plug-in (historical)", "Extension (current)"]}
            rows={[
              ["What it adds", "A new content type or runtime (Flash, Java applet)", "UI/behaviour changes (ad blocking, password fill)"],
              ["Where it runs", "Outside the browser's safety model; native code", "Inside the browser's sandbox; mostly JavaScript"],
              ["Distributed via", "Adobe / Oracle / Microsoft websites", "Chrome Web Store, Firefox Add-ons, etc."],
              ["Status today", "Almost entirely retired", "Ubiquitous"],
            ]}
          />

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>distinguish plug-in from extension</strong> with one example of each, and to explain <strong>why plug-ins largely disappeared</strong> &mdash; HTML5 made them unnecessary, security made them dangerous.
          </div>
        </Section>

        {/* ══════════════ 6.7 SEARCH ENGINES ══════════════ */}
        <Section id="search-engines" title="6.7 Search Engines" completed={completed["search-engines"]} onToggle={() => toggle("search-engines")}>
          <p>At Gr10 you met search engines as &ldquo;type words, get pages&rdquo; with the basic Boolean operators. Gr11 extends in two directions named explicitly in the SAG: <strong>multiple input modalities</strong> (sound, image) and <strong>advanced query operators</strong> (<code>site:</code>, <code>filetype:</code>, etc.).</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.7.1 Search modalities</h3>
          <KeyTerm term="Modality (search)">The kind of input the search engine accepts: text typed by the user, audio recorded by a microphone, an image uploaded or pasted, a screenshot. Modern engines accept several.</KeyTerm>

          <Table
            headers={["Modality", "What you give the engine", "Example service"]}
            rows={[
              ["Text", "Typed words", "Google, Bing, DuckDuckGo"],
              ["Sound", "5–15 s audio recording (often a song)", "Shazam, Google Sound Search, SoundHound"],
              ["Image (text → image)", "A description of the image you want", "Google Images, Bing Images"],
              ["Reverse image (image → pages)", "An image you have", "Google Reverse Image Search, TinEye, Bing Visual Search"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.7.2 How sound search works (Shazam)</h3>
          <KeyTerm term="Audio fingerprinting">The technique behind sound search. The recording is converted into a SPECTROGRAM; distinctive peaks are extracted as a compact <strong>fingerprint</strong> robust to background noise, low volume, and phone-microphone quality. The fingerprint is matched against a pre-built database of fingerprints of millions of songs.</KeyTerm>

          <p className="mt-3 text-sm italic text-slate-600">The result feels magical, but the algorithm is the same shape as text search: turn the input into a small signature; look the signature up in a pre-built index; return the best matches. Different modality, same recipe.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.7.3 Reverse image search</h3>
          <KeyTerm term="Reverse image search">Find pages that contain a GIVEN IMAGE, or visually similar images. The engine extracts a visual fingerprint and searches BY FINGERPRINT, not by keyword.</KeyTerm>

          <p className="mt-3">Useful for:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>Tracing the source</strong> of an unfamiliar photo &mdash; where was this published before?</li>
            <li><strong>Spotting fakes</strong> &mdash; a &ldquo;current event&rdquo; image that turns out to be from a different country and decade.</li>
            <li><strong>Finding higher-resolution copies</strong> of a low-res image you already have.</li>
            <li><strong>Identifying</strong> a plant, building, breed of dog, or landmark in a personal photo.</li>
          </ul>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.7.4 Advanced query operators</h3>
          <p>Beyond <code>AND</code>, <code>OR</code>, <code>NOT</code> (Gr10), Gr11 introduces operators that constrain the <em>kind</em> of pages searched.</p>

          <Table
            headers={["Operator", "Restricts to…", "Example"]}
            rows={[
              ["site:", "One domain or subdomain", "site:sars.gov.za tax brackets"],
              ["filetype:", "One file extension (pdf, doc, docx, ppt, xls)", "filetype:pdf \"Information Technology SAG\""],
              ["intitle:", "Pages with the word in the title", "intitle:syllabus information technology"],
              ['"phrase"', "Words in EXACT order", '"third normal form"'],
              ["-word", "Excludes pages containing the word", "python -snake"],
              ["*", "Wildcard for any word", '"how to * a school timetable"'],
              ["Date filter (UI)", "Pages within a date range", "Tools → Past year"],
            ]}
          />

          <InfoBox title="Combining operators — a worked query" color="green">
            You want the latest IEB IT SAG as a PDF on the IEB site:
            <pre className="mt-2 text-xs bg-slate-100 p-2 rounded overflow-x-auto"><code>site:ieb.co.za filetype:pdf "Information Technology" SAG</code></pre>
            Each operator narrows further: <code>site:</code> by source · <code>filetype:</code> by form · phrase quotes by exact wording · <code>SAG</code> by keyword. Add operators <em>one at a time</em> and check the result count; stop when the results are useful. Stacking too many narrows to zero results.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.7.5 Behind the scenes &mdash; crawl, index, rank</h3>
          <KeyTerm term="Crawl">The search engine's automated browser (the <em>crawler</em> or <em>spider</em>) follows links, downloading every reachable page so its content can be analysed.</KeyTerm>
          <KeyTerm term="Index">The data structure built from crawled pages &mdash; for each word, the list of pages containing it (plus position, frequency, importance signals). A query is answered by intersecting indexes, not by re-reading every page.</KeyTerm>
          <KeyTerm term="Rank">The algorithm that ORDERS matching results by relevance, page authority, freshness, link structure, user signals. Ranking is what turns &ldquo;10 million matches&rdquo; into &ldquo;10 results most likely to be useful&rdquo;.</KeyTerm>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Name <strong>three search modalities</strong> with example services. Distinguish <strong>image search</strong> (find images by text) from <strong>reverse image search</strong> (find pages by image). Be ready to <strong>write a query</strong> using <code>site:</code>, <code>filetype:</code>, phrase quotes, and exclusion correctly for a given research need.
          </div>
        </Section>

        {/* ══════════════ 6.8 MULTIMEDIA ONLINE ══════════════ */}
        <Section id="multimedia-online" title="6.8 Multimedia Online" completed={completed["multimedia-online"]} onToggle={() => toggle("multimedia-online")}>
          <p>The Internet is, for most users, a multimedia delivery system. The two technical decisions that determine whether multimedia feels smooth, sluggish, or unusable are the <strong>delivery model</strong> (download vs streaming) and <strong>compression</strong> (next section).</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.8.1 Multimedia, broadly</h3>
          <KeyTerm term="Multimedia">Content that combines two or more of: text, images, audio, video, animation, and interactive elements. Streaming a YouTube video, joining a Zoom call, listening to a podcast &mdash; all multimedia.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.8.2 Download vs streaming</h3>
          <KeyTerm term="Download">The file is FULLY TRANSFERRED to the user's device before (or independently of) use. Once downloaded, it can be played offline, replayed, and shared. Single bandwidth burst; doesn't require a steady stream.</KeyTerm>
          <KeyTerm term="Streaming">The file is delivered CONTINUOUSLY in real time, played as it arrives. Only a small buffer is held locally; the bulk of the file is never stored on the device. Requires SUSTAINED bandwidth for the duration of playback.</KeyTerm>

          <Table
            headers={["", "Download", "Streaming"]}
            rows={[
              ["Time to start", "Wait for the whole file", "Plays after a few seconds (initial buffer)"],
              ["Total bandwidth used", "Same as file size", "Roughly the same — same data must arrive"],
              ["Connection drops mid-watch", "No problem (file already local)", "Playback stalls until the network recovers"],
              ["Local storage", "Full file size", "Almost none — only the running buffer"],
              ["Best for", "Offline use; content to keep; weak connections", "Casual one-time viewing; scaling to many viewers"],
            ]}
          />

          <InfoBox title="Streaming does NOT save bandwidth" color="orange">
            A common misconception. The same number of bytes must arrive whether you download or stream. <strong>Streaming changes <em>when</em> the bytes arrive</strong> (continuously, in real time) and reduces the local-storage requirement. The total bandwidth use is similar.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.8.3 Buffering and adaptive bitrate</h3>
          <KeyTerm term="Buffering">Holding a few seconds of the upcoming stream in memory before playing it, so brief drops in network speed don't interrupt playback. The &ldquo;Buffering&hellip;&rdquo; message means the buffer ran empty &mdash; the network couldn't keep up.</KeyTerm>
          <KeyTerm term="Adaptive bitrate streaming">Modern streaming (HLS, MPEG-DASH) automatically picks a quality level matched to the user's measured bandwidth, dropping resolution if the connection slows. Why YouTube and Netflix degrade gracefully instead of stopping.</KeyTerm>
          <KeyTerm term="Jitter">Variation in the time between successive packets arriving. A constant 50 ms latency is fine; a latency that swings between 30 ms and 200 ms produces audible glitches in voice/video calls. The buffer absorbs jitter at the cost of latency.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.8.4 Specific multimedia applications</h3>
          <Table
            headers={["Application", "Delivery model", "Sensitive to"]}
            rows={[
              ["Video on-demand (Netflix, YouTube)", "Streaming with adaptive bitrate", "Sustained bandwidth"],
              ["Live streaming (Twitch, sports broadcast)", "Streaming, low-latency", "Bandwidth + latency"],
              ["VOIP (WhatsApp, Zoom audio/video)", "Streaming, real-time, UDP-based", "Latency + jitter + packet loss"],
              ["Podcast / Vodcast", "Both — download for offline listening; stream for one-off", "Storage (download) or bandwidth (stream)"],
            ]}
          />

          <KeyTerm term="VOIP (Voice over IP)">Real-time voice (and often video) calls carried as data over the Internet. WhatsApp calls, Zoom audio, Teams. Replaces traditional circuit-switched phone calls. Requires LOW LATENCY and LOW JITTER &mdash; humans notice delays above ~150 ms in conversation.</KeyTerm>
          <KeyTerm term="Podcast / Vodcast">Episodic AUDIO (podcast) or VIDEO (vodcast) programmes published as a feed. Subscribers download new episodes (or stream them) using a podcast app. Inherently asynchronous &mdash; listened to whenever convenient, often offline.</KeyTerm>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>distinguish download from streaming</strong>; explain why streaming needs <strong>sustained</strong> bandwidth while a download does not. Know that <strong>VOIP needs low latency and low jitter</strong>; <strong>VOD streams adaptive bitrate</strong>. State that <strong>streaming does not save total bandwidth</strong> &mdash; common misconception.
          </div>
        </Section>

        {/* ══════════════ 6.9 COMPRESSION TECHNOLOGY ══════════════ */}
        <Section id="compression" title="6.9 Compression Technology" completed={completed["compression"]} onToggle={() => toggle("compression")}>
          <p>Raw audio, image, and video files are <em>huge</em>. Without compression, almost no modern Internet service would work &mdash; no Netflix, no Zoom, no WhatsApp voice notes, no Spotify. Compression shrinks files in two flavours: <strong>lossless</strong> (every bit reversible) and <strong>lossy</strong> (some data permanently discarded).</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.9.1 Compression and codecs</h3>
          <KeyTerm term="Compression">Encoding data so it occupies less space. Trade-offs: smaller file = less bandwidth and storage; processing required at both ends; sometimes quality lost.</KeyTerm>
          <KeyTerm term="Codec">Coder&ndash;decoder. Software (or hardware) that performs compression and decompression for a particular format. MP3 has an MP3 codec; H.264 has an H.264 codec. <strong>Both ends of a stream / call must support the same codec.</strong></KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.9.2 Lossy vs lossless</h3>
          <KeyTerm term="Lossy compression">Discards data the human eye or ear is least likely to notice, in order to shrink the file dramatically. The original CANNOT be reconstructed exactly. Examples: <strong>MP3</strong> and <strong>AAC</strong> for audio; <strong>JPEG</strong> for images; <strong>MP4 / H.264 / H.265 / AV1</strong> for video.</KeyTerm>
          <KeyTerm term="Lossless compression">Every bit of the original survives; the original is reconstructed EXACTLY on decompression. Smaller file but not as small as a lossy version. Examples: <strong>FLAC</strong> for audio; <strong>PNG</strong> for images; <strong>ZIP / RAR / 7z</strong> for general files.</KeyTerm>

          <Table
            headers={["", "Lossy", "Lossless"]}
            rows={[
              ["Quality vs original", "Some loss (designed to be imperceptible at high bitrates)", "Identical to original on decompression"],
              ["File-size reduction", "Dramatic — often 10× smaller", "Modest — typically 30–50% smaller"],
              ["Best for", "Photos, music, video — perceptual data", "Code, documents, screenshots, archival audio"],
              ["Examples", "MP3, AAC, JPEG, MP4 (H.264)", "FLAC, PNG, ZIP, 7z"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.9.3 Lossy audio &mdash; the psychoacoustic model</h3>
          <KeyTerm term="Bitrate">The data rate of an encoded media stream, measured in kbps or Mbps. <strong>Higher bitrate ⇒ better quality, larger file, more bandwidth needed.</strong> A 128 kbps MP3 is &ldquo;good enough&rdquo; for casual listening; 320 kbps is near-CD quality.</KeyTerm>
          <KeyTerm term="Sample rate">For audio, how many measurements of the sound wave are taken per second (44.1 kHz on a CD; 48 kHz for video audio). Higher = more accurate sound; doesn't necessarily mean larger file (compression dominates).</KeyTerm>
          <KeyTerm term="Psychoacoustic model">The basis of LOSSY AUDIO compression. The encoder identifies sounds the human ear can't perceive (frequencies above hearing range, sounds masked by louder simultaneous sounds) and discards them. Why a 320 kbps MP3 sounds nearly identical to the uncompressed CD source.</KeyTerm>

          <p className="mt-3 text-sm">A 4-minute song:</p>
          <Table
            headers={["Format / bitrate", "File size", "Quality (typical listener)"]}
            rows={[
              ["WAV uncompressed", "≈ 40 MB", "Original — perfect"],
              ["FLAC (lossless ~900 kbps)", "≈ 25 MB", "Identical to WAV on decompress"],
              ["MP3 320 kbps (lossy)", "≈ 9 MB", "Near-indistinguishable from WAV on most equipment"],
              ["MP3 128 kbps (lossy)", "≈ 3.6 MB", "Acceptable for casual listening"],
              ["MP3 64 kbps (lossy)", "≈ 1.8 MB", "Noticeable artefacts; voice-only ok"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">6.9.4 Common formats at a glance</h3>
          <Table
            headers={["Domain", "Lossy", "Lossless"]}
            rows={[
              ["Audio", "MP3, AAC", "FLAC, WAV (uncompressed)"],
              ["Image", "JPEG", "PNG, BMP (uncompressed), GIF (lossless palette)"],
              ["Video", "MP4 / H.264, H.265, AV1, WebM", "Rare in distribution; FFV1 / lossless ProRes for archival"],
              ["File / general", "—", "ZIP, RAR, 7z, gzip"],
            ]}
          />

          <InfoBox title="Why MP3 dominates over lossless" color="green">
            A 320 kbps MP3 is roughly 1/4 the size of the uncompressed source and sounds near-identical to most listeners on consumer equipment. The ~75% reduction in file size is the practical reason MP3 (and now AAC) dominate over lossless formats for everyday use. Lossless wins for archival and audio engineering; lossy wins for distribution.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>distinguish lossy from lossless</strong> with one example format each. Know that <strong>lossy gives much smaller files</strong> at the cost of unrecoverable quality. Explain why <strong>MP3 (lossy) is acceptable for music</strong> &mdash; the psychoacoustic model discards what listeners can't hear. Know that <strong>lossless is essential for code, documents, archives</strong> where every bit matters.
          </div>
        </Section>

        {/* ══════════════ LU6 PRACTICE EXERCISES ══════════════ */}
        <Section id="exercises-lu6" title="LU6 Practice Exercises" completed={completed["exercises-lu6"]} onToggle={() => toggle("exercises-lu6")}>
          <p className="mb-4 text-sm italic text-slate-600">
            Work through each exercise on paper first, then check your answer. These exercises cover all Learning Unit 6 topics. Where a question is paraphrased from a past paper, the source is noted.
          </p>

          <div className="space-y-6 text-sm">
            <div>
              {/* Authored — no past paper covers 11.2.5 at L1 */}
              <p className="font-semibold">Exercise 1 &mdash; Web 1.0 vs Web 2.0</p>
              <p className="text-sm mt-1">Distinguish between <em>Web 1.0</em> and <em>Web 2.0</em>. Give ONE example of a service typical of each. <em>(4 marks)</em></p>
              <Spoiler>
{`• Web 1.0 — the READ-ONLY Web; mostly static HTML
  pages where users read what publishers had
  written. (1)
  Example: an early news site, an early Yahoo!
  directory, a personal homepage. (1)

• Web 2.0 — the READ-WRITE Web; users CONTRIBUTE
  content. (1)
  Example (any one): Wikipedia, YouTube, Facebook,
  Gmail, Instagram, Google Docs. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.5 */}
              <p className="font-semibold">Exercise 2 &mdash; Client-side vs server-side scripting (L2)</p>
              <p className="text-sm mt-1">Distinguish between <em>client-side</em> and <em>server-side</em> scripting. Give an example language used in each, and ONE situation where each is the better choice. <em>(6 marks)</em></p>
              <Spoiler>
{`• Client-side — code runs IN THE USER'S BROWSER. (1)
  Example: JavaScript. (1)
  Better for: instant form validation; UI animation;
  updating part of the page without reloading;
  reducing server load. (1)

• Server-side — code runs ON THE WEB SERVER, before
  the response is sent. (1)
  Example: PHP, Python, Node.js, Java, C#. (1)
  Better for: anything that needs the database;
  anything that must remain secret (passwords,
  payment processing); authoritative validation
  that the user cannot bypass. (1)

Note: client-side validation is a UX improvement
only; the server MUST validate again because the
user can disable JavaScript or call the submit URL
directly.`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.5 */}
              <p className="font-semibold">Exercise 3 &mdash; Cookies</p>
              <p className="text-sm mt-1">Define a <em>cookie</em> and distinguish between a <em>session cookie</em> and a <em>persistent cookie</em>. <em>(4 marks)</em></p>
              <Spoiler>
{`• Cookie — a small piece of data the server asks
  the browser to store. The browser sends it back
  with every subsequent request to the same site.
  Used for state across requests (login,
  preferences, shopping cart). (2)

• Session cookie — temporary; deleted when the
  browser closes. Used for short-lived state like
  the current login session. (1)

• Persistent cookie — has a future expiry date and
  survives browser restarts. Used for 'remember me'
  logins and saved preferences. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.5 */}
              <p className="font-semibold">Exercise 4 &mdash; Native vs Hybrid mobile apps (L2)</p>
              <p className="text-sm mt-1">Distinguish between a <em>native mobile application</em> and a <em>hybrid application</em>. Discuss ONE strength and ONE weakness of each. <em>(6 marks)</em></p>
              <Spoiler>
{`• Native — built for ONE mobile OS using its
  native toolkit (Kotlin / Swift). (1)
  Strength: best performance and deepest access to
  device features (camera, biometrics, push). (1)
  Weakness: requires a separate codebase per
  platform — most expensive to build and
  maintain. (1)

• Hybrid — a web app (HTML/CSS/JS) wrapped in a
  native shell so it can be distributed through
  app stores. (1)
  Strength: a single codebase covers Android and
  iOS; faster and cheaper to build than two native
  apps. (1)
  Weakness: performance below native; limited or
  harder access to some device features; framework
  lock-in. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.5 */}
              <p className="font-semibold">Exercise 5 &mdash; Plug-ins vs Extensions (L2)</p>
              <p className="text-sm mt-1">Distinguish between a <em>plug-in</em> and a <em>browser extension</em>. Why have plug-ins largely disappeared? <em>(4 marks)</em></p>
              <Spoiler>
{`• Plug-in — separately-installed module that added
  support for a new content type or runtime to the
  browser. Examples: Adobe Flash, Java applets,
  Microsoft Silverlight. (1)

• Browser extension — small program installed INTO
  the browser that modifies its UI or behaviour.
  Examples: ad blockers, password managers,
  translation tools. (1)

Why plug-ins disappeared:
• Modern browsers (HTML5) handle audio, video,
  animation, and many interactive features
  natively — they no longer need an external
  module.
• Plug-ins were also a frequent SECURITY weakness
  (Flash had many critical vulnerabilities) and a
  performance drag.
• Mobile browsers (iOS, then Android) never
  supported them, forcing the issue. (2)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.6 */}
              <p className="font-semibold">Exercise 6 &mdash; Three search modalities</p>
              <p className="text-sm mt-1">Name THREE <em>modalities</em> (kinds of input) that modern search engines accept, and give an example service or app for each. <em>(6 marks)</em></p>
              <Spoiler>
{`• Text — the user types words. (1)
  Example: Google search, Bing, DuckDuckGo. (1)

• Sound — the user records audio (often a song). (1)
  Example: Shazam, Google Sound Search,
  SoundHound. (1)

• Image / reverse image — the user uploads or
  pastes an image. (1)
  Example: Google Reverse Image Search, Bing Visual
  Search, TinEye. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.6 at L2 */}
              <p className="font-semibold">Exercise 7 &mdash; Build an advanced query (L2)</p>
              <p className="text-sm mt-1">A learner needs to find the original school year-end exam paper from 2023 as a PDF, only on the school's own website (<code>helpmekaar.co.za</code>). Write ONE search query, using appropriate advanced operators, that targets this need. Briefly explain what each operator contributes. <em>(4 marks)</em></p>
              <Spoiler>
{`Sample query (any equivalent):

site:helpmekaar.co.za filetype:pdf "year-end exam" 2023

What each operator contributes:
• site:helpmekaar.co.za — limits results to the
  school's own domain. (1)
• filetype:pdf — limits to PDF files (the form
  exam papers are typically published in). (1)
• "year-end exam" — phrase search; keeps the
  words together rather than matching pages where
  they appear separately. (1)
• 2023 — keyword to narrow further by year. (1)

Accept variations such as 'intitle:' if used
sensibly, or different phrasings of the year/exam.`}
              </Spoiler>
            </div>

            <div>
              {/* Source: ieb_it_2023_p2 Q4.6.1 + Q4.6.2 — paraphrased; combined into one multi-part exercise */}
              <p className="font-semibold">Exercise 8 &mdash; Lossy vs Lossless characteristics (L1 + L2)</p>
              <p className="text-sm mt-1">For each characteristic below, indicate whether it applies to <em>lossy</em> or <em>lossless</em> compression. Then, for TWO of the three, briefly explain your choice.</p>
              <p className="text-sm mt-1">(A) Removes as much data as possible from the original file. (B) Doesn't allow for significant storage savings. (C) Data quality, especially for video images, is compromised. <em>(5 marks)</em></p>
              <Spoiler>
{`Categorisation (3 marks):
• A — Removes as much data as possible:
  LOSSY. (1)
• B — Doesn't allow for significant storage
  savings: LOSSLESS. (1)
• C — Data quality is compromised, especially for
  video: LOSSY. (1)

Explanation (any 2, 1 mark each):

(A) LOSSY removes as much data as possible because
    it specifically targets data the human eye/ear
    cannot perceive (frequencies above hearing
    range; sounds masked by louder ones; visual
    detail below perception threshold) and
    discards it. The trade-off is dramatic file
    shrinkage. (1)

(B) LOSSLESS doesn't allow significant storage
    savings because every bit of the original must
    be reconstructable on decompression. There is
    a hard mathematical limit on how much you can
    compress without losing information; lossless
    typically achieves only ~30–50% reduction
    versus lossy's ~10× reduction. (1)

(C) LOSSY compromises quality because data is
    permanently discarded; the original cannot be
    reconstructed exactly. For video, repeated
    re-encoding accumulates artefacts (visible
    blocking, blurring). Lossless avoids this but
    produces files too large for streaming. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.5 at L3 */}
              <p className="font-semibold">Exercise 9 &mdash; NGO mobile-app scenario (L3)</p>
              <p className="text-sm mt-1">A small NGO wants to launch a mobile-friendly tool for community health workers to record patient visits. The workers travel between villages where mobile signal is intermittent and use mid-range Android phones with limited storage. Recommend an <em>application type</em> (web / native / hybrid) and discuss the FOUR mobile design factors that influence your choice. <em>(8 marks)</em></p>
              <Spoiler>
{`Sample recommendation: HYBRID application (or
fully native Android — defensible if budget
allows). A pure mobile web app is risky because
of intermittent signal — it cannot reliably
operate offline.

Mobile design factors (each 1 mark for naming +
1 mark for application to scenario):

• Screen size. Workers use phones — likely 5–6
  inch screens. UI must use a single-column
  layout, large tap targets, and minimal text per
  screen. (2)

• Processing demands. Mid-range phones have
  limited CPU. Avoid heavy in-browser computation;
  prefer simple forms over complex JavaScript-
  heavy interfaces. (2)

• Storage demands. Limited phone storage means
  the app must keep its install size and local
  cache small (< 50 MB ideally). Hybrid is a
  reasonable middle ground; large native app
  frameworks bloat install size. (2)

• Bandwidth requirements. Workers have intermittent
  connectivity. The app MUST work offline —
  record visits locally, then sync when signal
  returns. This pushes the decision AWAY from a
  pure mobile web app and TOWARDS a hybrid or
  native app that can store data locally. (2)

Award full marks for any defensible recommendation
that correctly explains why offline behaviour and
modest device specs drive the choice.`}
              </Spoiler>
            </div>
          </div>
        </Section>

        {/* ══════════════════════════════════════════════════════════════════════
             LEARNING UNIT 7 — ERRORS, THREATS & SECURITY
             ══════════════════════════════════════════════════════════════════════ */}

        <div className="mt-12 mb-8 pt-6 border-t-2 border-amber-400">
          <h2 className="text-2xl font-bold text-amber-800 text-center" style={{ fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" }}>Learning Unit 7 — Errors, Threats &amp; Security</h2>
          <p className="text-center text-sm text-slate-500 mt-1">Sources of Errors · Verification &amp; Validation · DBMS Integrity · Threats · Security Solutions</p>
        </div>

        {/* ══════════════ 7.1 SOURCES OF ERRORS ══════════════ */}
        <Section id="error-sources" title="7.1 Sources of Errors" completed={completed["error-sources"]} onToggle={() => toggle("error-sources")}>
          <p>Computer systems handle data <strong>captured</strong> by humans, <strong>processed</strong> by software, <strong>transmitted</strong> across networks, and <strong>stored</strong> on physical media. Each of those steps can introduce errors. The Gr11 skill is to recognise <em>where</em> errors come from and which defence to point at each kind.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.1.1 The four categories</h3>
          <Table
            headers={["Source", "Where it enters", "Typical example"]}
            rows={[
              ["Human / input", "When data is captured by a user", "Salary captured as R 350 000 instead of R 35 000"],
              ["Arithmetic", "When fixed-bit calculations exceed their representation", "200 + 100 in 8-bit unsigned int wraps to 44"],
              ["Transmission", "When bits flip on cable or wireless", "EMI flips one bit in a network frame"],
              ["Programming / logical", "When the program's logic is wrong", "A < where <= was needed → off-by-one"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.1.2 Human and input errors — GIGO</h3>

          <KeyTerm term="GIGO (Garbage In, Garbage Out)">The principle that incorrect input produces incorrect output. A computer cannot fix wrong data; if a salary is captured wrongly, every report based on it is wrong &mdash; however perfect the rest of the system.</KeyTerm>
          <KeyTerm term="Input error">Any error introduced when data is captured &mdash; typos, missing fields, transposed digits, wrong units, picking the wrong dropdown option, scanning a damaged barcode.</KeyTerm>

          <p className="mt-3 text-sm">Almost every defence in 7.2 exists to reduce input errors at source or catch them just after capture. The cheapest fix is to <em>not let the bad data in</em>; the second-cheapest is to flag it before it propagates.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.1.3 Arithmetic errors</h3>
          <p>Numbers in a computer are stored in a <strong>fixed</strong> number of bits (LU1 §1.4). The bit count caps the range of representable values, and operations near the edge of that range can produce wrong answers <em>silently</em>.</p>

          <KeyTerm term="Overflow">A calculation produces a value larger than the fixed-bit type can store. An 8-bit unsigned int holds 0&ndash;255; computing 200 + 100 wraps to 44 (or raises an exception, depending on the language). Always a bug; sometimes silent.</KeyTerm>
          <KeyTerm term="Underflow">The opposite of overflow &mdash; a value too small (in magnitude or below the minimum) to represent. Common with floating-point: a very small positive number rounds to zero.</KeyTerm>
          <KeyTerm term="Rounding error">Small inaccuracies that result from representing real numbers in finite precision. <code>0.1 + 0.2</code> in 64-bit float is <strong>not</strong> exactly <code>0.3</code> &mdash; it's <code>0.30000000000000004</code>. Negligible per operation; significant after many.</KeyTerm>
          <KeyTerm term="Truncation error">Discarding digits beyond a fixed precision. <code>7.99</code> truncated to one decimal is <code>7.9</code>, not <code>8.0</code>.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.1.4 Transmission errors</h3>
          <p>Bits travel across cables (LU4) or wireless links (LU5). They can arrive at the receiver <em>different</em> from how they left the sender.</p>

          <KeyTerm term="Transmission error">Bits arrive at the receiver different from those that left the sender. Caused by <strong>EMI</strong>, <strong>attenuation</strong>, or <strong>cross-talk</strong> on cable; by interference, range, or weak signal on wireless.</KeyTerm>
          <KeyTerm term="Bit flip">A single bit changes value during transmission (or in faulty memory). Detected by parity / checksums / FCS; recovered by retransmission (TCP).</KeyTerm>

          <InfoBox title="Layered detection" color="blue">
            Real protocols don't rely on one check. A network frame is protected by an FCS at the data-link layer, a TCP checksum at the transport layer, and (for downloads) a SHA-256 hash at the application layer. Each layer independently catches what the lower one missed.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.1.5 Programming errors</h3>

          <KeyTerm term="Logical error">The program runs without crashing but produces a wrong result because the logic is wrong &mdash; a <code>&lt;</code> where <code>&lt;=</code> was needed, an off-by-one, the wrong variable used. Hardest to spot: the program looks fine until someone double-checks the output.</KeyTerm>

          <p className="mt-3 text-sm italic text-slate-600">Logical errors are <em>not</em> caught by the compiler (the syntax is fine) and <em>not</em> caught by the runtime (the program doesn't crash). They are caught by <strong>testing</strong>, structured walk-throughs, code review, and validation at boundaries.</p>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>name the four sources of errors</strong> with one example each. Know that <strong>overflow is arithmetic, not transmission</strong>. Know that <strong>logical errors compile and run without crashing</strong>.
          </div>
        </Section>

        {/* ══════════════ 7.2 VERIFICATION & VALIDATION ══════════════ */}
        <Section id="error-solutions" title="7.2 Verification & Validation" completed={completed["error-solutions"]} onToggle={() => toggle("error-solutions")}>
          <p>The two phrases that confuse most learners are <strong>verification</strong> and <strong>validation</strong>. They sound similar; they mean different things; an exam will test the distinction.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.2.1 The core distinction</h3>
          <Table
            headers={["", "Verification", "Validation"]}
            rows={[
              ["Question it asks", "Did I capture what the user intended?", "Does the captured value make sense?"],
              ["Tests against", "The original source", "A set of rules"],
              ["Catches", "Typos, mis-keyed fields", "Out-of-range values, wrong types, missing data"],
              ["Example technique", "Double entry · Visual check · Read-back", "Range check · Type check · Check digit"],
            ]}
          />
          <p className="mt-3 text-sm italic text-slate-600">If a learner types their date of birth as <code>1812-05-04</code> (200 years ago), <strong>validation</strong> can flag it (range check on year). <strong>Verification</strong> would only catch this if the user had to confirm the captured value matched what they meant to type. Different protections; both useful; neither replaces the other.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.2.2 Verification techniques</h3>
          <KeyTerm term="Double entry">The user types the value twice; the system compares. Catches random typos that aren't repeated identically. Used historically for ATM PIN setup, password creation, payroll capture.</KeyTerm>
          <KeyTerm term="Visual check">The user reads the captured value back against the source document. Slow but cheap; relies on the user being careful.</KeyTerm>
          <KeyTerm term="Read-back">The system displays or speaks the captured value and asks the user to confirm. Common in banking apps and call centres.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.2.3 Validation checks</h3>
          <Table
            headers={["Check", "Rule it enforces", "Example"]}
            rows={[
              ["Presence", "Field must not be empty", "Required name field"],
              ["Range", "Value lies between min and max", "Age 0–120; mark 0–100"],
              ["Length", "Input has the expected number of characters", "SA ID = 13 digits; SA postcode = 4 digits"],
              ["Type", "Input is the expected data type", "Numeric field rejects letters; date field rejects malformed dates"],
              ["Uniqueness", "Value not already in the database", "Email at sign-up; student number"],
              ["Logical", "Value consistent with other fields", "Date of joining ≤ date of leaving"],
              ["Check digit", "Appended digit recomputes correctly", "SA ID, ISBN, credit-card numbers (Luhn)"],
              ["Checksum", "Value computed over a whole file matches", "Verifying a downloaded file's integrity"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.2.4 Input techniques — reduce errors at source</h3>
          <p>The cheapest validation is the one that's never needed. <strong>Don't ask the user to type what you can ask them to pick or scan.</strong></p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>Barcode / QR / RFID</strong> &mdash; eliminates typing the product code or ID; near-zero error rate when readable.</li>
            <li><strong>OCR</strong> &mdash; converts an image of text into editable digital text. Eliminates re-typing whole pages; introduces small errors (an <code>O</code> for a <code>0</code>).</li>
            <li><strong>Biometric input</strong> &mdash; identifies a person by a physical trait (fingerprint, iris, face, voice). Removes the &ldquo;did I type the right credentials&rdquo; failure mode entirely; introduces sensor failure modes.</li>
            <li><strong>GUI controls:</strong> dropdown for fixed list · checkbox for independent multiple choices · radio button for mutually exclusive choices · date picker for dates.</li>
          </ul>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.2.5 Transmission checks</h3>
          <KeyTerm term="Parity bit">An extra bit appended to a byte so the total number of <code>1</code>s is <strong>even</strong> (even parity) or <strong>odd</strong> (odd parity), as agreed in advance. Receiver counts; mismatch = at least one bit flipped. Cheap and old; catches <strong>single-bit errors only</strong>.</KeyTerm>

          <p className="mt-2 text-sm">Worked example: byte <code>0101 1010</code> with even parity. Count the <code>1</code>s: four (already even). Parity bit = <code>0</code>; transmitted: <code>0101 1010 0</code>. If EMI flips bit two during transmission &rarr; receiver sees three <code>1</code>s &rarr; odd &rarr; error detected. <strong>Limitation:</strong> if <em>two</em> bits flip, the count returns to even and the corruption slips past parity. Real protocols use stronger checks (FCS, TCP checksum, SHA hashes) for this reason.</p>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>distinguish verification from validation</strong> with the question each one asks and one technique each. Know <strong>at least four validation checks</strong> with their rules. Be able to walk through a <strong>parity</strong> example and state the single-bit limitation.
          </div>
        </Section>

        {/* ══════════════ 7.3 DBMS INTEGRITY ══════════════ */}
        <Section id="dbms-integrity" title="7.3 DBMS Integrity" completed={completed["dbms-integrity"]} onToggle={() => toggle("dbms-integrity")}>
          <p>A database is only as useful as the quality of the data it contains. The Gr11 SAG names <strong>five properties</strong> a well-managed database keeps in mind &mdash; sometimes referred to as the <em>five Cs</em>, though the SAG uses slightly different names.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.3.1 The five integrity properties</h3>
          <Table
            headers={["Property", "What it measures", "Failure example"]}
            rows={[
              ["Accuracy", "Captured value matches reality (no typos, wrong units)", "Salary captured as R 350 000 instead of R 35 000"],
              ["Correctness", "Internally consistent — follows the system's rules", "Foreign key points to a deleted record (orphan)"],
              ["Currency", "Up-to-date — reflects today's reality", "Phone number five years old; price last updated in 2022"],
              ["Completeness", "All required fields populated; no missing records", "Learner record with no date of birth; missing day's transactions in a sales report"],
              ["Relevance", "Captured data is appropriate for the purpose", "Storing every keystroke when only the saved value matters — irrelevant + privacy-violating"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.3.2 Why each one matters</h3>

          <KeyTerm term="Accuracy">The captured data correctly reflects reality &mdash; no typos, no transcription errors, no wrong units. Often confused with &ldquo;correctness&rdquo;; accuracy is about the <em>value matching reality</em>.</KeyTerm>
          <KeyTerm term="Correctness">The data is internally consistent and follows the system's rules &mdash; <strong>referential integrity</strong>, foreign-key constraints, no orphan records, no contradictions across tables.</KeyTerm>
          <KeyTerm term="Currency">The data is <strong>up-to-date</strong> &mdash; phone numbers, addresses, prices, stock counts reflect today's reality. Stale data may still be &ldquo;accurate&rdquo; for a past moment but useless now.</KeyTerm>
          <KeyTerm term="Completeness">All required fields are populated; no records are missing. A learner record without a date of birth is not complete; a sales report missing one day's transactions is not complete.</KeyTerm>
          <KeyTerm term="Relevance">The data captured is <strong>appropriate for the purpose</strong>. Storing every keystroke for a payroll system is irrelevant; storing only what's relevant respects privacy and reduces clutter.</KeyTerm>

          <InfoBox title="The DBMS does some of this automatically" color="green">
            A relational DBMS enforces some of these properties without the developer doing extra work. <strong>Foreign-key constraints</strong> prevent orphan records (correctness). <strong>NOT NULL</strong> constraints prevent some completeness gaps. <strong>UNIQUE</strong> constraints prevent some accuracy issues. But <em>currency</em> and <em>relevance</em> are policy questions &mdash; the DBMS can't tell you that a record is stale or irrelevant; people have to.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.3.3 The audit trail's role</h3>
          <KeyTerm term="Audit trail (DB)">A log of every change to a database record &mdash; <em>who</em> updated, <em>when</em>, the <em>before</em> and <em>after</em> values. Lets the school trace bad data back to its source and roll back if needed.</KeyTerm>

          <p className="mt-2 text-sm">Audit trails are how a school finds out that an integrity failure happened &mdash; and who introduced it. Without a log, a wrong record in the database is just a wrong record; with a log, it's a tractable problem.</p>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>name and define the five integrity properties</strong>: Accuracy, Correctness, Currency, Completeness, Relevance. For each, give a <em>failure example</em>. Examiners reward concrete examples over abstract definitions.
          </div>
        </Section>

        {/* ══════════════ 7.4 THREATS ══════════════ */}
        <Section id="threats" title="7.4 Threats" completed={completed["threats"]} onToggle={() => toggle("threats")}>
          <p>Section 7.1 covered the errors that creep in <em>accidentally</em>. This section covers the <strong>deliberate</strong> threats &mdash; everything someone or something might actively do to damage the data, the systems, or the people using them.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.4.1 Physical and environmental threats</h3>
          <KeyTerm term="Hardware failure">Components fail eventually. <strong>HDDs</strong> wear mechanically; <strong>SSDs</strong> wear by write cycles; PSUs and motherboards fail electrically. A single failure can lose every byte on the affected device.</KeyTerm>
          <KeyTerm term="Power failure">The electricity supply is cut. Running computers shut down uncleanly; unsaved work is lost; databases mid-write may corrupt. South African <strong>load-shedding</strong> makes this an everyday risk.</KeyTerm>
          <KeyTerm term="Power surge">A sudden spike in voltage (lightning, grid switching). Can fry electronics in milliseconds &mdash; far more damage than a clean cut.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.4.2 Malware</h3>
          <KeyTerm term="Malware">Umbrella term for malicious software &mdash; virus, worm, ransomware, spyware, trojan. Distinguished by what they <em>do</em> and how they <em>spread</em>.</KeyTerm>

          <Table
            headers={["Type", "What it does", "How it spreads"]}
            rows={[
              ["Virus", "Runs malicious code when triggered", "Attaches to a host file/program; spreads when the file is shared"],
              ["Worm", "Same as virus but self-propagating", "Self-replicates across networks; doesn't need a host file"],
              ["Ransomware", "Encrypts the user's files and demands payment for the key", "Email attachments; drive-by downloads; exploits unpatched services"],
              ["Spyware", "Silently observes — keystrokes, screens, web activity — sends data home", "Bundled with free software; phishing payloads"],
              ["Trojan", "Disguised as legitimate software; hides malicious payload", "User installs willingly, believing the disguise"],
            ]}
          />

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.4.3 Deception &mdash; phishing, pharming, spoofing</h3>
          <KeyTerm term="Phishing">Tricking a user into giving up credentials by <strong>impersonating a trusted party</strong> &mdash; bank, employer, school IT. Usually via email; SMS = <em>smishing</em>; voice = <em>vishing</em>.</KeyTerm>
          <KeyTerm term="Pharming">Quietly redirecting a user from a real site to a fake one <strong>without changing the URL they typed</strong> &mdash; typically by poisoning DNS or hosts files. Worse than phishing because the user did nothing wrong.</KeyTerm>
          <KeyTerm term="Spoofing">Falsifying the source of a message &mdash; the <strong>From:</strong> address, the caller-ID, the source IP. The technical foundation that makes phishing and pharming convincing.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.4.4 Network-level threats</h3>
          <KeyTerm term="Botnet">A network of compromised computers (&ldquo;<em>zombies</em>&rdquo;) controlled remotely by an attacker. Used for DDoS, spam, cryptocurrency mining, spreading further malware. The owner of an infected machine usually doesn't know it's part of a botnet.</KeyTerm>
          <KeyTerm term="DoS / DDoS">Denial of Service &mdash; flooding a server with so many requests it can't respond to legitimate users. <strong>D</strong>DoS = <em>distributed</em>, launched from a botnet (many sources at once). Takes <em>availability</em> away; the attacker doesn't gain access.</KeyTerm>
          <KeyTerm term="Open port">A network port with a service listening on it. Each open port is a potential <strong>entry point</strong>; reducing the open-port footprint shrinks the attack surface.</KeyTerm>
          <KeyTerm term="SQL Injection">An attack that smuggles SQL commands into an input field, tricking the database into executing them &mdash; leaking, modifying, or deleting data. Defended by <strong>parameterised queries</strong> and input validation.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.4.5 Wi-Fi vulnerabilities</h3>
          <KeyTerm term="Wi-Fi vulnerabilities">Weak passphrases (<code>Helpmekaar123</code>), outdated encryption (WEP &mdash; broken; WPA &mdash; weak), open networks (no encryption at all), default router-admin passwords, evil-twin access points (an attacker's AP impersonating the legitimate one).</KeyTerm>

          <InfoBox title="The CIA triad" color="blue">
            Standard security framing &mdash; threats attack one or more of:
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li><strong>Confidentiality</strong> &mdash; only the right people see the data. Spyware, phishing, eavesdropping break this.</li>
              <li><strong>Integrity</strong> &mdash; the data is correct and unaltered. Ransomware encryption, SQL injection, malicious updates break this.</li>
              <li><strong>Availability</strong> &mdash; the data is reachable when needed. DoS / DDoS, hardware failure, power loss break this.</li>
            </ul>
            Different defences protect different parts of the triad. Encryption protects confidentiality but does nothing for availability; backups protect availability but do nothing for confidentiality.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>distinguish virus / worm / ransomware</strong> in one sentence each. Know the <strong>phishing / pharming / spoofing</strong> distinction. Understand that <strong>DDoS doesn't gain access</strong>; it takes availability away. Know that <strong>WPA3 &gt; WPA2 &gt; WPA &gt; WEP</strong> in security strength.
          </div>
        </Section>

        {/* ══════════════ 7.5 SECURITY SOLUTIONS ══════════════ */}
        <Section id="security-solutions" title="7.5 Security Solutions" completed={completed["security-solutions"]} onToggle={() => toggle("security-solutions")}>
          <p>No single defence is enough. Real security is <strong>defence in depth</strong> &mdash; physical security, network security, OS security, application security, user training, and backups, all working together. If one layer fails, others catch the threat.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.5.1 Hardware redundancy &mdash; RAID</h3>

          <KeyTerm term="RAID">Redundant Array of Independent Disks &mdash; multiple drives configured to behave as one, with extra capacity used for redundancy or speed.</KeyTerm>
          <KeyTerm term="Mirroring (RAID 1)">Every block written to disk A is also written to disk B. Either disk can fail with no data loss. Cost: half the raw capacity.</KeyTerm>
          <KeyTerm term="Striping with parity (RAID 5)">Data striped across 3+ disks; one disk's worth of capacity is parity, spread across the array. Survives one disk failure; uses (n&minus;1)/n of capacity. Slower writes; long rebuild time.</KeyTerm>

          <Table
            headers={["Aspect", "RAID 1 (mirror)", "RAID 5 (striping + parity)"]}
            rows={[
              ["Minimum disks", "2", "3"],
              ["Storage efficiency", "50%", "(n−1)/n — better"],
              ["Survives 1 disk failure", "Yes", "Yes"],
              ["Survives 2 simultaneous failures", "No (with 2 disks)", "No"],
              ["Write performance", "Normal", "Slower (parity must be computed)"],
              ["Best for", "Small servers, high-value data", "Larger arrays where storage efficiency matters"],
            ]}
          />

          <InfoBox title="RAID is NOT a backup" color="orange">
            RAID protects against <em>one disk failing</em>. It does <strong>not</strong> protect against ransomware (which encrypts both copies), accidental deletion (which deletes from both), fire (which destroys both), or theft (which steals both). <strong>Backups are still required.</strong>
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.5.2 Backup &mdash; the single most important defence</h3>
          <KeyTerm term="Backup">A separate copy of data, made regularly, kept somewhere different from the original. The single most important defence against ransomware, hardware failure, fire, and theft.</KeyTerm>
          <KeyTerm term="Local vs Remote backup"><strong>Local</strong> = same building (external HDD, NAS) &mdash; fast restore but vulnerable to fire / theft / ransomware spreading on the LAN. <strong>Remote / off-site / cloud</strong> = another building or cloud provider &mdash; slow restore (Internet bandwidth) but survives building-level disasters.</KeyTerm>
          <KeyTerm term="3-2-1 backup rule">Keep <strong>3</strong> copies of important data, on <strong>2</strong> different media, with <strong>1</strong> copy off-site. The lowest-effort discipline that survives most realistic disasters.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.5.3 Power protection</h3>
          <KeyTerm term="UPS (Uninterruptible Power Supply)">A battery-backed power source that bridges short outages and gives time for clean shutdown during longer ones. Also <strong>conditions</strong> the supply, smoothing out surges and dips. The first line of defence against load-shedding.</KeyTerm>
          <KeyTerm term="Redundant power supply">A server with <strong>two PSUs</strong> fed from independent power circuits; if one fails or its circuit drops, the other keeps the server running.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.5.4 Authentication &amp; authorisation</h3>
          <KeyTerm term="Strong password">Long (&gt;12 characters), not based on guessable words, not reused across sites. Modern advice: pass<em>phrases</em> over short complex passwords; a password manager so each site has a unique random one.</KeyTerm>
          <KeyTerm term="Multi-factor authentication (MFA)">Login requires more than just a password &mdash; a code from an authenticator app, a hardware token, or a biometric. <strong>Even if the password is stolen, the attacker can't get in.</strong></KeyTerm>
          <KeyTerm term="Principle of least privilege">Every user (and program) gets <em>only</em> the permissions they actually need. A learner account shouldn't install software; a teacher account shouldn't be a domain admin. Limits the blast radius if any one account is compromised.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.5.5 Encryption</h3>
          <KeyTerm term="Encryption">Converting readable data (&ldquo;plaintext&rdquo;) into unreadable form (&ldquo;ciphertext&rdquo;) using a key. Only someone with the right key can decrypt. <strong>At rest</strong> = files stored encrypted on disk (BitLocker, FileVault); <strong>in transit</strong> = data encrypted on the wire (TLS / HTTPS / WPA3).</KeyTerm>

          <Table
            headers={["", "Symmetric", "Asymmetric"]}
            rows={[
              ["Keys", "One shared key", "Public + private key pair"],
              ["Speed", "Fast", "Slower (more computation)"],
              ["Key exchange problem", "Yes — both parties must share the key securely first", "No — public key is public; only the private key decrypts"],
              ["Common algorithms", "AES-128, AES-256", "RSA, ECC"],
              ["Used for", "Bulk data — files, traffic, Wi-Fi (WPA3)", "Key exchange, digital signatures, HTTPS server identity"],
            ]}
          />

          <KeyTerm term="Key length">The size of the encryption key in bits. Longer keys are <strong>exponentially</strong> harder to brute-force. AES-128 is currently safe for most purposes; AES-256 is recommended for sensitive data and long-term secrets.</KeyTerm>
          <KeyTerm term="Digital signature">Uses public-key cryptography to prove a message was sent by the holder of a particular private key, and that the message wasn't altered in transit. Basis for HTTPS server identity, signed software updates, secure email.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.5.6 Network security</h3>
          <KeyTerm term="Firewall">Hardware or software that <strong>inspects</strong> traffic entering and leaving a network and blocks anything that violates the rules. Edge firewall sits between the LAN and the Internet; modern OSes also include host-based firewalls per machine.</KeyTerm>
          <KeyTerm term="Port filtering">The firewall rule pattern of <strong>allowing or blocking traffic by destination port</strong>. Only the ports needed for legitimate services are open; everything else is closed by default. Drastically reduces the <strong>attack surface</strong>.</KeyTerm>
          <KeyTerm term="WPA2 / WPA3">Wi-Fi encryption standards. WPA2 (2004) has been the workhorse; <strong>WPA3 (2018)</strong> replaces it with stronger handshake and individualised data encryption. New equipment should default to WPA3 with WPA2 fallback for older devices.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">7.5.7 Operational defences</h3>
          <KeyTerm term="Audit trail">A log of who did what, when. Records logins, configuration changes, access to sensitive records. Needed for <strong>incident response</strong>, <strong>compliance</strong>, and <strong>deterrence</strong> (users behave better when they know they're logged).</KeyTerm>
          <KeyTerm term="Anti-malware">Software that detects and removes viruses, worms, ransomware, spyware. Modern anti-malware combines <strong>signature</strong> matching (known threats) with <strong>behavioural</strong> detection (rapid mass encryption is a ransomware tell). Must be kept up-to-date.</KeyTerm>
          <KeyTerm term="Patching / Updates">Software fixes for known bugs and security vulnerabilities. <strong>Most successful attacks exploit known vulnerabilities for which a patch already exists</strong> &mdash; the victim hadn't installed it. Routine patching is one of the highest-value security activities.</KeyTerm>

          <InfoBox title="A worked attack chain — phishing → ransomware" color="blue">
            <ol className="list-decimal list-inside space-y-1 mt-2">
              <li>Spoofed email impersonates &ldquo;IT Support&rdquo; &mdash; <em>blocked by</em> email filtering + user training.</li>
              <li>User clicks a fake login link &mdash; <em>blocked by</em> browser HTTPS warnings + password manager.</li>
              <li>User types credentials into the fake page &mdash; <em>blocked by</em> MFA (the attacker still can't log in).</li>
              <li>Attacker logs in, uploads ransomware &mdash; <em>contained by</em> least-privilege user rights + anti-malware.</li>
              <li>Ransomware spreads, encrypts files &mdash; <em>contained by</em> network segmentation; <em>recovered by</em> off-site backup.</li>
            </ol>
            Every layer matters. The same attack would have been blocked at step 3 if MFA was deployed, or recovered at step 5 by reliable off-site backups.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Recommend <strong>layered</strong> defences &mdash; one technical (encryption, MFA), one operational (backup, audit trail), one human (training). Know <strong>RAID is not a backup</strong> and the <strong>3-2-1 rule</strong>. Be able to walk through symmetric vs asymmetric encryption with the <strong>key-exchange problem</strong> as the discriminating factor.
          </div>
        </Section>

        {/* ══════════════ LU7 PRACTICE EXERCISES ══════════════ */}
        <Section id="exercises-lu7" title="LU7 Practice Exercises" completed={completed["exercises-lu7"]} onToggle={() => toggle("exercises-lu7")}>
          <p className="mb-4 text-sm italic text-slate-600">
            Work through each exercise on paper first, then check your answer. These exercises cover all Learning Unit 7 topics. Where a question is paraphrased from a past paper, the source is noted.
          </p>

          <div className="space-y-6 text-sm">
            <div>
              {/* Authored — no past paper covers 11.2.7 */}
              <p className="font-semibold">Exercise 1 &mdash; Verification vs Validation</p>
              <p className="text-sm mt-1">Distinguish between <em>verification</em> and <em>validation</em> of input data. Give ONE example technique used by each. <em>(4 marks)</em></p>
              <Spoiler>
{`• Verification — confirming that captured data
  MATCHES ITS SOURCE — that the value was entered
  correctly. Asks: 'did I capture what the user
  intended?' (1)
  Example technique: double entry (the user types
  it twice; the system compares); read-back; visual
  check. (1)

• Validation — confirming that captured data is
  SENSIBLE — within expected ranges, formats, or
  constraints. Asks: 'does the captured value make
  sense?' (1)
  Example technique: range check, presence check,
  type check, format check, check digit. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.7 */}
              <p className="font-semibold">Exercise 2 &mdash; Four sources of errors</p>
              <p className="text-sm mt-1">Name FOUR sources of errors that can affect data in a computer system, and give ONE example of each. <em>(8 marks)</em></p>
              <Spoiler>
{`Any 4 (2 marks each — 1 for naming, 1 for example):

• Human / input error — typos, missed fields,
  transposed digits. Example: typing R 350 000
  instead of R 35 000.
• Arithmetic error — overflow, rounding,
  truncation in fixed-bit calculations. Example:
  a sum of bytes overflows an 8-bit register and
  wraps to a small value.
• Transmission error — bits flip during
  transmission due to EMI, attenuation, cross-talk.
  Example: a network frame received with one bit
  flipped, caught by the FCS.
• Programming / logical error — buggy logic
  produces wrong results without crashing. Example:
  a < used where <= was needed, causing an
  off-by-one in a count.`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.7 */}
              <p className="font-semibold">Exercise 3 &mdash; Match the validation check</p>
              <p className="text-sm mt-1">Match each validation check to the rule it enforces: (a) Presence (b) Range (c) Length (d) Type (e) Uniqueness (f) Check digit. Rules: (i) value lies between min and max · (ii) value has expected number of characters · (iii) value is the expected data type · (iv) field is not empty · (v) value is not already in the database · (vi) appended digit recomputes correctly. <em>(6 marks)</em></p>
              <Spoiler>
{`(a) Presence    → (iv) field is not empty
(b) Range       → (i)  value between min and max
(c) Length      → (ii) expected number of characters
(d) Type        → (iii)expected data type
(e) Uniqueness  → (v)  not already in the database
(f) Check digit → (vi) appended digit recomputes
                       correctly`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.7 */}
              <p className="font-semibold">Exercise 4 &mdash; Parity bit and its limitation (L2)</p>
              <p className="text-sm mt-1">Explain <em>parity</em> as a transmission-error check, and discuss ONE limitation. <em>(5 marks)</em></p>
              <Spoiler>
{`• A parity bit is an extra bit appended to a group
  of bits (typically a byte) so that the total number
  of 1s in the group is EVEN (even parity) or ODD
  (odd parity), as agreed in advance. (2)

• The receiver counts the 1s; if the count violates
  the agreed convention, at least one bit flipped
  during transmission, and the byte is rejected (or
  retransmission is requested). (1)

• Limitation: parity only catches SINGLE-BIT errors.
  If TWO bits flip in the same byte, the count of 1s
  is unchanged (or changed by 2 — same parity), and
  the corruption goes undetected. (2)

• Real protocols use stronger checks (Frame Check
  Sequence on Ethernet; TCP checksum; cryptographic
  hashes for files) for this reason.`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.4.9 */}
              <p className="font-semibold">Exercise 5 &mdash; The five DBMS integrity properties</p>
              <p className="text-sm mt-1">Name and define the FIVE properties used to measure DBMS data integrity. For each, give a one-line failure example. <em>(10 marks)</em></p>
              <Spoiler>
{`2 marks each (1 for definition, 1 for failure
example):

• Accuracy — the captured value matches reality.
  Failure: salary captured as R 350 000 instead of
  R 35 000.
• Correctness — the data is internally consistent
  with the system's rules.
  Failure: a foreign key points to a deleted record
  (an orphan).
• Currency — the data is up-to-date.
  Failure: a learner's phone number is five years
  old; the contact list is useless.
• Completeness — all required fields are populated;
  no records are missing.
  Failure: a learner record without a date of birth.
• Relevance — the data captured is appropriate for
  the purpose.
  Failure: storing every keystroke when only the
  saved value matters — irrelevant and a privacy
  problem.`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.8 at L1 for malware */}
              <p className="font-semibold">Exercise 6 &mdash; Virus vs Worm vs Ransomware</p>
              <p className="text-sm mt-1">Distinguish between a <em>virus</em>, a <em>worm</em>, and <em>ransomware</em>. <em>(6 marks)</em></p>
              <Spoiler>
{`• Virus — malicious code that ATTACHES ITSELF to
  a host program or file and runs when the host
  runs. Spreads when the infected file is shared. (2)

• Worm — like a virus but SELF-PROPAGATING across
  networks; doesn't need a host file to spread.
  Examples: Morris Worm, ILOVEYOU, WannaCry. (2)

• Ransomware — encrypts the user's files and demands
  payment (usually in cryptocurrency) for the
  decryption key. Modern variants also exfiltrate
  data and threaten to publish it. (2)`}
              </Spoiler>
            </div>

            <div>
              {/* Source: ieb_it_2023_p2 Q5.2.1 — paraphrased; expanded with worked answer */}
              <p className="font-semibold">Exercise 7 &mdash; What does &ldquo;encrypted&rdquo; mean?</p>
              <p className="text-sm mt-1">What is meant by the term <em>encrypted</em> when referring to network traffic? <em>(2 marks)</em></p>
              <Spoiler>
{`Encrypted network traffic has been converted from
its readable form ('plaintext') into an unreadable
form ('ciphertext') using an encryption key. (1)

Anyone intercepting the traffic on the wire sees
only ciphertext; only a party with the correct
decryption key can recover the plaintext. (1)

Used routinely on the web (HTTPS / TLS), on Wi-Fi
(WPA2 / WPA3), and for VPNs.`}
              </Spoiler>
            </div>

            <div>
              {/* Source: ieb_it_2023_p2 Q5.2.2(a)–(d) — paraphrased; combined into one multi-part exercise */}
              <p className="font-semibold">Exercise 8 &mdash; Symmetric vs Asymmetric Encryption (L2)</p>
              <p className="text-sm mt-1">An attacker has captured a wireless network's encrypted traffic. The encryption uses a 128-bit key. (a) How many different combinations would the attacker need to try to brute-force the key? Write your answer as a power. <em>(1)</em> (b) The encryption is <em>symmetric</em>. Name and describe ONE feature of symmetric encryption that is a <strong>weakness</strong>. <em>(2)</em> (c) Name an alternative encryption technique that overcomes that weakness, and explain how it does so. <em>(2)</em> (d) List ONE <strong>disadvantage</strong> of the technique you named in (c). <em>(1)</em></p>
              <Spoiler>
{`(a) 2^128 combinations. (1)
    (For AES-256: 2^256.)

(b) Weakness: KEY EXCHANGE. (1)
    Both the sender and the receiver need the SAME
    secret key. Sharing that key over the network
    safely is hard — if an attacker intercepts the
    key during exchange, they can decrypt all the
    traffic. (1)

(c) ASYMMETRIC encryption (also called public-key
    cryptography). (1)
    Each party has a PUBLIC + PRIVATE key pair. The
    public key can be shared openly; only the
    matching private key can decrypt messages
    encrypted with the public key. The key-exchange
    problem disappears — the public key being
    public is fine. (1)

(d) Disadvantage of asymmetric encryption (any 1):
    • SLOWER than symmetric — much more computation
      per byte; not practical for bulk data.
    • Larger keys are needed for equivalent
      strength (RSA-2048 ≈ AES-128 in security).

    (Real protocols use BOTH: asymmetric for the
    initial key exchange, then symmetric for the
    bulk data — TLS does exactly this.)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.8 */}
              <p className="font-semibold">Exercise 9 &mdash; RAID 1 vs RAID 5 (L2)</p>
              <p className="text-sm mt-1">Distinguish between <em>RAID 1</em> and <em>RAID 5</em>. Comment on storage efficiency and resilience. End your answer by stating whether either is a substitute for backup. <em>(7 marks)</em></p>
              <Spoiler>
{`• RAID 1 — MIRRORING. Every block is written to
  two (or more) disks identically. Survives one
  disk failure. Uses 50% of raw capacity. Minimum
  two disks. (2)

• RAID 5 — STRIPING WITH PARITY. Data is striped
  across three or more disks; one disk's worth of
  capacity is parity, spread across the array.
  Survives one disk failure. Uses (n−1)/n of raw
  capacity. Slower writes (parity must be computed);
  long rebuild time after a failure. (2)

• Comparison: RAID 5 is more storage-efficient
  (better usable capacity per disk count); RAID 1 is
  simpler and recovers faster. Both survive ONE disk
  failure; neither survives two simultaneous
  failures. (2)

• Neither is a substitute for backup. RAID protects
  against ONE DISK failing; it does NOT protect
  against ransomware (which encrypts both copies),
  accidental deletion, fire, or theft. (1)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.2.8 at L3 */}
              <p className="font-semibold">Exercise 10 &mdash; Recommend a security posture (L3)</p>
              <p className="text-sm mt-1">A small school is reviewing its data-protection posture. It currently has: a single HDD on its file server, no UPS, password-only login, and antivirus that auto-updates. List FIVE concrete improvements you would recommend, naming the <em>threat</em> each addresses. <em>(10 marks)</em></p>
              <Spoiler>
{`Sample answer (others defensible). 2 marks per
recommendation: 1 for the change, 1 for naming the
threat addressed.

1. Add RAID 1 (mirror the file-server HDD) AND an
   off-site / cloud backup.
   Threat: HDD failure (continued operation via
   RAID); ransomware / fire / theft (recovery via
   off-site backup). (2)

2. Install a UPS on the server and key network
   equipment.
   Threat: load-shedding / power failure (clean
   shutdown, continued availability for short
   outages); power surges. (2)

3. Enforce MFA on all staff accounts.
   Threat: phishing — even if the password is
   stolen, the attacker can't log in. (2)

4. Apply principle of least privilege — separate
   staff and learner accounts; admin accounts only
   used for admin work.
   Threat: lateral spread of any compromised
   account; ransomware blast radius reduced. (2)

5. Enable automatic OS and application patching,
   plus the existing antivirus.
   Threat: known vulnerabilities (most attacks
   exploit unpatched software); known malware
   variants. (2)

Other defensible items: WPA3 on Wi-Fi with a strong
passphrase; a separate guest VLAN; firewall with
default-deny port filtering; centralised audit
trail; user-awareness training; encryption of
staff laptops at rest (BitLocker).`}
              </Spoiler>
            </div>
          </div>
        </Section>

        {/* ══════════════════════════════════════════════════════════════════════
             LEARNING UNIT 8 — SOCIAL IMPLICATIONS
             ══════════════════════════════════════════════════════════════════════ */}

        <div className="mt-12 mb-8 pt-6 border-t-2 border-amber-400">
          <h2 className="text-2xl font-bold text-amber-800 text-center" style={{ fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" }}>Learning Unit 8 — Social Implications</h2>
          <p className="text-center text-sm text-slate-500 mt-1">Workplace &amp; Employment · Computer Crime · Safeguards · Evaluating Information</p>
        </div>

        {/* ══════════════ 8.1 WORKPLACE & EMPLOYMENT ══════════════ */}
        <Section id="workplace-employment" title="8.1 Workplace & Employment" completed={completed["workplace-employment"]} onToggle={() => toggle("workplace-employment")}>
          <p>Computers and the Internet have <strong>changed where, when, and how</strong> people work. The change is structural, not cosmetic &mdash; whole categories of work have moved out of the office, and whole categories have moved out of human hands altogether. Grade 11 IT looks at the social and ethical sides of those changes; the technical infrastructure (LAN/WAN, cloud, mobile) is covered in LU4&ndash;LU6.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">8.1.1 Decentralisation of work</h3>
          <p>Before reliable Internet, work meant <em>going to the office</em>. Today, for most information-economy roles, work is <strong>decentralised</strong> &mdash; staff work from anywhere with a connection.</p>

          <KeyTerm term="Decentralisation">The shift from work being concentrated in a single physical office to being distributed across locations — telecommuting, remote work, hybrid working, mobile offices, branch offices over WAN.</KeyTerm>
          <KeyTerm term="Telecommuting">Working from home (or any non-office location) with employer connectivity. Pre-2020 it was the exception; post-2020 it is the default for much of the information sector.</KeyTerm>
          <KeyTerm term="Mobile office">A workplace pattern where staff are routinely productive away from a fixed desk — laptop, smartphone, mobile data, cloud storage — turning any location into a temporary office.</KeyTerm>

          <InfoBox title="What changed during 2020" color="blue">
            The 2020 pandemic forced an experiment most employers had resisted: every white-collar role that <em>could</em> go remote, did. Five years on, <strong>hybrid working</strong> (some days at home, some in the office) is the dominant compromise for many firms. The technology that enabled it &mdash; reliable broadband, video conferencing, cloud-hosted documents &mdash; existed before 2020 but was used as a backup; afterwards it became the default.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">8.1.2 Acceptable User Policies (AUPs)</h3>
          <p>Once staff use employer IT &mdash; especially from home, on personal devices, over public networks &mdash; the question is no longer whether something is <em>technically possible</em> but whether it is <em>permitted</em>. An <strong>AUP</strong> is the document that draws that line.</p>

          <KeyTerm term="Acceptable User Policy (AUP)">A written policy defining what employees (or learners, in a school) may and may not do with employer-provided IT — devices, accounts, network access, data. Signed by the user as a condition of access.</KeyTerm>

          <p className="mt-3">A typical AUP covers:</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li>Appropriate use of email and the Internet (no harassment, no illegal content).</li>
            <li>Security responsibilities (password hygiene, MFA, locking screens, reporting incidents).</li>
            <li>Ownership of work produced (employer typically owns work products created on company time / equipment).</li>
            <li>Consequences of breach (warning &rarr; dismissal &rarr; legal action depending on severity).</li>
          </ul>
          <p className="mt-2 text-sm italic text-slate-600">Schools have AUPs too. The one your parents signed when you enrolled is the legal basis for a teacher confiscating a phone, blocking a website, or excluding a learner who misuses the network.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">8.1.3 Replacement of the workforce — automation</h3>
          <p>Three categories of automation are explicitly named in the SAG:</p>

          <KeyTerm term="Robotics">Replacement (or augmentation) of human physical labour by programmable machines — industrial robots in factories, warehouse robots in logistics, surgical robots in hospitals.</KeyTerm>
          <KeyTerm term="Artificial intelligence (AI)">Software systems that perform tasks formerly requiring human intelligence — language understanding, image recognition, decision support, content generation. Modern AI (deep learning, large language models) is far more capable than 1990s–2010s 'AI'.</KeyTerm>
          <KeyTerm term="UAV (drone)">Unmanned Aerial Vehicle — a flying robot, remotely piloted or autonomous. Used in agriculture, security, parcel delivery, film-making, surveying.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">8.1.4 Effects on employment — both sides</h3>
          <Table
            headers={["Positive effects", "Negative effects"]}
            rows={[
              ["Higher productivity — automated systems work 24/7 without breaks.", "Job displacement — workers replaced by automation face unemployment."],
              ["New roles emerge — AI supervision, data labelling, prompt engineering, drone operators.", "Disproportionate impact on lower-skilled workers — re-training is not automatic."],
              ["Dangerous work removed from humans — drones inspect dangerous infrastructure; robots handle hazardous chemicals.", "Concentration of economic gain — productivity gains accrue to capital owners more than to workers."],
              ["Access to remote / under-served areas — drones reach farms and clinics ground vehicles cannot.", "Privacy concerns — drones with cameras over residential areas raise surveillance worries."],
              ["Cost savings — over time, automation reduces per-unit operating cost.", "Loss of human contact — couriers and clerks were also informal social check-ins for elderly or isolated customers."],
            ]}
          />

          <InfoBox title="The honest answer in the exam" color="orange">
            Examiners look for a <strong>balanced</strong> answer &mdash; positives <em>and</em> negatives, with brief justification for each. Don&rsquo;t take a one-sided position. The skill being tested is <em>weighing the trade-off</em>, not picking a side.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to define <strong>decentralisation</strong>, <strong>telecommuting</strong>, and <strong>AUP</strong> in one sentence each. Know what an AUP typically covers and why a school has one. For automation questions, give <strong>three positives and three negatives</strong> with concrete examples (couriers, factory workers, drones, AI-generated content).
          </div>
        </Section>

        {/* ══════════════ 8.2 COMPUTER CRIME ══════════════ */}
        <Section id="computer-crime" title="8.2 Computer Crime" completed={completed["computer-crime"]} onToggle={() => toggle("computer-crime")}>
          <p>Section 11.2.8 (covered in LU7) deals with the <em>technical</em> side of computer crime &mdash; firewalls, encryption, RAID, backups. This section covers the <strong>human and ethical</strong> side: the people who commit the crimes, the methods they use against <em>users</em> rather than systems, and the categories of crime the SAG names explicitly.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">8.2.1 Social engineering — attacking the user</h3>
          <p>The strongest password and the best firewall are useless if the attacker just <em>asks</em> for the password and the user gives it. <strong>Social engineering</strong> is the umbrella term for attacks that bypass technology by manipulating the human in the loop.</p>

          <KeyTerm term="Social engineering">Manipulating PEOPLE (not computers) to obtain information, access, or actions that benefit an attacker. Bypasses technical defences by going through the user.</KeyTerm>

          <p className="mt-3">The SAG names six tactics:</p>
          <Table
            headers={["Tactic", "Concrete example", "Primary mitigation"]}
            rows={[
              ["Shoulder surfing", "A learner watches the IT teacher type the admin password during a lesson", "Privacy filters; type passwords away from observers; cover the keyboard"],
              ["Dumpster diving", "Last term's printed mark schedule is binned; an outsider retrieves it", "Shred sensitive paper; certified data-erasure for old hard drives"],
              ["Phishing", "An email 'from the principal' asks staff to verify their portal credentials at a fake URL", "User awareness training; email filtering; MFA"],
              ["Trojan horse (social)", "A 'free school timetable app' on social media installs spyware alongside the timetable", "Install software only from approved sources; anti-malware; least privilege"],
              ["Reverse social engineering", "'IT Help' cards left near the photocopier; staff call the attacker for support", "Verify support requests through known channels only; defined incident-response"],
              ["Social-media engineering", "An attacker reads a teacher's Facebook profile, then phishes referencing her child's name", "Lock down public-profile visibility; awareness of common pretexts"],
            ]}
          />

          <InfoBox title="The recurring lesson" color="orange">
            Technical defences alone do not stop social engineering &mdash; only <strong>training, awareness, and procedures</strong> do. Ten years of strong passwords mean nothing if someone tells the attacker theirs.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">8.2.2 Hackers, crackers, virus authors</h3>
          <p>The SAG distinguishes between three terms commonly confused in the press.</p>

          <KeyTerm term="Hacker">Historically a neutral term for someone who explores computer systems creatively. Modern usage is loose — popular media uses 'hacker' to mean any computer-crime perpetrator.</KeyTerm>

          <Table
            headers={["Sub-category", "Authorisation", "Intent", "Legality"]}
            rows={[
              ["White-hat", "With permission", "Helpful — find and fix vulnerabilities", "Legal"],
              ["Black-hat", "Without permission", "Malicious — gain or harm", "Illegal"],
              ["Grey-hat", "Without permission", "No malicious intent; sometimes discloses publicly", "Murky / sometimes illegal"],
            ]}
          />

          <KeyTerm term="Cracker">An older term sometimes used specifically for a malicious hacker. In modern usage largely overtaken by 'black-hat hacker'.</KeyTerm>
          <KeyTerm term="Virus / malware author">Someone who writes malicious software (viruses, ransomware, spyware). May or may not be the same person who deploys it; modern malware is often bought and sold between criminal groups.</KeyTerm>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">8.2.3 Categories of computer crime</h3>
          <p>The SAG lists <strong>seven</strong> categories. Many chain together &mdash; defending the first link reduces the others.</p>

          <Table
            headers={["Category", "Example", "What enables the next link"]}
            rows={[
              ["Hardware theft", "School laptop stolen from a staff car", "Unencrypted disk → information theft"],
              ["Software theft (piracy)", "Cracked Adobe Suite installed on a school PC", "—"],
              ["Information theft", "Attacker exfiltrates the learner database", "Personal info → identity theft"],
              ["Identity theft", "Fraudulent loan opened in a teacher's name", "—"],
              ["Bandwidth theft", "Neighbour piggy-backs on unsecured Wi-Fi", "Hidden access point → information theft"],
              ["Time theft", "Staff member spends most of the day on social media", "—"],
              ["Service theft", "One Netflix login shared across a dozen people in violation of terms", "—"],
            ]}
          />

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Be ready to <strong>name and define social engineering</strong> and list <strong>three forms</strong> with a one-line example. Know the <strong>white-hat / black-hat / cracker</strong> distinction. List <strong>five of the seven categories</strong> of computer crime with one-sentence examples. Examiners reward concrete examples over abstract definitions.
          </div>
        </Section>

        {/* ══════════════ 8.3 SAFEGUARDS ══════════════ */}
        <Section id="safeguards" title="8.3 Safeguards" completed={completed["safeguards"]} onToggle={() => toggle("safeguards")}>
          <p>Most safeguards against computer crime are <em>technical</em> &mdash; firewalls, encryption, RAID, backups, anti-malware. Those live in LU7 (Errors, Threats &amp; Security). This section covers the <strong>human and procedural</strong> safeguards that pair with the technical ones to defend a school or business.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">8.3.1 Three layers of defence</h3>
          <Table
            headers={["Layer", "What it protects against", "Examples"]}
            rows={[
              ["Technical (LU7)", "Threats that exploit the technology directly", "Firewall, anti-malware, encryption, MFA, RAID, backups, UPS"],
              ["Procedural", "Threats that exploit gaps in process", "AUP, password policy, change management, incident-response plan, audit logs"],
              ["Human", "Threats that exploit the user", "Awareness training, phishing simulations, supervisor verification, situational awareness"],
            ]}
          />
          <p className="mt-2 text-sm italic text-slate-600">A defence with all three layers is much stronger than one with only the technical layer. Most breaches are not <em>technical</em> failures &mdash; they are <strong>process or human</strong> failures that the technical layer never had a chance to stop.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">8.3.2 Practical safeguards by tactic</h3>
          <p>Map each tactic in 8.2.1 to its primary mitigation. The pairing matters more than the list.</p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
            <li><strong>Phishing</strong> &rarr; <em>user training</em> + email filtering + <strong>MFA</strong> (so a stolen password alone is insufficient).</li>
            <li><strong>Shoulder surfing</strong> &rarr; <em>privacy filters</em>, screen locks (Windows+L), situational awareness in cafes and libraries.</li>
            <li><strong>Dumpster diving</strong> &rarr; <em>shred sensitive paper</em>; certified data-erasure for old hard drives; secure waste removal.</li>
            <li><strong>Social-media engineering</strong> &rarr; <em>lock down public profiles</em>; teach staff to recognise pretexts; verify unusual requests through a separate channel.</li>
            <li><strong>Reverse social engineering</strong> &rarr; <em>only call IT through known internal numbers</em>; never give credentials to someone who calls or messages you first.</li>
            <li><strong>Malware (trojans, ransomware)</strong> &rarr; <em>application allow-lists</em>; regular updates; reputable anti-malware; least-privilege user accounts.</li>
          </ul>

          <InfoBox title="The 'least privilege' principle" color="green">
            Give every user (and every program) <strong>only the access they need</strong> to do their job. A staff member who never needs admin rights should not have them by default; a program that doesn&rsquo;t need network access should not have it. When something does go wrong, least-privilege limits the blast radius.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">8.3.3 The AUP as a safeguard</h3>
          <p>Beyond setting expectations, the AUP is the <strong>legal basis</strong> for the school or employer to act when something goes wrong. Without an AUP that the user signed, disciplinary action may be hard to defend; with one, the rules are explicit and enforcement is straightforward.</p>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> When asked to <strong>suggest safeguards</strong>, mix layers &mdash; one technical (e.g. MFA, encryption), one procedural (e.g. AUP, change management), one human (e.g. awareness training). A balanced answer beats three answers from the same layer. Examiners explicitly call this back to <strong>11.2.8</strong> &mdash; cross-reference is rewarded.
          </div>
        </Section>

        {/* ══════════════ 8.4 EVALUATING INFORMATION ══════════════ */}
        <Section id="evaluating-information" title="8.4 Evaluating Information" completed={completed["evaluating-information"]} onToggle={() => toggle("evaluating-information")}>
          <p>Computers make information <em>abundant</em>. They do not make it <em>accurate</em>. The skill the SAG names &mdash; and the one most directly relevant to research, study, and adult life &mdash; is being able to look at a source and decide <strong>whether to trust it</strong> before you rely on it.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">8.4.1 The seven SAG criteria</h3>
          <p>The SAG names seven criteria for evaluating any information source. Memorise them in order &mdash; <strong>A · A · A · C · C · D · O</strong> &mdash; and apply them <em>together</em>, not in isolation.</p>

          <Table
            headers={["Criterion", "The question it answers", "Quick example"]}
            rows={[
              ["Affiliation", "Who supports or hosts the source?", "A SARS .gov.za page is more trustworthy on tax than 'taxhelpfree.com'."],
              ["Audience", "Who is the source written for?", "A primary-school explainer is not a research paper."],
              ["Authority", "Who is the author and what are their credentials?", "A named tax expert with SAIPA credentials beats an anonymous blog."],
              ["Content", "Is the content well-organised and well-supported?", "Citations to primary sources, working internal links, clear structure."],
              ["Currency", "Is the information up-to-date?", "Tax tables change every year. Old = wrong."],
              ["Design", "Is the page navigable, professional, reasonably fast?", "A site that hasn't been updated in five years often has equally stale content."],
              ["Objectivity", "Does the source acknowledge other viewpoints?", "An advocacy site can still be accurate — but knowing the bias lets you read it critically."],
            ]}
          />

          <InfoBox title="Triangulation — the practical extension" color="blue">
            Cross-check any important fact across <strong>at least three independent sources</strong> before relying on it. Triangulation is not in the SAG&rsquo;s seven-criterion list but it is the practical habit that holds the criteria together. If three independent sources agree, the fact is probably solid; if they disagree, trust the most authoritative one and dig deeper.
          </InfoBox>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">8.4.2 Worked example — a tax-table page</h3>
          <p>A learner is researching the <strong>2025 South African income-tax brackets</strong> for a Life Orientation assignment and finds a page titled <em>&ldquo;2025 Tax Tables Explained &mdash; Free Download&rdquo;</em>. Apply the seven criteria:</p>
          <Table
            headers={["Criterion", "What the learner asks"]}
            rows={[
              ["Affiliation", "Who runs the site? SARS = high trust. Anonymous blog = low. Accounting firm = trustworthy but selling services."],
              ["Audience", "Is this for tax practitioners (technical) or individuals (general)? Match to need."],
              ["Authority", "Named expert with SAIPA / SAICA credentials? Or anonymous / AI-generated?"],
              ["Content", "Citations to the SARS site? Working links to the Income Tax Act? Clear examples?"],
              ["Currency", "Is it 2025? Tax tables change every year. Currency is decisive here."],
              ["Design", "Is it crisp and quick? Or cluttered, broken, ad-heavy?"],
              ["Objectivity", "Is the page selling tax advice? Acknowledging nuance? Pushing one position?"],
            ]}
          />
          <p className="mt-3"><strong>Triangulation</strong>: cross-check the bracket numbers against (a) the SARS site directly and (b) a major news source. All three agree &rarr; trust the original. Disagreement &rarr; trust the SARS source.</p>

          <h3 className="font-bold text-slate-800 mt-5 mb-2 text-base">8.4.3 Fake news, misinformation, disinformation</h3>
          <p>The criteria above are exactly what separates real journalism from <strong>fake news</strong>. Two related terms are worth knowing:</p>
          <KeyTerm term="Misinformation">False information spread WITHOUT intent to deceive — someone passing on what they believed to be true.</KeyTerm>
          <KeyTerm term="Disinformation">False information spread DELIBERATELY to deceive — propaganda, fraud, political manipulation. Stronger than misinformation; intent is the difference.</KeyTerm>
          <p className="mt-3 text-sm">An ordinary person sharing a hoax on WhatsApp is spreading <em>misinformation</em>. A state actor running a coordinated campaign of fabricated stories is spreading <em>disinformation</em>. The seven criteria help against both.</p>

          <InfoBox title="AI-generated content" color="orange">
            With modern AI capable of generating plausible-looking text, images, and video, &ldquo;is this AI-generated?&rdquo; is increasingly hard to answer reliably. Detection tools are unreliable and getting worse, not better. The SAG&rsquo;s seven criteria &mdash; especially <strong>Authority</strong>, <strong>Content</strong>, <strong>Currency</strong>, and <strong>Objectivity</strong> &mdash; remain the right defence. Ask &ldquo;does this source check out?&rdquo;, not &ldquo;is this AI?&rdquo;.
          </InfoBox>

          <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <strong>Test Prep:</strong> Memorise <strong>all seven criteria</strong> &mdash; you can be asked to name any subset (e.g. &ldquo;name FOUR&rdquo;). For each criterion, know the <em>question</em> it answers in one short sentence. Be ready to <strong>apply</strong> the criteria to a worked example (a website, a blog post, a Wikipedia article). Triangulation is worth a mark even though it&rsquo;s not in the SAG list.
          </div>
        </Section>

        {/* ══════════════ LU8 PRACTICE EXERCISES ══════════════ */}
        <Section id="exercises-lu8" title="LU8 Practice Exercises" completed={completed["exercises-lu8"]} onToggle={() => toggle("exercises-lu8")}>
          <p className="mb-4 text-sm italic text-slate-600">
            Work through each exercise on paper first, then check your answer. These exercises cover all Learning Unit 8 topics. (Note: 11.3.1 has no past-paper coverage in the current ingest, so all exercises here are hand-authored.)
          </p>

          <div className="space-y-6 text-sm">
            <div>
              {/* Authored — no past paper covers 11.3.1 */}
              <p className="font-semibold">Exercise 1 &mdash; Define social engineering</p>
              <p className="text-sm mt-1">Define <em>social engineering</em> and name THREE forms of social-engineering attack. <em>(5 marks)</em></p>
              <Spoiler>
{`Social engineering — manipulating PEOPLE (not
computers) to obtain information, access, or actions
that benefit an attacker. It bypasses technical
defences by going through the user. (2)

Three forms (any 3, 1 mark each):
• Shoulder surfing
• Dumpster diving
• Phishing
• Trojan horse (deceptive software)
• Reverse social engineering / role-playing
• Social-media engineering`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.3.1 */}
              <p className="font-semibold">Exercise 2 &mdash; White-hat vs Black-hat vs Cracker</p>
              <p className="text-sm mt-1">Distinguish between a <em>white-hat hacker</em>, a <em>black-hat hacker</em>, and a <em>cracker</em>. <em>(6 marks)</em></p>
              <Spoiler>
{`• White-hat hacker — breaks into systems WITH
  PERMISSION to find vulnerabilities so they can be
  fixed (penetration testers, bug-bounty hunters).
  Legal and beneficial. (2)
• Black-hat hacker — breaks into systems MALICIOUSLY
  AND ILLEGALLY for personal gain or harm. The
  popular-press meaning of "hacker". (2)
• Cracker — older term sometimes used specifically
  for a malicious hacker; in modern usage largely
  overtaken by "black-hat hacker". (2)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.3.1 */}
              <p className="font-semibold">Exercise 3 &mdash; Categories of computer crime</p>
              <p className="text-sm mt-1">List FIVE categories of computer crime and give a one-sentence example of each. <em>(10 marks)</em></p>
              <Spoiler>
{`Any 5, 2 marks each (1 for naming, 1 for example):

• Hardware theft — physical theft of devices,
  e.g. a laptop stolen from a car.
• Software theft (piracy) — unauthorised copying or
  use of paid software, e.g. cracked Windows.
• Information theft — unauthorised access to and
  copying of confidential data, e.g. exfiltrating a
  school's learner database.
• Identity theft — impersonating someone using their
  identifying information, e.g. opening a fraudulent
  loan in another person's name.
• Bandwidth theft — using another person's Internet
  connection without permission, e.g. piggy-backing on
  unsecured Wi-Fi.
• Time theft — using paid work time for personal
  activity, e.g. extensive social-media use during
  work hours.
• Service theft — using a paid service in violation
  of its terms, e.g. sharing one subscription across
  a dozen people.`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.3.1 */}
              <p className="font-semibold">Exercise 4 &mdash; What is an AUP?</p>
              <p className="text-sm mt-1">Define an <em>Acceptable User Policy (AUP)</em> and explain TWO reasons a school has one. <em>(5 marks)</em></p>
              <Spoiler>
{`An AUP is a written policy that defines what users
(employees, or learners in a school) may and may not
do with employer/school-provided IT — devices,
accounts, network access, data. Signed by the user
as a condition of access. (1)

Two reasons (any 2, 2 marks each):
• Sets clear expectations — users know in advance what
  is and is not permitted (e.g. no harassment via
  email; no installing unapproved software). Prevents
  misunderstandings before they become incidents. (2)
• Provides a legal basis for action — when something
  goes wrong, the signed AUP is what allows the school
  to confiscate a phone, exclude a learner, or
  dismiss a staff member. Without it, enforcement is
  hard to defend. (2)
• Allocates security responsibilities — passwords,
  MFA, locking screens, reporting incidents are made
  the user's explicit duty, not the IT department's. (2)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.3.1 */}
              <p className="font-semibold">Exercise 5 &mdash; Phishing vs Reverse Social Engineering</p>
              <p className="text-sm mt-1">Distinguish between <em>phishing</em> and <em>reverse social engineering</em>, giving a concrete example of each. <em>(6 marks)</em></p>
              <Spoiler>
{`Phishing — the ATTACKER initiates contact, tricking
the user via email, SMS, or web message into giving
up credentials or clicking a malicious link. (2)
Example: an email "from the principal" asking staff
to verify their school-portal password at a fake
URL. (1)

Reverse social engineering — the attacker arranges a
situation in which the VICTIM initiates contact with
THEM, then exploits the apparent authority. The
victim believes they are calling for help. (2)
Example: "IT Help" business cards left near the
photocopier; later, when the (attacker-engineered)
printer issue happens, staff phone the number on
the card and willingly give up their credentials
for "remote support". (1)

Key distinction: in phishing the attacker reaches
out; in reverse SE the victim reaches out. The
mitigation differs — phishing is countered by email
filtering and link checks; reverse SE is countered
by always verifying support requests through known
internal channels.`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.3.1 */}
              <p className="font-semibold">Exercise 6 &mdash; Name FOUR evaluation criteria (L2)</p>
              <p className="text-sm mt-1">Name FOUR of the seven criteria for evaluating an information source. For each, write the <em>question</em> the criterion answers. <em>(8 marks)</em></p>
              <Spoiler>
{`Any 4, 2 marks each (1 for naming, 1 for the question):

• Affiliation — Who supports or hosts the source?
• Audience — Who is the source written for?
• Authority — Who is the author, what are their
  credentials?
• Content — Is the content well-organised and
  well-supported?
• Currency — Is the information up-to-date?
• Design — Is the source navigable and professionally
  presented?
• Objectivity — Does the source acknowledge other
  viewpoints?

(Triangulation also accepted as a bonus, with
"Cross-checking a fact across multiple independent
sources before relying on it" as its question.)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.3.1 */}
              <p className="font-semibold">Exercise 7 &mdash; Apply the criteria to a tax-table page (L2)</p>
              <p className="text-sm mt-1">A learner finds a website giving 2024 South African income-tax brackets and prepares to use it for a Life Orientation assignment. Suggest THREE specific checks the learner should perform before trusting the page, naming the <em>evaluation criterion</em> each check applies. <em>(6 marks)</em></p>
              <Spoiler>
{`Any 3, 2 marks each (1 for the check, 1 for naming
the criterion):

• Confirm the site is run by SARS or another credible
  body, not an anonymous blog. (Affiliation)
• Verify the figures are for the CURRENT tax year —
  tax tables change annually, so old figures are
  wrong. (Currency)
• Verify the author is a credible tax practitioner
  or organisation, not unsigned content. (Authority)
• Check the page cites primary sources (the Income
  Tax Act, the budget speech). (Content)
• Compare the bracket figures against SARS or another
  credible source. (Triangulation — applies multiple
  criteria simultaneously)
• Check whether the page is selling tax-advice
  services and may oversell its product. (Objectivity)`}
              </Spoiler>
            </div>

            <div>
              {/* Authored — no past paper covers 11.3.1 */}
              <p className="font-semibold">Exercise 8 &mdash; UAVs replacing couriers (L3)</p>
              <p className="text-sm mt-1">A logistics company plans to replace half of its couriers with autonomous <strong>delivery drones (UAVs)</strong>. Discuss THREE <em>positive</em> effects and THREE <em>negative</em> effects on workplaces and society. <em>(8 marks)</em></p>
              <Spoiler>
{`Positives (any 3 — 1 mark each, ½ for naming, ½ for
elaborating; up to 4 marks):

• Faster deliveries — UAVs avoid traffic; deliveries
  that took hours by road take minutes by drone.
• Lower per-delivery cost — once the drones are paid
  off, ongoing cost per parcel is far below a
  courier's wage.
• Access to remote areas — UAVs reach areas with poor
  road infrastructure (rural farms, mountainous
  regions).
• Reduced human risk — couriers no longer drive
  through dangerous areas at night; UAVs handle the
  hazardous routes.
• Lower emissions — electric drones may be more
  efficient per parcel than petrol/diesel vans for
  short-range deliveries.

Negatives (any 3 — 1 mark each; up to 4 marks):

• Job losses — couriers replaced by drones face
  unemployment; the impact on lower-skilled workers
  is disproportionate.
• Concentration of economic gain — gains accrue to
  the company / drone operator; couriers spending
  wages locally previously stimulated community
  economic activity.
• Privacy concerns — drones with cameras flying over
  residential areas raise surveillance concerns.
• Safety risks — drone failures, mid-air collisions,
  weather-related crashes can cause property damage
  or injury.
• Regulation gaps — flight safety, airspace, noise,
  and privacy regulations may not yet cover
  commercial drone use.
• Reduced human contact — couriers also served as
  low-tech check-ins for elderly or isolated
  customers; drones don't.

Award full marks for any thoughtful, balanced
discussion that names three positives and three
negatives with brief justification.`}
              </Spoiler>
            </div>
          </div>
        </Section>

        {/* ─── Footer ─── */}
        <div className="mt-12 pt-6 border-t border-amber-200 text-center text-xs text-slate-500">
          <p>Grade 11 Information Technology · Interactive Study Guide</p>
          <p className="mt-1">Helpmekaar Kollege · IEB Affiliated · 2026</p>
        </div>
        </div>)}
      </main>
      </div>

      {/* ─── Scroll to top ─── */}
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 z-50 w-10 h-10 rounded-full bg-amber-600 text-white shadow-lg flex items-center justify-center hover:bg-amber-700 transition-colors"
        >
          <ArrowUp size={18} />
        </button>
      )}
    </div>
  );
}
